import cloneDeep from 'lodash/cloneDeep';
import pick from 'lodash/pick';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import { PendoIcon, PendoMetric, PendoTooltip, PendoAlert, PendoCollapseTransition, PendoInput, PendoForm, PendoFormItem, PendoCard, PendoButton, PendoClipboard, PendoInputNumber, PendoMultiselect, PendoDatePicker, PendoGuideStatusOption, PendoIconOption, PendoTag, PendoCheckbox, PendoModal, PendoProgressBar, PendoIconButton, PendoNotification, PendoCollapse, PendoMultiselectOption, PendoPopover, PendoAppDisplay, PendoDivider, PendoEmptyState, PendoRadioGroup, PendoRadio, PendoDrawer } from '@pendo/components';
import isPlainObject from 'lodash/isPlainObject';
import isUndefined from 'lodash/isUndefined';
import isEmpty from 'lodash/isEmpty';
import 'lodash/intersection';
import '@pendo/services/Apps';
import '@pendo/services/Constants';
import { http, initializeHttpWrapper } from '@pendo/http';
import isArray from 'lodash/isArray';
import moment from 'moment-timezone';
import uuid from 'uuid';
import flatMap from 'lodash/flatMap';
import 'lodash/invoke';
import keyBy from 'lodash/keyBy';
import '@pendo/services/Formatters';
import { operators } from '@pendo/aggregations';
import { connectAggregationToState, withComponent, LOADING_STATES, initializeAggConnect } from '@pendo/agg-connect';
import debounce from 'lodash/debounce';
import reduce from 'lodash/reduce';
import isNumber from 'lodash/isNumber';
import merge from 'lodash/merge';
import isString from 'lodash/isString';
import clone from 'lodash/clone';
import isNil from 'lodash/isNil';
import map from 'lodash/map';
import find from 'lodash/find';
import capitalize from 'lodash/capitalize';
import { env } from '@pendo/services/Environments';

function pluralize(value, count) {
  if (!value) {
    return '';
  }
  if (count && count === 1) {
    return value;
  }
  return `${value}s`;
}

Object.freeze({
  APP_ID: 'appId',
  APP_IDS: 'appIds'
});

// Default web app `-` needs to be converted to _
function convertAppIdToAggSafeId(appId) {
  return String(appId).replace('-', '_');
}

/* This segment builder started out as two unique segment builders in Adopt and Engage
 * with unique solutions on how segment filter options get saved to the BE. This section
 * is intended to provide a clear-cut way to carefully migrate segment filters from both apps
 * to conform to a consistent pattern. This does NOT impact saved aggs; only how the segment
 * builder represents the agg in user-selection form so we can correctly render the builder
 * UI across both platforms. */

function migrateRule(rule) {
  // NOTE: Keep these in order in case we change decisions in the future!
  rule = migrateStartEndKeys(rule);
  rule = migrateGranularityCountArrayToKeys(rule);
  return rule;
}

/* Context: Adopt segments use start/end values while Engage segments use first/last
 * Migration: Use first/last to match with timeSeries key fields:
 *
 * `{ value: { start: 123, end: 456 } }`
 *  becomes
 * `{ value: { first: 123, last: 456 } }` */
function migrateStartEndKeys(rule) {
  if (!isUndefined(rule.value?.start)) {
    rule.value.first = rule.value.start;
    delete rule.value.start;
  }
  if (!isUndefined(rule.value?.end)) {
    rule.value.last = rule.value.end;
    delete rule.value.end;
  }
  return rule;
}

/* Context: Adopt segments overload `value` with `count`/`granularity` array
 * while Engage segments use unique fields.
 * Migration: Use unique fields under `value` Object instead of Array for metadata:
 *
 * `{ value: [5, "weeks"] }`
 *  becomes
 * `{ value: { count: 5, granularity: "weeks" } }` */
function migrateGranularityCountArrayToKeys(rule) {
  if (isArray(rule.value)) {
    const [count, granularity] = rule.value;
    rule.value = {
      count,
      granularity
    };
  }
  return rule;
}

const DATE_FORMAT = {
  iso: 'YYYY-MM-DD',
  short: 'MMM D, YYYY',
  time: 'h:mma',
  full: 'MMM D, YYYY h:mma z',
  isoDateTime: 'YYYY-MM-DD hh:mm:ss'
};
function formatTimeValue(value) {
  let formattedValue;
  if (isPlainObject(value)) {
    formattedValue = Object.entries(value).reduce((acc, [key, val]) => {
      acc[key] = formatTimeValue(val);
      return acc;
    }, {});
  } else {
    formattedValue = moment.utc(value).format(DATE_FORMAT.iso);
  }
  return formattedValue;
}
function formatTimeFilterSummary(valueFromTimeSchema) {
  let value;
  if (isPlainObject(valueFromTimeSchema)) {
    if (valueFromTimeSchema.first && valueFromTimeSchema.last) {
      value = [moment(valueFromTimeSchema.first).format(DATE_FORMAT.short), moment(valueFromTimeSchema.last).format(DATE_FORMAT.short)].join(' and ');
    } else {
      value = Object.keys(valueFromTimeSchema).map(key => valueFromTimeSchema[key]).join(' ');
    }
  } else {
    value = moment(valueFromTimeSchema).format(DATE_FORMAT.short);
  }
  return value;
}

function parseApplicationUsageFilterPipeline({
  operator,
  selectedApp,
  time,
  value
}) {
  let filterString;
  const appId = convertAppIdToAggSafeId(get(selectedApp, 'id', ''));
  switch (operator) {
    case 'used':
      if (time === 'withinlast') {
        const {
          count,
          granularity
        } = value;
        return {
          filter: `!isNil(metadata.auto_${appId}.lastvisit)&&metadata.auto_${appId}.lastvisit>=dateAdd(now(),${-1 * count},"${granularity}")`
        };
      }
      if (time === 'since') {
        return {
          filter: `!isNil(metadata.auto_${appId}.lastvisit)&&metadata.auto_${appId}.lastvisit>=date("${formatTimeValue(value)}")`
        };
      }
      return {
        filter: `!isNil(metadata.auto_${appId}.lastvisit)`
      };
    case 'notused':
      if (time === 'withinlast') {
        const {
          count,
          granularity
        } = value;
        return {
          filter: `(isNil(metadata.auto_${appId}.lastvisit))||(metadata.auto_${appId}.lastvisit<dateAdd(now(),${-1 * count},"${granularity}"))`
        };
      }
      if (time === 'since') {
        return {
          filter: `(isNil(metadata.auto_${appId}.lastvisit))||(metadata.auto_${appId}.lastvisit<date("${formatTimeValue(value)}"))`
        };
      }
      return {
        filter: `isNil(metadata.auto_${appId}.lastvisit)`
      };
  }
  return {
    filter: filterString
  };
}

function parseFeatureFilterPipeline({
  operator,
  time,
  value,
  filterIndex
}) {
  let filterString;
  switch (operator) {
    case 'clicked':
      if (time === 'withinlast') {
        const {
          count,
          granularity
        } = value;
        filterString = buildFeatureClickedWithinLastFilterString(filterIndex, count, granularity, '>=');
        break;
      }
      if (time === 'since') {
        filterString = buildFeatureClickedSinceFilterString(filterIndex, value, '>=');
        break;
      }
      if (time === 'atleast') {
        filterString = buildFeatureClickedAtLeastOrMostFilterString(filterIndex, value, '>=');
        break;
      }
      if (time === 'atmost') {
        filterString = buildFeatureClickedAtLeastOrMostFilterString(filterIndex, value, '<=');
        break;
      }
      filterString = buildFeatureClickedEverFilterString(filterIndex, '>');
      break;
    case 'lastclicked':
      if (time === 'withinlast') {
        const {
          count,
          granularity
        } = value;
        filterString = buildFeatureClickedWithinLastFilterString(filterIndex, count, granularity, '>=');
        break;
      }
      if (time === '!withinlast') {
        const {
          count,
          granularity
        } = value;
        filterString = buildFeatureClickedNotWithinLastFilterString(filterIndex, count, granularity);
        break;
      }
      if (time === 'since') {
        filterString = buildFeatureClickedSinceFilterString(filterIndex, value, '>=');
        break;
      }
      if (time === 'before') {
        filterString = buildFeatureClickedSinceFilterString(filterIndex, value, '<=');
        break;
      }
      if (time === 'between') {
        filterString = buildFeatureClickedBetweenFilterString(filterIndex, value.first, value.last);
        break;
      }
      if (time === 'atleast') {
        filterString = buildFeatureClickedAtLeastOrMostFilterString(filterIndex, value, '>=');
        break;
      }
      if (time === 'atmost') {
        filterString = buildFeatureClickedAtLeastOrMostFilterString(filterIndex, value, '<=');
        break;
      }
      filterString = buildFeatureClickedEverFilterString(filterIndex, '>');
      break;
    case 'notclicked':
      if (time === 'withinlast') {
        const {
          count,
          granularity
        } = value;
        filterString = buildFeatureClickedWithinLastFilterString(filterIndex, count, granularity, '<');
        break;
      }
      if (time === 'since') {
        filterString = buildFeatureClickedSinceFilterString(filterIndex, value, '<');
        break;
      }
      filterString = buildFeatureClickedEverFilterString(filterIndex, '<=');
      break;
  }
  return {
    filter: filterString
  };
}
function buildFeatureClickedEverFilterString(filterIndex, comparisonSymbol) {
  return `feature${filterIndex}_itemSeen.count${comparisonSymbol}0`;
}
function buildFeatureClickedWithinLastFilterString(filterIndex, countValue, timeUnit, comparisonSymbol) {
  return `feature${filterIndex}_itemSeen.lastUsed${comparisonSymbol}dateAdd(now(),${-1 * countValue},"${timeUnit}")`;
}
function buildFeatureClickedNotWithinLastFilterString(filterIndex, countValue, timeUnit) {
  return `(feature${filterIndex}_itemSeen.lastUsed>=now()||feature${filterIndex}_itemSeen.lastUsed<=dateAdd(now(),-${countValue},"${timeUnit}"))`;
}
function buildFeatureClickedSinceFilterString(filterIndex, timeValue, comparisonSymbol) {
  return `feature${filterIndex}_itemSeen.lastUsed${comparisonSymbol}date("${formatTimeValue(timeValue)}")`;
}
function buildFeatureClickedBetweenFilterString(filterIndex, first, last) {
  return `(feature${filterIndex}_itemSeen.lastUsed>=date("${formatTimeValue(first)}")&&feature${filterIndex}_itemSeen.lastUsed<=date("${formatTimeValue(last)}"))`;
}
function buildFeatureClickedAtLeastOrMostFilterString(filterIndex, countValue, comparisonSymbol) {
  return `feature${filterIndex}_itemSeen.count${comparisonSymbol}${countValue}`;
}

function parseGuideElementFilterPipeline({
  operator,
  time,
  value,
  filterIndex
}) {
  let filterString;
  switch (operator) {
    case 'clicked':
      if (time === 'withinlast') {
        const {
          count,
          granularity
        } = value;
        filterString = buildGuideElementClickedWithinLastFilterString(filterIndex, count, granularity, '>=');
        break;
      }
      if (time === 'since') {
        filterString = buildGuideElementClickedSinceFilterString(filterIndex, value, '>=');
        break;
      }
      if (time === 'atleast') {
        filterString = buildGuideElementClickedAtLeastOrMostFilterString(filterIndex, value, '>=');
        break;
      }
      if (time === 'atmost') {
        filterString = `${buildGuideElementNullFilterString(filterIndex)}||${buildGuideElementClickedAtLeastOrMostFilterString(filterIndex, value, '<=')}`;
        break;
      }
      filterString = buildGuideElementClickedEverFilterString(filterIndex, '>');
      break;
    case 'notclicked':
      filterString = buildGuideElementNullFilterString(filterIndex);
      if (time === 'withinlast') {
        const {
          count,
          granularity
        } = value;
        filterString += `||${buildGuideElementClickedWithinLastFilterString(filterIndex, count, granularity, '<')}`;
        break;
      }
      if (time === 'since') {
        filterString += `||${buildGuideElementClickedSinceFilterString(filterIndex, value, '<')}`;
        break;
      }
      filterString += `||${buildGuideElementClickedEverFilterString(filterIndex, '<=')}`;
      break;
  }
  return {
    filter: filterString
  };
}
function buildGuideElementNullFilterString(filterIndex) {
  return `isNull(guideElement${filterIndex}_itemSeen.count)`;
}
function buildGuideElementClickedEverFilterString(filterIndex, comparisonSymbol) {
  return `guideElement${filterIndex}_itemSeen.count${comparisonSymbol}0`;
}
function buildGuideElementClickedWithinLastFilterString(filterIndex, countValue, timeUnit, comparisonSymbol) {
  return `guideElement${filterIndex}_itemSeen.lastUsed${comparisonSymbol}dateAdd(now(),${-1 * countValue},"${timeUnit}")`;
}
function buildGuideElementClickedSinceFilterString(filterIndex, timeValue, comparisonSymbol) {
  return `guideElement${filterIndex}_itemSeen.lastUsed${comparisonSymbol}date("${formatTimeValue(timeValue)}")`;
}
function buildGuideElementClickedAtLeastOrMostFilterString(filterIndex, countValue, comparisonSymbol) {
  return `guideElement${filterIndex}_itemSeen.count${comparisonSymbol}${countValue}`;
}

function parseGuideFilterPipeline({
  operator,
  time,
  ...params
}) {
  let filterString;
  switch (operator) {
    case 'viewed':
      filterString = buildGuideViewedFilterString(params);
      switch (time) {
        case '<=':
          filterString += `&&${buildGuideTimeBeforeFilterString(params)}`;
          break;
        case '>=':
          filterString += `&&${buildGuideTimeSinceFilterString(params)}`;
          break;
        case 'withinlast':
          filterString += `&&${buildGuideTimeWithinLastFilterString(params)}`;
          break;
        case '!withinlast':
          filterString += `&&${buildGuideTimeNotWithinLastFilterString(params)}`;
          break;
        case 'atleast':
          filterString += `&&${buildGuideSeenAtLeastOrMostFilterString({
            comparisonSymbol: '>=',
            ...params
          })}`;
          break;
        case 'atmost':
          filterString += `&&${buildGuideSeenAtLeastOrMostFilterString({
            comparisonSymbol: '<=',
            ...params
          })}`;
          break;
        // default 'ever'; nothing appended
      }

      break;
    case 'lastviewed':
      filterString = buildGuideViewedFilterString(params);
      switch (time) {
        case '<=':
          filterString += `&&${buildGuideLastTimeBeforeFilterString(params)}`;
          break;
        case '>=':
          filterString += `&&${buildGuideTimeSinceFilterString(params)}`;
          break;
        case 'withinlast':
          filterString += `&&${buildGuideTimeWithinLastFilterString(params)}`;
          break;
        case '!withinlast':
          filterString += `&&${buildGuideTimeNotWithinLastFilterString(params)}`;
          break;
        case 'atleast':
          filterString += `&&${buildGuideSeenAtLeastOrMostFilterString({
            comparisonSymbol: '>=',
            ...params
          })}`;
          break;
        case 'atmost':
          filterString += `&&${buildGuideSeenAtLeastOrMostFilterString({
            comparisonSymbol: '<=',
            ...params
          })}`;
          break;
        case 'between':
          filterString += `&&${buildGuideTimeBetweenFilterString(params)}`;
          break;
      }
      break;
    case 'notviewed':
      filterString = buildGuideNotViewedFilterString(params);
      switch (time) {
        case '<=':
          filterString += `||!(${buildGuideTimeBeforeFilterString(params)})`;
          break;
        case '>=':
          filterString += `||!(${buildGuideTimeSinceFilterString(params)})`;
          break;
        case 'withinlast':
          filterString += `||!(${buildGuideTimeWithinLastFilterString(params)})`;
          break;
        case '!withinlast':
          filterString += `||!(${buildGuideTimeNotWithinLastFilterString(params)})`;
          break;
        // default 'ever'; nothing appended
      }

      break;
  }
  return {
    filter: filterString
  };
}
function buildGuideViewedFilterString({
  filterIndex
}) {
  return `!isNull(lastState${filterIndex})`;
}
function buildGuideNotViewedFilterString({
  filterIndex
}) {
  return `isNull(lastState${filterIndex})`;
}
function buildGuideTimeBeforeFilterString({
  filterIndex,
  first
}) {
  return `guideFirstTime${filterIndex}<=date("${formatTimeValue(first)}")`; // double-check this
}

function buildGuideLastTimeBeforeFilterString({
  filterIndex,
  first
}) {
  return `guideLastTime${filterIndex}<=date("${formatTimeValue(first)}")`; // double-check this
}

function buildGuideTimeSinceFilterString({
  filterIndex,
  first
}) {
  return `guideLastTime${filterIndex}>=date("${formatTimeValue(first)}")`;
}
function buildGuideTimeBetweenFilterString({
  filterIndex,
  first,
  last
}) {
  return `(guideLastTime${filterIndex}>=date("${formatTimeValue(first)}")&&guideLastTime${filterIndex}<=date("${formatTimeValue(last)}"))`;
}
function buildGuideTimeWithinLastFilterString({
  filterIndex,
  count,
  granularity
}) {
  return `guideLastTime${filterIndex}>=dateAdd(now(),-${count},"${granularity}")`;
}
function buildGuideTimeNotWithinLastFilterString({
  filterIndex,
  count,
  granularity
}) {
  return `(guideLastTime${filterIndex}>=now()||guideLastTime${filterIndex}<=dateAdd(now(),-${count},"${granularity}"))`;
}
function buildGuideSeenAtLeastOrMostFilterString({
  filterIndex,
  value,
  comparisonSymbol
}) {
  return `guideSeenCount${filterIndex}${comparisonSymbol}${value}`;
}

function parsePageFilterPipeline({
  operator,
  time,
  value,
  filterIndex
}) {
  let filterString;
  switch (operator) {
    case 'viewed':
      if (time === 'withinlast') {
        const {
          count,
          granularity
        } = value;
        filterString = buildPageSeenWithinLastFilterString(filterIndex, count, granularity, '>=');
        break;
      }
      if (time === 'since') {
        filterString = buildPageSeenSinceFilterString(filterIndex, value, '>=');
        break;
      }
      if (time === 'atleast') {
        filterString = buildPageSeenAtLeastOrMostFilterString(filterIndex, value, '>=');
        break;
      }
      if (time === 'atmost') {
        filterString = buildPageSeenAtLeastOrMostFilterString(filterIndex, value, '<=');
        break;
      }
      filterString = buildPageSeenEverFilterString(filterIndex, '>');
      break;
    case 'lastviewed':
      if (time === 'withinlast') {
        const {
          count,
          granularity
        } = value;
        filterString = buildPageSeenWithinLastFilterString(filterIndex, count, granularity, '>=');
        break;
      }
      if (time === '!withinlast') {
        const {
          count,
          granularity
        } = value;
        filterString = buildPageSeenNotWithinLastFilterString(filterIndex, count, granularity);
        break;
      }
      if (time === 'since') {
        filterString = buildPageSeenSinceFilterString(filterIndex, value, '>=');
        break;
      }
      if (time === 'before') {
        filterString = buildPageSeenSinceFilterString(filterIndex, value, '<=');
        break;
      }
      if (time === 'atleast') {
        filterString = buildPageSeenAtLeastOrMostFilterString(filterIndex, value, '>=');
        break;
      }
      if (time === 'atmost') {
        filterString = buildPageSeenAtLeastOrMostFilterString(filterIndex, value, '<=');
        break;
      }
      if (time === 'between') {
        filterString = buildPageSeenBetweenFilterString(filterIndex, value.first, value.last);
        break;
      }
      filterString = buildPageSeenEverFilterString(filterIndex, '>');
      break;
    case 'notviewed':
      if (time === 'withinlast') {
        const {
          count,
          granularity
        } = value;
        filterString = buildPageSeenWithinLastFilterString(filterIndex, count, granularity, '<');
        break;
      }
      if (time === 'since') {
        filterString = buildPageSeenSinceFilterString(filterIndex, value, '<');
        break;
      }
      filterString = buildPageSeenEverFilterString(filterIndex, '<=');
      break;
  }
  return {
    filter: filterString
  };
}
function buildPageSeenEverFilterString(filterIndex, comparisonSymbol) {
  return `page${filterIndex}_itemSeen.count${comparisonSymbol}0`;
}
function buildPageSeenWithinLastFilterString(filterIndex, countValue, timeUnit, comparisonSymbol) {
  return `page${filterIndex}_itemSeen.lastUsed${comparisonSymbol}dateAdd(now(),${-1 * countValue},"${timeUnit}")`;
}
function buildPageSeenNotWithinLastFilterString(filterIndex, countValue, timeUnit) {
  return `(page${filterIndex}_itemSeen.lastUsed>=now()||page${filterIndex}_itemSeen.lastUsed<=dateAdd(now(),-${countValue},"${timeUnit}"))`;
}
function buildPageSeenSinceFilterString(filterIndex, timeValue, comparisonSymbol) {
  return `page${filterIndex}_itemSeen.lastUsed${comparisonSymbol}date("${formatTimeValue(timeValue)}")`;
}
function buildPageSeenBetweenFilterString(filterIndex, first, last) {
  return `(page${filterIndex}_itemSeen.lastUsed>=date("${formatTimeValue(first)}")&&page${filterIndex}_itemSeen.lastUsed<=date("${formatTimeValue(last)}"))`;
}
function buildPageSeenAtLeastOrMostFilterString(filterIndex, countValue, comparisonSymbol) {
  return `page${filterIndex}_itemSeen.count${comparisonSymbol}${countValue}`;
}

function parsePollFilterPipeline({
  operator,
  time,
  ...params
}) {
  let filterString;
  switch (operator) {
    case 'responded':
      filterString = buildPollRespondedFilterString(params);
      break;
    case 'notresponded':
      filterString = buildPollNotRespondedFilterString(params);
      break;
    case 'contains':
      filterString = buildPollContainsFilterString(params);
      break;
    case '!contains':
      filterString = buildPollNotContainsFilterString(params);
      break;
    case '!=':
      filterString = buildPollResponseNotEqualsFilterString({
        operator,
        ...params
      });
      break;
    default:
      filterString = buildPollResponseEqualsFilterString({
        operator,
        ...params
      });
  }
  switch (time) {
    case '<=':
      filterString += `&&${buildPollTimeBeforeFilterString(params)}`;
      break;
    case '>=':
      filterString += `&&${buildPollTimeSinceFilterString(params)}`;
      break;
    case 'withinlast':
      filterString += `&&${buildPollTimeWithinLastFilterString(params)}`;
      break;
    case '!withinlast':
      filterString += `&&${buildPollTimeNotWithinLastFilterString(params)}`;
      break;
    case 'between':
      filterString += `&&${buildPollTimeBetweenFilterString(params)}`;
      break;
    // default 'ever'; nothing appended
  }

  return {
    filter: filterString
  };
}
function buildPollRespondedFilterString({
  filterIndex
}) {
  return `!isNull(pollResponse${filterIndex})`;
}
function buildPollNotRespondedFilterString({
  filterIndex
}) {
  return `isNull(pollResponse${filterIndex})`;
}
function buildPollContainsFilterString({
  filterIndex,
  value
}) {
  return `contains(pollResponse${filterIndex},${value})`;
}
function buildPollNotContainsFilterString({
  filterIndex,
  value
}) {
  return `!isNull(pollResponse${filterIndex})&&!contains(pollResponse${filterIndex},${value})`;
}
function buildPollResponseEqualsFilterString({
  filterIndex,
  operator,
  value
}) {
  return `pollResponse${filterIndex}${operator}${value}`;
}
function buildPollResponseNotEqualsFilterString({
  filterIndex,
  operator,
  value
}) {
  return `!isNull(pollResponse${filterIndex})&&pollResponse${filterIndex}${operator}${value}`;
}
function buildPollTimeBeforeFilterString({
  filterIndex,
  first
}) {
  return `pollTime${filterIndex}<=date("${formatTimeValue(first)}")`;
}
function buildPollTimeSinceFilterString({
  filterIndex,
  first
}) {
  return `pollTime${filterIndex}>=date("${formatTimeValue(first)}")`;
}
function buildPollTimeWithinLastFilterString({
  filterIndex,
  count,
  granularity
}) {
  return `pollTime${filterIndex}>=dateAdd(now(),-${count},"${granularity}")`;
}
function buildPollTimeNotWithinLastFilterString({
  filterIndex,
  count,
  granularity
}) {
  return `(pollTime${filterIndex}>=now()||pollTime${filterIndex}<=dateAdd(now(),-${count},"${granularity}"))`;
}
function buildPollTimeBetweenFilterString({
  filterIndex,
  first,
  last
}) {
  return `(pollTime${filterIndex}>=date("${formatTimeValue(first)}")&&pollTime${filterIndex}<=date("${formatTimeValue(last)}"))`;
}

function parseWorkflowUsageFilterPipeline({
  operator,
  time,
  value,
  filterIndex
}) {
  let filterString;
  const startedAndCompletedCheck = `(!isNull(startedAt${filterIndex}) && !isNull(lastCompletedAt${filterIndex})) && (lastCompletedAt${filterIndex} != 0 && startedAt${filterIndex} != 0)`;
  const startedNotCompletedCheck = `startedAt${filterIndex} > 0 && startedAt${filterIndex} > lastCompletedAt${filterIndex}`;
  const notStartedNotCompletedCheck = `(isNull(startedAt${filterIndex}) && isNull(lastCompletedAt${filterIndex})) || (startedAt${filterIndex} == 0 && lastCompletedAt${filterIndex} == 0)`;
  switch (operator) {
    case 'completed':
      if (time === 'withinlast') {
        const {
          count,
          granularity
        } = value;
        filterString = `(${startedAndCompletedCheck}) && lastCompletedAt${filterIndex} >= dateAdd(now(),${-1 * count},"${granularity}")`;
        break;
      }
      if (time === 'since') {
        filterString = `(${startedAndCompletedCheck}) && lastCompletedAt${filterIndex} >= date("${formatTimeValue(value)}")`;
        break;
      }
      if (time === 'atleast') {
        filterString = `completeCount >= ${value}`;
        break;
      }
      if (time === 'atmost') {
        filterString = `completeCount <= ${value}`;
        break;
      }

      // ever case
      filterString = `!isNull(lastCompletedAt${filterIndex}) && lastCompletedAt${filterIndex} != 0`;
      break;
    case 'incomplete':
      if (time === 'withinlast') {
        const {
          count,
          granularity
        } = value;
        filterString = `${startedNotCompletedCheck} && startedAt${filterIndex} >= dateAdd(now(),${-1 * count},"${granularity}")`;
        break;
      }
      if (time === 'since') {
        filterString = `${startedNotCompletedCheck} && startedAt${filterIndex} >= date("${formatTimeValue(value)}")`;
        break;
      }
      if (time === 'atleast') {
        filterString = `incompleteCount >= ${value}`;
        break;
      }
      if (time === 'atmost') {
        filterString = `incompleteCount <= ${value}`;
        break;
      }
      // ever case
      filterString = `startedAt${filterIndex} > 0 && (lastCompletedAt${filterIndex} == 0  || isNull(lastCompletedAt${filterIndex}))`;
      break;
    case 'notStarted':
      if (time === 'withinlast') {
        const {
          count,
          granularity
        } = value;
        filterString = `${notStartedNotCompletedCheck} || startedAt${filterIndex} < dateAdd(now(),${-1 * count},"${granularity}")`;
        break;
      }
      if (time === 'since') {
        filterString = `${notStartedNotCompletedCheck} || startedAt${filterIndex} < date("${formatTimeValue(value)}")`;
        break;
      }
      filterString = `${notStartedNotCompletedCheck}`;
      break;
    case 'notcompleted':
      filterString = `isNull(lastCompletedAt${filterIndex}) || lastCompletedAt${filterIndex} == 0`;
  }
  return {
    filter: filterString
  };
}

const SEGMENT_RULE_LIMIT = 5;
const operatorRequiresUnitCountFormat = {
  'withinLast': true,
  '!withinLast': true
};

// keys match `/api/s/_SID_/metadata/:kind/schema` schema obj.schema
const numberOperators = [{
  id: uuid(),
  value: '==',
  label: 'equals'
}, {
  id: uuid(),
  value: '!=',
  label: 'does not equal'
}, {
  id: uuid(),
  value: '>=',
  label: 'greater than or equal to'
}, {
  id: uuid(),
  value: '<=',
  label: 'less than or equal to'
}];
const SEGMENT_OPERATORS = {
  guideElement: [{
    id: uuid(),
    value: 'clicked',
    label: 'clicked'
  }, {
    id: uuid(),
    value: 'notclicked',
    label: 'not clicked'
  }],
  integer: numberOperators,
  float: numberOperators,
  time: [{
    id: uuid(),
    value: '<=',
    label: 'before',
    multivalue: false
  }, {
    id: uuid(),
    value: '>=',
    label: 'since',
    multivalue: false
  }, {
    id: uuid(),
    value: 'between',
    label: 'between',
    multivalue: true
  }, {
    id: uuid(),
    value: 'withinLast',
    label: 'within last',
    multivalue: true
  }, {
    id: uuid(),
    value: '!withinLast',
    label: 'not within last',
    multivalue: true
  }],
  string: [{
    id: uuid(),
    value: 'contains',
    label: 'contains'
  }, {
    id: uuid(),
    value: '!contains',
    label: 'does not contain'
  }, {
    id: uuid(),
    value: '==',
    label: 'equals'
  }, {
    id: uuid(),
    value: '!=',
    label: 'does not equal'
  }, {
    id: uuid(),
    value: 'listContains',
    label: 'csv contains'
  }, {
    id: uuid(),
    value: '!listContains',
    label: 'csv does not contain'
  }],
  boolean: [{
    id: uuid(),
    value: '==',
    unary: true,
    label: 'is true'
  }, {
    id: uuid(),
    value: '!=',
    unary: true,
    label: 'is false'
  }],
  list: [{
    id: uuid(),
    value: 'contains',
    label: 'contains'
  }, {
    id: uuid(),
    value: '!contains',
    label: 'does not contain'
  }],
  guide: [{
    id: uuid(),
    value: 'viewed',
    label: 'seen'
  }, {
    id: uuid(),
    value: 'lastviewed',
    label: 'last seen'
  }, {
    id: uuid(),
    value: 'notviewed',
    label: 'not seen'
  }],
  page: [{
    id: uuid(),
    value: 'viewed',
    label: 'seen'
  }, {
    id: uuid(),
    value: 'lastviewed',
    label: 'last seen'
  }, {
    id: uuid(),
    value: 'notviewed',
    label: 'not seen'
  }],
  segment: [{
    id: uuid(),
    value: '==',
    label: 'equals'
  }, {
    id: uuid(),
    value: '!=',
    label: 'does not equal'
  }],
  feature: [{
    id: uuid(),
    value: 'clicked',
    label: 'clicked'
  }, {
    id: uuid(),
    value: 'lastclicked',
    label: 'last clicked'
  }, {
    id: uuid(),
    value: 'notclicked',
    label: 'not clicked'
  }],
  poll: [{
    id: uuid(),
    value: '==',
    label: 'equals'
  }, {
    id: uuid(),
    value: '!=',
    label: 'does not equal'
  }, {
    id: uuid(),
    value: 'contains',
    label: 'contains'
  }, {
    id: uuid(),
    value: '!contains',
    label: 'does not contain'
  }, {
    id: uuid(),
    value: 'responded',
    label: 'responded'
  }, {
    id: uuid(),
    value: 'notresponded',
    label: 'not responded'
  }],
  application: [{
    id: uuid(),
    value: 'used',
    label: 'used'
  }, {
    id: uuid(),
    value: 'notused',
    label: 'not used'
  }],
  workflow: [{
    id: uuid(),
    value: 'completed',
    label: 'Completed'
  }, {
    id: uuid(),
    value: 'incomplete',
    label: 'Incomplete'
  }, {
    id: uuid(),
    value: 'notStarted',
    label: 'Not Started'
  }],
  workflowNonRS: [{
    id: uuid(),
    value: 'completed',
    label: 'Completed'
  }, {
    id: uuid(),
    value: 'notcompleted',
    label: 'Incomplete or Not Started'
  }]
};
const GUIDE_TIME_RANGE_SEEN_OPTIONS = [{
  label: 'ever',
  value: 'ever'
}, {
  label: 'before',
  value: '<='
}, {
  label: 'since',
  value: '>='
}, {
  label: 'within last',
  value: 'withinlast'
}, {
  label: 'not within last',
  value: '!withinlast'
}, {
  label: 'at least',
  value: 'atleast'
}, {
  label: 'at most',
  value: 'atmost'
}];
const GUIDE_TIME_RANGE_LAST_SEEN_OPTIONS = [{
  label: 'before',
  value: '<='
}, {
  label: 'since',
  value: '>='
}, {
  label: 'within last',
  value: 'withinlast'
}, {
  label: 'not within last',
  value: '!withinlast'
}, {
  label: 'between',
  value: 'between'
}];
const GUIDE_TIME_RANGE_NOT_SEEN_OPTIONS = [{
  label: 'ever',
  value: 'ever'
}, {
  label: 'before',
  value: '<='
}, {
  label: 'since',
  value: '>='
}, {
  label: 'within last',
  value: 'withinlast'
}, {
  label: 'not within last',
  value: '!withinlast'
}];
const PAGE_TIME_RANGE_VIEWED_OPTIONS = [{
  label: 'ever',
  value: 'ever'
}, {
  label: 'within last',
  value: 'withinlast'
}, {
  label: 'since',
  value: 'since'
}, {
  label: 'at least',
  value: 'atleast'
}, {
  label: 'at most',
  value: 'atmost'
}];
const PAGE_TIME_RANGE_LAST_VIEWED_OPTIONS = [{
  label: 'before',
  value: 'before'
}, {
  label: 'since',
  value: 'since'
}, {
  label: 'within last',
  value: 'withinlast'
}, {
  label: 'not within last',
  value: '!withinlast'
}, {
  label: 'between',
  value: 'between'
}];
const PAGE_TIME_RANGE_NOT_VIEWED_OPTIONS = [{
  label: 'ever',
  value: 'ever'
}, {
  label: 'within last',
  value: 'withinlast'
}, {
  label: 'since',
  value: 'since'
}];
const FEATURE_TIME_RANGE_CLICKED_OPTIONS = [{
  label: 'ever',
  value: 'ever'
}, {
  label: 'within last',
  value: 'withinlast'
}, {
  label: 'since',
  value: 'since'
}, {
  label: 'at least',
  value: 'atleast'
}, {
  label: 'at most',
  value: 'atmost'
}];
const FEATURE_TIME_RANGE_LAST_CLICKED_OPTIONS = [{
  label: 'before',
  value: 'before'
}, {
  label: 'since',
  value: 'since'
}, {
  label: 'within last',
  value: 'withinlast'
}, {
  label: 'not within last',
  value: '!withinlast'
}, {
  label: 'between',
  value: 'between'
}];
const FEATURE_TIME_RANGE_NOT_CLICKED_OPTIONS = [{
  label: 'ever',
  value: 'ever'
}, {
  label: 'within last',
  value: 'withinlast'
}, {
  label: 'since',
  value: 'since'
}];
const GUIDE_ELEMENT_TIME_RANGE_CLICKED_OPTIONS = [{
  label: 'ever',
  value: 'ever'
}, {
  label: 'within last',
  value: 'withinlast'
}, {
  label: 'since',
  value: 'since'
}, {
  label: 'at least',
  value: 'atleast'
}, {
  label: 'at most',
  value: 'atmost'
}];
const GUIDE_ELEMENT_TIME_RANGE_NOT_CLICKED_OPTIONS = [{
  label: 'ever',
  value: 'ever'
}, {
  label: 'within last',
  value: 'withinlast'
}, {
  label: 'since',
  value: 'since'
}];
const APP_USAGE_TIME_RANGE_OPTIONS = [{
  label: 'ever',
  value: 'ever'
}, {
  label: 'within last',
  value: 'withinlast'
}, {
  label: 'since',
  value: 'since'
}];
const POLL_TIME_RANGE_OPTIONS = [{
  label: 'ever',
  value: 'ever'
}, {
  label: 'before',
  value: '<='
}, {
  label: 'since',
  value: '>='
}, {
  label: 'within last',
  value: 'withinlast'
}, {
  label: 'not within last',
  value: '!withinlast'
}, {
  label: 'between',
  value: 'between'
}];
const INPUT_OPTIONS_MAP = {
  PICK_LIST: 'PickList',
  FREE_FORM: 'FreeForm',
  NUMBER_SCALE: 'NumberScale',
  POSITIVE_NEGATIVE: 'PositiveNegative'
};
const WORKFLOW_TIME_RANGE_COMPLETED_INCOMPLETE_OPTIONS = [{
  label: 'ever',
  value: 'ever'
}, {
  label: 'within last',
  value: 'withinlast'
}, {
  label: 'since',
  value: 'since'
}
// { label: 'at least', value: 'atleast' },
// { label: 'at most', value: 'atmost' }
];

const WORKFLOW_TIME_RANGE_NOT_STARTED_OPTIONS = [{
  label: 'ever',
  value: 'ever'
}, {
  label: 'within last',
  value: 'withinlast'
}, {
  label: 'since',
  value: 'since'
}];

function getDefaultValueForSchema({
  schema
}) {
  let defaultValue;
  switch (schema) {
    case 'integer':
    case 'float':
      defaultValue = 0;
      break;
    default:
      defaultValue = null;
  }
  return defaultValue;
}
function getDefaultOperatorForSchema(operators, {
  schema
}) {
  const schemaOperators = operators[schema];
  return get(schemaOperators, '[0]', null);
}
function getEntitiesByAppSelection(app, entityList, config) {
  if (!entityList) {
    return [];
  }
  const appId = config.usesMultiApp ? get(app, 'id', null) : config.appId;
  if (appId === null) {
    return entityList;
  }
  return entityList.filter(entity => entity.appId === appId);
}
function getPollsForGuide(guide) {
  if (!guide.polls?.length) {
    return [];
  }
  const polls = keyBy(guide.polls, 'id');
  return flatMap(guide.steps, step => step.pollIds).map(id => polls[id]).filter(poll => poll);
}
function generateRule(operators, schema) {
  return {
    schema,
    id: uuid(),
    operator: getDefaultOperatorForSchema(operators, schema),
    value: getDefaultValueForSchema(schema)
  };
}
function addCommasToNumber(num) {
  const str = String(num);
  const parts = str.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
}
function getNoResourcesFoundText({
  rule,
  isMultiApp,
  resourceType
}) {
  const resourcesLabel = {
    page: 'pages',
    feature: 'features',
    trackType: 'track events',
    guide: 'guides',
    guideElement: 'guide elements',
    resourceCenter: 'resource centers',
    poll: 'polls'
  }[resourceType || rule.id];
  if (isMultiApp && rule.appId !== '*') {
    return `No ${resourcesLabel} found. Please select a different app.`;
  }
  return `No ${resourcesLabel} found.`;
}
function isCsvUploadOperator(operator) {
  return ['listContains', '!listContains'].includes(operator);
}
function generateEngageUrlByEnv(_env) {
  if (!_env) {
    return '';
  }
  const env = _env.toLowerCase();
  if (env === 'prod') {
    return 'https://app.pendo.io';
  }
  if (env === 'prod-eu') {
    return 'https://app.eu.pendo.io';
  }
  if (env === 'prod-us1') {
    return 'https://app.us1.app.pendo.io';
  }
  if (env === 'jp-prod') {
    return 'https://app.jpn.pendo.io';
  }
  return `https://app.pendo-${env}.pendo-dev.com`;
}

// Copy of fucntion used in segments.js in engage
// Let's be clear, this entire function is a big guess. Segments in engage have evolved a ton over the past 5 years,
// so there might be odd edges that we don't catch here (yet). If you find a new scenario where
// we incorrectly identify a segment as adopt/engage, slide it in here!
function filterRuleMightHaveBeenMadeInAdopt(filterRule, segmentLastUpdatedDate) {
  // adopt filter rules attach an icon object to each rule, but engage segments do not
  const hasIcon = filterRule.icon;
  if (hasIcon) {
    return true;
  }

  // $valid and key were added as part of a large refactor in 2020 - ancient engage segments won't have these keys
  const timestampOfValidInCodebase = 1587441600000; // 04/21/2020
  const segmentCanHaveValidOrKey = segmentLastUpdatedDate >= timestampOfValidInCodebase;
  const ruleDoesNotHaveValidOrKey = !filterRule.$valid && !filterRule.key;
  if (segmentCanHaveValidOrKey && ruleDoesNotHaveValidOrKey) {
    return true;
  }
  return false;
}
function isEngageSegment(segment) {
  const adoptSegmentsFirstCreatedAt = 1553558400000; // 03/26/2019
  if (segment.createdAt < adoptSegmentsFirstCreatedAt) {
    return true;
  }

  // as long as default segments haven't been edited, they work fine in both places
  const isDefaultSegment = segment.id.includes('_PENDO_DEFAULT_');
  const lastUpdatedBySystemUser = segment.lastUpdatedByUser.username === '--system-user--';
  if (isDefaultSegment && lastUpdatedBySystemUser) {
    return false;
  }
  const filters = segment.definition?.filters || [];

  // if any filter rule might have been made in adopt, then it's an adopt segment and not an engage segment
  return !filters.some(rule => {
    if (rule.or) {
      return rule.or.some(subrule => filterRuleMightHaveBeenMadeInAdopt(subrule, segment.lastUpdatedAt));
    }
    return filterRuleMightHaveBeenMadeInAdopt(rule, segment.lastUpdatedAt);
  });
}

// rules -> segment

function validateAndBuildSegment({
  rules,
  ...params
}) {
  const isValid = validateSegmentRules(rules);
  if (!isValid) {
    return null;
  }
  return buildSegment({
    rules,
    ...params
  });
}
function validateSegmentRules(rules = []) {
  if (!rules.length) {
    return false;
  }
  return rules.every(orRules => {
    return orRules.every(rule => isRuleValid(rule));
  });
}
function isRuleValid(rule) {
  const {
    schema,
    operator,
    value,
    time,
    guide,
    guideElementId,
    poll,
    page,
    feature,
    app,
    segment,
    workflow,
    first,
    last,
    granularity,
    count
  } = rule;
  const noSourceEntryProvided = value == null && guide == null && page == null && feature == null && app == null && workflow == null && segment == null;
  if (isEmpty(schema) || isEmpty(operator) || noSourceEntryProvided) {
    return false;
  }
  if (schema.schema === 'poll') {
    if (!guide) {
      return false;
    }
    if (!poll) {
      return false;
    }
    if (time === 'withinlast' && (!count || !granularity)) {
      return false;
    }
    if (time === '!withinlast' && (!count || !granularity)) {
      return false;
    }
    if (time === 'since' && !first) {
      return false;
    }
    if (time === 'before' && !first) {
      return false;
    }
    if (time === 'between' && (!first || !last)) {
      return false;
    }
    if (!RegExp(/^(not)?responded$/).test(operator.value)) {
      return value !== null && value !== undefined;
    }
  }
  if (schema.schema === 'guide') {
    if (!guide) {
      return false;
    }
    if (time === 'withinlast' && (!count || !granularity)) {
      return false;
    }
    if (time === '!withinlast' && (!count || !granularity)) {
      return false;
    }
    if (time === 'since' && !first) {
      return false;
    }
    if (time === 'before' && !first) {
      return false;
    }
    if (time === 'between' && (!first || !last)) {
      return false;
    }
  }
  if (schema.schema === 'segment') {
    if (!segment) {
      return false;
    }
    if (rule.segmentInvalid) {
      return false;
    }
  }
  if (schema.schema === 'guideElement') {
    if (!guide || !guideElementId) {
      return false;
    }
    if (time === 'withinlast' && !value) {
      return false;
    }
    if (time === 'since' && !value) {
      return false;
    }
    if (time === 'ever') {
      return true;
    }
    if (time === 'atleast' && !value) {
      return false;
    }
    if (time === 'atmost' && !value) {
      return false;
    }
  }
  if (schema.schema === 'page') {
    if (!page) {
      return false;
    }
    if (time === 'withinlast' && !value) {
      return false;
    }
    if (time === '!withinlast' && !value) {
      return false;
    }
    if (time === 'since' && !value) {
      return false;
    }
    if (time === 'before' && !value) {
      return false;
    }
    if (time === 'between' && (!value?.first || !value?.last)) {
      return false;
    }
    if (time === 'ever') {
      return true;
    }
    if (time === 'atleast' && !value) {
      return false;
    }
    if (time === 'atmost' && !value) {
      return false;
    }
  }

  // duplicating this logic since we're adding at least/at most in the future
  if (schema.schema === 'feature') {
    if (!feature) {
      return false;
    }
    if (time === 'withinlast' && !value) {
      return false;
    }
    if (time === '!withinlast' && !value) {
      return false;
    }
    if (time === 'since' && !value) {
      return false;
    }
    if (time === 'before' && !value) {
      return false;
    }
    if (time === 'between' && (!value?.first || !value?.last)) {
      return false;
    }
    if (time === 'atleast' && !value) {
      return false;
    }
    if (time === 'atmost' && !value) {
      return false;
    }
    if (time === 'ever') {
      return true;
    }
  }
  if (schema.schema === 'application') {
    if (!rule.app) {
      return false;
    }
    if (time === 'ever') {
      return true;
    }
    if (time === 'withinlast' || time === '!withinlast') {
      if (!value || !value.granularity || isUndefined(value.count)) {
        return false;
      }
    }
    if (time === 'since' && !value) {
      return false;
    }
    if (time === null) {
      return false;
    }
  }
  if (schema.schema === 'workflow') {
    if (!workflow) {
      return false;
    }
    if (time === 'withinlast' && !value) {
      return false;
    }
    if (time === 'since' && !value) {
      return false;
    }
    if (time === 'atleast' && !value) {
      return false;
    }
    if (time === 'atmost' && !value) {
      return false;
    }
    if (time === 'ever') {
      return true;
    }
  }
  if (!operator.multivalue) {
    return true;
  }
  if (operator.value === 'between') {
    return !!value.first && !!value.last;
  }

  // rule is csv upload related, but does not have a reference to an entity tag
  if (isCsvUploadOperator(operator.value) && !rule.tagId) {
    return false;
  }
  if (!value) {
    return false;
  }
  return Object.keys(value).every(valKey => value[valKey] != null);
}

// appId is legacy from before multi-app was introduced, and will be removed
function buildSegment({
  rules,
  name,
  flagName,
  appId,
  selectedVisitorType = 'visitorId'
}) {
  const isNestedSegment = containsNestedSegment(rules);
  const filters = parseRulesAsFilters(rules, appId);
  const segment = {
    name,
    flagName,
    shared: true,
    definition: {
      filters,
      advanced: false,
      advancedOptions: {
        selectedVisitorType
      }
    }
  };
  if (isNestedSegment) {
    segment.compound = buildCompoundOperator({
      rules
    });
    segment.pipelines = buildCompoundSegmentPipelines({
      filters,
      rules,
      appId,
      selectedVisitorType
    });
  } else {
    segment.pipeline = buildSegmentPipeline({
      filters,
      selectedVisitorType
    });
  }
  return segment;
}
function buildSegmentPipeline({
  filters,
  selectedVisitorType = 'visitorId'
}) {
  return [{
    source: {
      visitors: null
    }
  }, ...(operators.identified(selectedVisitorType) ? [operators.identified(selectedVisitorType)] : []), ...generateMergeBlocks(filters, selectedVisitorType), ...filters.map(getPipelineFiltersFromFilter), {
    select: {
      visitorId: 'visitorId'
    }
  }];
}
function buildCompoundOperator({
  rules,
  inlineCount = 0,
  logicalOperator = '&&'
} = {}) {
  if (!Array.isArray(rules)) {
    const {
      segment,
      operator
    } = rules;
    if (operator.value === '!=') {
      return `!${segment.id}`;
    }
    return segment.id;
  }
  let compoundOperator = '';

  // Extract rules containing nested segment rules
  const nestedSegmentRules = rules.filter(isNestedSegmentRule);
  const otherRules = rules.filter(rule => !isNestedSegmentRule(rule));

  // Build "regular" pipeline if other rules exist
  if (otherRules.length) {
    compoundOperator += `inline${inlineCount}`;
    inlineCount++;
  }
  if (nestedSegmentRules.length) {
    nestedSegmentRules.forEach((orRules, index) => {
      inlineCount += index;
      if (otherRules.length || index > 0) {
        compoundOperator += ` ${logicalOperator} `;
      }
      compoundOperator += `${buildCompoundOperator({
        rules: orRules,
        inlineCount,
        logicalOperator: '||'
      })}`;
    });
  }
  if (inlineCount > 0 && logicalOperator === '||') {
    compoundOperator = `(${compoundOperator})`;
  }
  return compoundOperator;
}
function buildCompoundSegmentPipelines({
  rules,
  appId,
  inlineCount = 0,
  selectedVisitorType = 'visitorId'
} = {}) {
  if (!Array.isArray(rules)) {
    return;
  }
  let pipelines = {};

  // Extract rules containing nested segment rules
  const nestedSegmentRules = rules.filter(isNestedSegmentRule);
  const otherRules = rules.filter(rule => !isNestedSegmentRule(rule));

  // Build "regular" compound rules if other rules exist
  if (otherRules.length) {
    const pipeline = buildSegmentPipeline({
      filters: parseRulesAsFilters(otherRules, appId),
      selectedVisitorType
    });
    pipelines[`inline${inlineCount}`] = pipeline;
    inlineCount++;
  }
  if (nestedSegmentRules.length) {
    nestedSegmentRules.forEach((orRules, index) => {
      inlineCount += index;
      const more = buildCompoundSegmentPipelines({
        rules: orRules,
        appId,
        inlineCount,
        selectedVisitorType
      });
      pipelines = {
        ...pipelines,
        ...more
      };
    });
  }
  return pipelines;
}
function parseRulesAsFilters(rules, overrideAppId) {
  // mapRulesToFilters needs to rely on the object reference to keep track of index
  // this MUST match the index in `merge` for `lastState<index>` which is calculated
  // indepedently in `getMergeFromFilter`
  const options = {
    filterIndex: 0,
    overrideAppId
  };
  return rules.map(orRules => {
    let filter = {};
    if (!Array.isArray(orRules)) {
      orRules = [orRules];
    }
    if (orRules.length > 1) {
      filter.or = orRules.map(mapRulesToFilters(options));
    } else {
      [filter] = orRules.map(mapRulesToFilters(options));
    }
    return filter;
  });
}
function mapRulesToFilters(options) {
  return ({
    operator,
    schema,
    value,
    ...rule
  }) => {
    const appId = options.overrideAppId || get(rule, 'app.id', null);
    if (schema.schema === 'segment') {
      const {
        segment
      } = rule;
      return {
        ...schema,
        type: 'segment',
        kind: 'segment',
        segmentId: get(segment, 'id', null),
        operator: get(operator, 'value', null),
        selectedApp: {
          id: appId
        },
        // temp override for subs without usesMultiApp
        filterIndex: options.filterIndex++
      };
    }
    if (schema.schema === 'guide') {
      const {
        time,
        guide
      } = rule;
      return {
        ...schema,
        type: 'guide',
        guideId: get(guide, 'id', null),
        // rule may reference deleted guide
        guideStepId: get(guide, 'steps[0].id', null),
        // guaranteed entry point for guide for at least/at most count
        operator: get(operator, 'value', null),
        time: time || 'ever',
        value,
        ...getConditionalKeyValues(rule),
        selectedApp: {
          id: appId
        },
        filterIndex: options.filterIndex++
      };
    }
    if (schema.schema === 'guideElement') {
      const {
        time,
        guideElementId,
        guide,
        guideStepId,
        guideElementName
      } = rule;
      return {
        ...schema,
        type: 'guideElement',
        guideElementId,
        guideStepId,
        guideElementName,
        guideId: get(guide, 'id', null),
        operator: get(operator, 'value', null),
        time: time || 'ever',
        value,
        selectedApp: {
          id: appId
        },
        filterIndex: options.filterIndex++
      };
    }
    if (schema.schema === 'page') {
      const {
        time,
        page
      } = rule;
      return {
        ...schema,
        type: 'page',
        pageId: get(page, 'id', null),
        // rule may reference deleted page
        operator: get(operator, 'value', null),
        time: time || 'ever',
        value,
        selectedApp: {
          id: appId
        },
        filterIndex: options.filterIndex++
      };
    }
    if (schema.schema === 'feature') {
      const {
        time,
        feature
      } = rule;
      return {
        ...schema,
        type: 'feature',
        featureId: get(feature, 'id', null),
        // rule may reference deleted feature
        operator: get(operator, 'value', null),
        time: time || 'ever',
        value,
        selectedApp: {
          id: appId
        },
        filterIndex: options.filterIndex++
      };
    }
    if (schema.schema === 'poll') {
      const {
        time,
        guide,
        poll
      } = rule;
      const noFormatPollTypes = ['PositiveNegative', 'NumberScale'];
      if (!noFormatPollTypes.includes(get(poll, 'attributes.type'))) {
        value = formatValueFromSchema(value, 'string');
      }
      return {
        ...schema,
        type: 'poll',
        guideId: get(guide, 'id', null),
        // rule may reference deleted guide
        pollId: get(poll, 'id', null),
        operator: get(operator, 'value', null),
        time: time || 'ever',
        selectedApp: {
          id: appId
        },
        filterIndex: options.filterIndex++,
        value,
        ...getConditionalKeyValues(rule)
      };
    }
    if (schema.schema === 'application') {
      const {
        time
      } = rule;
      const aggFormattedAppId = convertAppIdToAggSafeId(appId);
      return {
        ...schema,
        operator: get(operator, 'value', null),
        time: time || 'ever',
        value,
        selectedApp: {
          id: appId
        },
        filterIndex: options.filterIndex++,
        field: `visitor.auto_${aggFormattedAppId}.lastVisit`
      };
    }
    if (schema.schema === 'workflow') {
      const {
        workflow,
        time
      } = rule;
      return {
        ...schema,
        type: 'workflow',
        workflowId: get(workflow, 'id', null),
        operator: get(operator, 'value', null),
        filterIndex: options.filterIndex++,
        time: time || 'ever',
        value
      };
    }

    // CSV
    if (isCsvUploadOperator(operator.value)) {
      return {
        ...schema,
        type: 'tag',
        tagId: get(rule, 'tagId', null),
        operator: get(operator, 'value', null),
        selectedApp: {
          id: appId
        },
        filterIndex: options.filterIndex++,
        value: value != null ? formatValueFromSchema(value, 'string') : value
      };
    }
    return {
      ...schema,
      // when we remove single app aka usesMultiApp becomes the default, remove selectedApp
      selectedApp: {
        id: appId
      },
      value: formatValueFromSchema(value, schema.schema, operator.value),
      operator: get(operator, 'value', null)
    };
  };
}
function getConditionalKeyValues(rule) {
  const conditionalKeys = ['first', 'last', 'granularity', 'count'];
  const keysToInclude = {};
  conditionalKeys.forEach(key => {
    if (!isUndefined(rule[key])) {
      keysToInclude[key] = rule[key];
    }
  });
  return keysToInclude;
}
function formatValueFromSchema(value, schema, operator = null) {
  if (operatorRequiresUnitCountFormat[operator]) {
    schema = 'unitCount'; // eslint-disable-line no-param-reassign
  }

  let formattedValue = null;
  switch (schema) {
    case 'time':
      {
        formattedValue = formatTimeValue(value);
        break;
      }
    case 'string':
    case 'list':
      {
        formattedValue = `"${value}"`;
        break;
      }
    case 'unitCount':
      {
        const {
          count,
          granularity
        } = value;
        formattedValue = {
          count: Number(count) * -1,
          granularity: formatValueFromSchema(granularity, 'string')
        };
        break;
      }
    case 'integer':
      {
        formattedValue = parseInt(value, 10);
        break;
      }
    case 'float':
      {
        formattedValue = parseFloat(value);
        break;
      }
    case 'boolean':
      {
        formattedValue = value;
        break;
      }
    default:
      throw new Error(`Invalid schema ${schema} for value ${value}`);
  }
  return formattedValue;
}
function generateMergeBlocks(filters, selectedVisitorType = 'visitorId') {
  return filters.map(filter => getMergeFromFilter(filter, selectedVisitorType)).reduce((list, merge) => {
    if (Array.isArray(merge)) {
      list.push(...merge);
      return list;
    }
    list.push(merge);
    return list;
  }, []).filter(Boolean);
}
function getMergeFromFilter({
  or,
  type,
  guideId,
  guideStepId,
  guideElementId,
  pollId,
  pageId,
  featureId,
  tagId,
  workflowId,
  filterIndex
}, selectedVisitorType = 'visitorId') {
  if (or) {
    return or.map(filter => getMergeFromFilter(filter, selectedVisitorType));
  }
  let merge;
  switch (type) {
    case 'guide':
      {
        const lastStateId = `lastState${filterIndex}`;
        const guideLastTimeId = `guideLastTime${filterIndex}`;
        const guideFirstTimeId = `guideFirstTime${filterIndex}`;
        const guideCountId = `guideSeenCount${filterIndex}`;
        merge = {
          fields: ['guideId', 'visitorId'],
          mappings: {
            [lastStateId]: 'lastState',
            [guideLastTimeId]: 'lastSeen',
            [guideFirstTimeId]: 'firstSeen',
            [guideCountId]: 'seenCount'
          },
          pipeline: [{
            source: {
              guidesSeenEver: {
                guideId,
                guideStepId
              }
            }
          }, ...(operators.identified(selectedVisitorType) ? [operators.identified(selectedVisitorType)] : []), {
            group: {
              group: ['visitorId'],
              fields: [{
                lastState: {
                  first: 'lastState'
                }
              }, {
                lastSeen: {
                  first: 'lastSeenAt'
                }
              }, {
                firstSeen: {
                  first: 'firstSeenAt'
                }
              }, {
                seenCount: {
                  first: 'seenCount'
                }
              }]
            }
          }]
        };
        break;
      }
    case 'guideElement':
      {
        const itemSeenKey = `guideElement${filterIndex}_itemSeen`;
        merge = {
          fields: ['guideId', 'visitorId'],
          mappings: {
            [`${itemSeenKey}.count`]: 'clickCount',
            [`${itemSeenKey}.lastUsed`]: 'lastClickAt'
          },
          pipeline: [{
            source: {
              guidesElementClickEver: {
                guideId,
                guideStepId,
                uiElementId: guideElementId
              }
            }
          }, ...(operators.identified(selectedVisitorType) ? [operators.identified(selectedVisitorType)] : []), {
            group: {
              group: ['visitorId'],
              fields: [{
                lastClickAt: {
                  first: 'lastClickAt'
                }
              }, {
                clickCount: {
                  first: 'clickCount'
                }
              }]
            }
          }]
        };
        break;
      }
    case 'page':
      {
        const itemSeenKey = `page${filterIndex}_itemSeen`;
        merge = {
          fields: ['visitorId'],
          pipeline: [{
            source: {
              visitors: null
            }
          }, {
            itemSeen: {
              [itemSeenKey]: {
                pageId
              }
            }
          }, ...(operators.identified(selectedVisitorType) ? [operators.identified(selectedVisitorType)] : []), {
            select: {
              visitorId: 'visitorId',
              [itemSeenKey]: itemSeenKey
            }
          }]
        };
        break;
      }
    case 'feature':
      {
        const itemSeenKey = `feature${filterIndex}_itemSeen`;
        merge = {
          fields: ['visitorId'],
          pipeline: [{
            source: {
              visitors: null
            }
          }, {
            itemSeen: {
              [itemSeenKey]: {
                featureId
              }
            }
          }, ...(operators.identified(selectedVisitorType) ? [operators.identified(selectedVisitorType)] : []), {
            select: {
              visitorId: 'visitorId',
              [itemSeenKey]: itemSeenKey
            }
          }]
        };
        break;
      }
    case 'poll':
      {
        const pollResponseId = `pollResponse${filterIndex}`;
        const pollTimeId = `pollTime${filterIndex}`;
        merge = {
          fields: ['visitorId'],
          mappings: {
            [pollResponseId]: pollResponseId,
            [pollTimeId]: pollTimeId
          },
          pipeline: [{
            source: {
              pollsSeenEver: {
                guideId,
                pollId
              }
            }
          }, ...(operators.identified(selectedVisitorType) ? [operators.identified(selectedVisitorType)] : []), {
            select: {
              visitorId: 'visitorId',
              [pollResponseId]: 'response',
              [pollTimeId]: 'time'
            }
          }]
        };
        break;
      }
    case 'tag':
      {
        merge = {
          fields: ['visitorId'],
          pipeline: [{
            source: {
              visitorTags: {
                visitorTagId: tagId
              }
            }
          }, ...(operators.identified(selectedVisitorType) ? [operators.identified(selectedVisitorType)] : []), {
            select: {
              visitorId: 'visitorId',
              [`tag${filterIndex}`]: 'true'
            }
          }]
        };
        break;
      }
    case 'workflow':
      {
        const lastCompletedAtId = `lastCompletedAt${filterIndex}`;
        const startedAtId = `startedAt${filterIndex}`;
        merge = {
          fields: ['visitorId'],
          pipeline: [{
            source: {
              workflowCompletionEver: {
                workflowId
              }
            }
          }, ...(operators.identified(selectedVisitorType) ? [operators.identified(selectedVisitorType)] : []), {
            select: {
              visitorId: 'visitorId',
              [lastCompletedAtId]: 'lastCompletedAt',
              [startedAtId]: 'startedAt',
              incompleteCount: 'incompleteCount',
              completeCount: 'completeCount'
            }
          }]
        };
        break;
      }
    default:
      return null;
  }
  return {
    merge
  };
}
function getPipelineFiltersFromFilter({
  operator,
  type,
  value,
  or,
  ...rawFilter
}) {
  if (or) {
    return parseOrFilterForPipeline(or);
  }
  if (type === 'guide') {
    return parseGuideFilterPipeline({
      operator,
      value,
      ...rawFilter
    });
  }
  if (type === 'guideElement') {
    return parseGuideElementFilterPipeline({
      operator,
      value,
      ...rawFilter
    });
  }
  if (type === 'page') {
    return parsePageFilterPipeline({
      operator,
      value,
      ...rawFilter
    });
  }
  if (type === 'feature') {
    return parseFeatureFilterPipeline({
      operator,
      value,
      ...rawFilter
    });
  }
  if (type === 'poll') {
    return parsePollFilterPipeline({
      operator,
      value,
      ...rawFilter
    });
  }
  if (type === 'tag') {
    return parseListContainsFilterPipeline({
      operator,
      ...rawFilter
    });
  }
  if (type === 'application') {
    return parseApplicationUsageFilterPipeline({
      operator,
      value,
      ...rawFilter
    });
  }
  if (type === 'workflow') {
    return parseWorkflowUsageFilterPipeline({
      operator,
      value,
      ...rawFilter
    });
  }
  let filter = '';
  switch (operator) {
    case 'contains':
      {
        filter = {
          filter: `contains(${type},${value})`
        };
        break;
      }
    case '!contains':
      {
        filter = {
          filter: `!contains(${type},${value})`
        };
        break;
      }
    case 'between':
      {
        const {
          first,
          last
        } = value;
        filter = {
          filter: `${type}>=date("${first}")&&${type}<=date("${last}")`
        };
        break;
      }
    case 'withinLast':
      {
        const {
          count,
          granularity
        } = value;
        filter = {
          filter: `${type}<=now()&&${type}>=dateAdd(now(),${count},${granularity})`
        };
        break;
      }
    case '!withinLast':
      {
        const {
          count,
          granularity
        } = value;
        filter = {
          filter: `${type}>=now()||${type}<=dateAdd(now(),${count},${granularity})`
        };
        break;
      }
    default:
      if (rawFilter.schema === 'time') {
        filter = {
          filter: `${type}${operator}date("${value}")`
        };
      } else {
        filter = {
          filter: `${type}${operator}${value}`
        };
      }
  }
  return filter;
}
function parseListContainsFilterPipeline({
  operator,
  filterIndex
}) {
  let filter;
  switch (operator) {
    case 'listContains':
      {
        filter = {
          filter: `!isNil(tag${filterIndex})`
        };
        break;
      }
    case '!listContains':
      {
        filter = {
          filter: `isNil(tag${filterIndex})`
        };
        break;
      }
  }
  return filter;
}
function parseOrFilterForPipeline(filters) {
  return filters.map(getPipelineFiltersFromFilter).reduce((acc, rule) => {
    if (!acc.filter) {
      acc.filter = `(${rule.filter})`;
    } else {
      acc.filter += `||(${rule.filter})`;
    }
    return acc;
  }, {
    filter: ''
  });
}
function containsNestedSegment(rules) {
  return rules.some(isNestedSegmentRule);
}
function isNestedSegmentRule(rule) {
  // Outer "and" rule
  if (Array.isArray(rule)) {
    return rule.some(isNestedSegmentRule);
  }

  // Inner "or" rule
  return !!(rule && rule.schema?.schema === 'segment');
}

// segment -> rules

function getRulesFromSegment(segment) {
  const {
    filters
  } = segment.definition;
  return filters.reduce((rules, filter) => {
    if (filter.or) {
      rules.push(filter.or.map(parseOrRuleFromFilter));
    } else {
      rules.push([parseOrRuleFromFilter(filter)]);
    }
    return rules;
  }, []);
}
function parseOrRuleFromFilter(rule) {
  const {
    operator,
    value,
    ...schema
  } = migrateRule(rule);
  const {
    id,
    type,
    filterIndex,
    kind,
    selectedApp,
    name,
    ...rest
  } = schema;
  if (schema.schema === 'segment') {
    const {
      segmentId
    } = schema;
    return {
      schema,
      segmentId,
      operator: findSchemaOperator(operator, schema.schema)
    };
  }
  if (schema.schema === 'guide') {
    return {
      ...rest,
      schema,
      value,
      operator: findSchemaOperator(operator, schema.schema)
    };
  }
  if (schema.schema === 'poll') {
    return {
      ...rest,
      schema,
      value: parseValueFromDefinition(value, 'string'),
      operator: findSchemaOperator(operator, schema.schema)
    };
  }
  if (schema.schema === 'page') {
    const {
      time,
      pageId
    } = schema;
    return {
      schema,
      time,
      value: get(value, 'length') ? value.slice() : value,
      pageId,
      operator: findSchemaOperator(operator, schema.schema)
    };
  }
  if (schema.schema === 'feature') {
    const {
      time,
      featureId
    } = schema;
    return {
      schema,
      time,
      value: get(value, 'length') ? value.slice() : value,
      featureId,
      operator: findSchemaOperator(operator, schema.schema)
    };
  }
  if (schema.schema === 'application') {
    const {
      time
    } = schema;
    return {
      schema,
      time,
      value: get(value, 'length') ? value.slice() : value,
      operator: findSchemaOperator(operator, schema.schema)
    };
  }
  if (schema.type === 'tag') {
    const {
      tagId
    } = schema;
    return {
      schema,
      tagId,
      value: parseValueFromDefinition(value, schema.schema, operator),
      operator: findSchemaOperator(operator, schema.schema)
    };
  }
  if (schema.schema === 'workflow') {
    const {
      workflowId,
      time
    } = schema;
    const isNonRSWorkflow = operator === 'notcompleted';
    return {
      schema,
      workflowId,
      time,
      value: get(value, 'length') ? value.slice() : value,
      operator: findSchemaOperator(operator, isNonRSWorkflow ? 'workflowNonRS' : schema.schema)
    };
  }
  if (schema.schema === 'guideElement') {
    const {
      time,
      guideId,
      guideStepId,
      guideElementId,
      guideElementName
    } = schema;
    return {
      schema,
      guideId,
      guideStepId,
      guideElementId,
      guideElementName,
      time,
      value: get(value, 'length') ? value.slice() : value,
      operator: findSchemaOperator(operator, schema.schema)
    };
  }

  // when we remove single app aka usesMultiApp becomes the default, comment this back in
  // Before you could view multiple apps in adopt, `selectedApp` was added to all rules,
  // even rules that are not technically app-specific
  // for cases where rules are actually subscription-wide, such as visitor comparisons,
  // remove selectedApp to avoid confusion
  // delete schema.selectedApp;

  return {
    schema,
    value: parseValueFromDefinition(value, schema.schema, operator),
    operator: findSchemaOperator(operator, schema.schema)
  };
}
function parseValueFromDefinition(rawValue, schema, operator) {
  if (operatorRequiresUnitCountFormat[operator]) {
    schema = 'unitCount'; // eslint-disable-line no-param-reassign
  }

  let value;
  switch (schema) {
    case 'string':
    case 'list':
      {
        value = (rawValue || '').replace(/"/g, '');
        break;
      }
    case 'time':
      {
        const parseTimeValue = dateVal => {
          // legacy value (in ms)
          if (typeof dateVal === 'number') {
            return moment.tz(dateVal, 'UTC').format(DATE_FORMAT.iso);
          }
          return dateVal;
        };
        if (isPlainObject(rawValue)) {
          value = Object.entries(rawValue).reduce((acc, [key, val]) => {
            acc[key] = parseTimeValue(val);
            return acc;
          }, {});
        } else {
          value = parseTimeValue(rawValue);
        }
        break;
      }
    case 'unitCount':
      {
        const {
          count,
          granularity
        } = rawValue;
        value = {
          count: count * -1,
          granularity: parseValueFromDefinition(granularity, 'string')
        };
        break;
      }
    case 'integer':
    case 'float':
    default:
      value = rawValue;
  }
  return value;
}
function findSchemaOperator(operator, schema) {
  const list = SEGMENT_OPERATORS[schema];
  return list.find(opt => opt.value === operator);
}

// segment builder ui utilities

function getAbsoluteDateRangeConfig(rule) {
  return {
    component: 'absolute-date-range',
    props: {
      value: {
        first: rule.first || rule.value?.first,
        last: rule.last || rule.value?.last
      }
    }
  };
}
function getRelativeDateRangeConfig(rule, field) {
  return {
    component: 'relative-date-range',
    props: {
      value: {
        count: field ? rule[field].count : rule.count,
        granularity: field ? rule[field].granularity : rule.granularity
      }
    }
  };
}
function getSingleDateConfig(rule, field = 'value') {
  return {
    component: 'single-date',
    props: {
      field,
      value: rule[field]
    }
  };
}
function getSegmentCsvConfig(rule) {
  return {
    component: 'segment-csv-upload',
    props: {
      value: {
        tagId: rule.tagId,
        filename: rule.value
      }
    }
  };
}
function getNumberConfig(rule, field = 'value') {
  return {
    component: 'number-input',
    props: {
      field,
      value: rule[field],
      min: 0
    }
  };
}
function getStringConfig(rule, field = 'value') {
  const supportedAutoFields = {
    id: true,
    lastservername: true,
    accountid: true
  };
  const [kind, group, fieldName] = rule.schema.field?.split('.');
  if (rule.schema.schema !== 'string' || rule.operator.value !== '==' && rule.operator.value !== '!=' || rule.schema.neverIndex === undefined || (rule.schema.neverIndex || rule.schema.group === 'auto') && !supportedAutoFields[fieldName]) {
    return {
      component: 'string-input',
      props: {
        field,
        value: rule[field]
      }
    };
  }
  return {
    component: 'metadata-value-select',
    props: {
      field,
      kind,
      group,
      fieldName,
      value: rule[field]
    }
  };
}
function getSegmentVisitorType(segment) {
  const segmentVisitorType = get(segment, 'definition.advancedOptions.selectedVisitorType', null);
  if (segmentVisitorType) {
    return segmentVisitorType;
  }
  return segment?.pipeline?.some(pipelineItem => pipelineItem.identified === 'visitorId') ? 'visitorId' : null;
}

//
var script$x = {
  name: 'SegmentMetricPanel',
  components: {
    PendoIcon,
    PendoMetric
  },
  directives: {
    PendoTooltip
  },
  props: {
    title: {
      type: String,
      default: undefined
    },
    value: {
      type: String,
      default: undefined
    },
    loading: {
      type: Boolean,
      default: false
    },
    wrapperClass: {
      type: String,
      default: undefined
    },
    tooltip: {
      type: Object,
      default: () => null
    }
  }
};

function normalizeComponent(template, style, script, scopeId, isFunctionalTemplate, moduleIdentifier /* server only */, shadowMode, createInjector, createInjectorSSR, createInjectorShadow) {
    if (typeof shadowMode !== 'boolean') {
        createInjectorSSR = createInjector;
        createInjector = shadowMode;
        shadowMode = false;
    }
    // Vue.extend constructor export interop.
    const options = typeof script === 'function' ? script.options : script;
    // render functions
    if (template && template.render) {
        options.render = template.render;
        options.staticRenderFns = template.staticRenderFns;
        options._compiled = true;
        // functional template
        if (isFunctionalTemplate) {
            options.functional = true;
        }
    }
    // scopedId
    if (scopeId) {
        options._scopeId = scopeId;
    }
    let hook;
    if (moduleIdentifier) {
        // server build
        hook = function (context) {
            // 2.3 injection
            context =
                context || // cached call
                    (this.$vnode && this.$vnode.ssrContext) || // stateful
                    (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext); // functional
            // 2.2 with runInNewContext: true
            if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
                context = __VUE_SSR_CONTEXT__;
            }
            // inject component styles
            if (style) {
                style.call(this, createInjectorSSR(context));
            }
            // register component module identifier for async chunk inference
            if (context && context._registeredComponents) {
                context._registeredComponents.add(moduleIdentifier);
            }
        };
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        options._ssrRegister = hook;
    }
    else if (style) {
        hook = shadowMode
            ? function (context) {
                style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot));
            }
            : function (context) {
                style.call(this, createInjector(context));
            };
    }
    if (hook) {
        if (options.functional) {
            // register for functional component in vue file
            const originalRender = options.render;
            options.render = function renderWithStyleInjection(h, context) {
                hook.call(context);
                return originalRender(h, context);
            };
        }
        else {
            // inject component registration as beforeCreate hook
            const existing = options.beforeCreate;
            options.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
    }
    return script;
}

/* script */
const __vue_script__$x = script$x;

/* template */
var __vue_render__$x = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    class: _vm.wrapperClass
  }, [_c('div', {
    staticClass: "settings-panel--divider"
  }), _vm._v(" "), _c('div', {
    staticClass: "settings-panel__section"
  }, [_vm._t("beforeMetric"), _vm._v(" "), _c('div', {
    staticClass: "section-label"
  }, [_vm._v("\n            " + _vm._s(_vm.title) + "\n            "), _vm.tooltip ? _c('pendo-icon', {
    directives: [{
      name: "pendo-tooltip",
      rawName: "v-pendo-tooltip",
      value: _vm.tooltip,
      expression: "tooltip"
    }],
    attrs: {
      "type": "info",
      "size": "14"
    }
  }) : _vm._e()], 1), _vm._v(" "), _c('pendo-metric', {
    attrs: {
      "value": _vm.value,
      "loading": _vm.loading
    }
  }), _vm._v(" "), _vm._t("afterMetric")], 2)]);
};
var __vue_staticRenderFns__$x = [];

/* style */
const __vue_inject_styles__$x = undefined;
/* scoped */
const __vue_scope_id__$x = undefined;
/* module identifier */
const __vue_module_identifier__$x = undefined;
/* functional template */
const __vue_is_functional_template__$x = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$x = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$x,
  staticRenderFns: __vue_staticRenderFns__$x
}, __vue_inject_styles__$x, __vue_script__$x, __vue_scope_id__$x, __vue_is_functional_template__$x, __vue_module_identifier__$x, false, undefined, undefined, undefined);

var eligibleVisitorCountAgg = {
  name: 'SegmentEligibleVisitorCount',
  responseFormat: 'number',
  build: ({
    pipeline
  }) => {
    return operators.pipeline(operators.sources.visitors(), operators.segment(pipeline), operators.reduce({
      total: operators.count(null)
    }));
  }
};

//
const COUNT_PLACEHOLDER = '--';
var script$w = {
  name: 'SegmentVisitorCount',
  components: {
    PendoAlert,
    SegmentMetricPanel: __vue_component__$x
  },
  props: {
    pipeline: {
      type: [Array, Object],
      default: undefined
    },
    showCsvUploadWarning: {
      type: Boolean,
      default: false
    },
    isValid: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      visitorCount: COUNT_PLACEHOLDER,
      loading: false,
      debouncedPipeline: undefined
    };
  },
  computed: {
    eligibleVisitorCount() {
      return addCommasToNumber(this.visitorCount);
    }
  },
  watch: {
    pipeline: {
      handler(newPipeline) {
        if (!this.isValid || newPipeline === null) {
          this.visitorCount = COUNT_PLACEHOLDER;
        } else {
          this.setDebouncedPipeline(newPipeline);
        }
      },
      deep: true
    }
  },
  created() {
    this.setupVisitorCountAgg();
    this.setDebouncedPipeline = debounce(this.setPipeline, 1500);
  },
  methods: {
    setupVisitorCountAgg() {
      const agg$ = connectAggregationToState(eligibleVisitorCountAgg, withComponent({
        pipeline: 'debouncedPipeline'
      }))({
        component: this
      });
      agg$.subscribe(response => {
        const {
          status,
          value
        } = response;
        switch (status) {
          case LOADING_STATES.LOADING:
            this.visitorCount = COUNT_PLACEHOLDER;
            this.loading = true;
            break;
          case LOADING_STATES.RESOLVED:
            {
              this.visitorCount = value;
              this.loading = false;
              break;
            }
          case LOADING_STATES.REJECTED:
            {
              this.visitorCount = COUNT_PLACEHOLDER;
              this.loading = false;

              // eslint-disable-next-line no-console
              console.warn(response.error);
              break;
            }
        }
      });
    },
    setPipeline(pipeline) {
      this.loading = false;
      this.debouncedPipeline = pipeline;
    }
  }
};

const isOldIE = typeof navigator !== 'undefined' &&
    /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());
function createInjector(context) {
    return (id, style) => addStyle(id, style);
}
let HEAD;
const styles = {};
function addStyle(id, css) {
    const group = isOldIE ? css.media || 'default' : id;
    const style = styles[group] || (styles[group] = { ids: new Set(), styles: [] });
    if (!style.ids.has(id)) {
        style.ids.add(id);
        let code = css.source;
        if (css.map) {
            // https://developer.chrome.com/devtools/docs/javascript-debugging
            // this makes source maps inside style tags work properly in Chrome
            code += '\n/*# sourceURL=' + css.map.sources[0] + ' */';
            // http://stackoverflow.com/a/26603875
            code +=
                '\n/*# sourceMappingURL=data:application/json;base64,' +
                    btoa(unescape(encodeURIComponent(JSON.stringify(css.map)))) +
                    ' */';
        }
        if (!style.element) {
            style.element = document.createElement('style');
            style.element.type = 'text/css';
            if (css.media)
                style.element.setAttribute('media', css.media);
            if (HEAD === undefined) {
                HEAD = document.head || document.getElementsByTagName('head')[0];
            }
            HEAD.appendChild(style.element);
        }
        if ('styleSheet' in style.element) {
            style.styles.push(code);
            style.element.styleSheet.cssText = style.styles
                .filter(Boolean)
                .join('\n');
        }
        else {
            const index = style.ids.size - 1;
            const textNode = document.createTextNode(code);
            const nodes = style.element.childNodes;
            if (nodes[index])
                style.element.removeChild(nodes[index]);
            if (nodes.length)
                style.element.insertBefore(textNode, nodes[index]);
            else
                style.element.appendChild(textNode);
        }
    }
}

/* script */
const __vue_script__$w = script$w;

/* template */
var __vue_render__$w = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('segment-metric-panel', {
    attrs: {
      "title": "Eligible Visitors",
      "wrapper-class": "editor-visitor-count",
      "value": _vm.eligibleVisitorCount,
      "loading": _vm.loading
    },
    scopedSlots: _vm._u([{
      key: "beforeMetric",
      fn: function () {
        return [_vm.showCsvUploadWarning ? _c('pendo-alert', {
          attrs: {
            "type": "warning"
          }
        }, [_vm._v("\n            Warning: Processing CSV analytics can take up to 15 minutes to correctly show visitor counts.\n        ")]) : _vm._e()];
      },
      proxy: true
    }, {
      key: "afterMetric",
      fn: function () {
        return [_c('div', {
          staticClass: "hidden-pipeline",
          attrs: {
            "data-cy": "segment-pipeline"
          }
        }, [_vm._v("\n            " + _vm._s(_vm.pipeline) + "\n        ")])];
      },
      proxy: true
    }])
  });
};
var __vue_staticRenderFns__$w = [];

/* style */
const __vue_inject_styles__$w = function (inject) {
  if (!inject) return;
  inject("data-v-d26c9fda_0", {
    source: ".editor-visitor-count[data-v-d26c9fda]{display:flex;flex-flow:column nowrap}.editor-visitor-count .pendo-alert[data-v-d26c9fda]{margin-bottom:20px}.editor-visitor-count .hidden-pipeline[data-v-d26c9fda]{display:none}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$w = "data-v-d26c9fda";
/* module identifier */
const __vue_module_identifier__$w = undefined;
/* functional template */
const __vue_is_functional_template__$w = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$w = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$w,
  staticRenderFns: __vue_staticRenderFns__$w
}, __vue_inject_styles__$w, __vue_script__$w, __vue_scope_id__$w, __vue_is_functional_template__$w, __vue_module_identifier__$w, false, createInjector, undefined, undefined);

//
var script$v = {
  name: 'SegmentStatsPanel',
  components: {
    SegmentVisitorCount: __vue_component__$w,
    PendoCollapseTransition,
    SegmentMetricPanel: __vue_component__$x
  },
  props: {
    pipeline: {
      type: [Array, Object],
      default: undefined
    },
    showCsvUploadWarning: {
      type: Boolean,
      default: false
    },
    isValid: {
      type: Boolean,
      default: true
    },
    nestedSegmentCount: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      segmentsUsedTooltip: {
        content: `The number of segments you can use is limited to ${SEGMENT_RULE_LIMIT}.`,
        arrow: true,
        multiline: true
      }
    };
  },
  computed: {
    showSegmentsUsed() {
      return this.nestedSegmentCount > 0;
    },
    usedSegmentsStat() {
      return `${this.nestedSegmentCount} of ${SEGMENT_RULE_LIMIT}`;
    }
  }
};

/* script */
const __vue_script__$v = script$v;

/* template */
var __vue_render__$v = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "segment-stats-panel"
  }, [_c('segment-visitor-count', _vm._b({}, 'segment-visitor-count', {
    pipeline: _vm.pipeline,
    showCsvUploadWarning: _vm.showCsvUploadWarning,
    isValid: _vm.isValid
  }, false)), _vm._v(" "), _c('pendo-collapse-transition', [_vm.showSegmentsUsed ? _c('segment-metric-panel', {
    attrs: {
      "title": "Segments Used",
      "wrapper-class": "segments-used-count",
      "tooltip": _vm.segmentsUsedTooltip,
      "value": _vm.usedSegmentsStat
    }
  }) : _vm._e()], 1)], 1);
};
var __vue_staticRenderFns__$v = [];

/* style */
const __vue_inject_styles__$v = undefined;
/* scoped */
const __vue_scope_id__$v = undefined;
/* module identifier */
const __vue_module_identifier__$v = undefined;
/* functional template */
const __vue_is_functional_template__$v = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$v = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$v,
  staticRenderFns: __vue_staticRenderFns__$v
}, __vue_inject_styles__$v, __vue_script__$v, __vue_scope_id__$v, __vue_is_functional_template__$v, __vue_module_identifier__$v, false, undefined, undefined, undefined);

//
var script$u = {
  name: 'SegmentFlagInput',
  components: {
    PendoInput,
    PendoForm,
    PendoFormItem
  },
  props: {
    value: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      rules: {
        segmentFlagName: [{
          validator: this.validateSegmentFlagInput,
          trigger: ['blur', 'change']
        }]
      }
    };
  },
  methods: {
    validateSegmentFlagInput(rule, val, cb) {
      const alphanumericSpaceRegex = /^[\p{L}0-9]*$/u;
      if (alphanumericSpaceRegex.test(val)) {
        this.$emit('invalid-segment-flag', false);
        cb();
      } else {
        this.$emit('invalid-segment-flag', true);
        cb(new Error('Only letters or numbers without spaces are allowed'));
      }
    },
    onInput(val) {
      this.$emit('input', val);
    }
  }
};

/* script */
const __vue_script__$u = script$u;

/* template */
var __vue_render__$u = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "segment-flag-input"
  }, [_c('pendo-form', {
    attrs: {
      "model": {
        value: _vm.value
      }
    }
  }, [_c('pendo-form-item', {
    attrs: {
      "id": "segmentFlagName",
      "name": "segmentFlagName",
      "prop": "value",
      "rules": _vm.rules.segmentFlagName
    }
  }, [_c('pendo-input', {
    attrs: {
      "id": "segmentFlagName",
      "name": "segmentFlagName",
      "disabled": _vm.disabled,
      "value": _vm.value
    },
    on: {
      "input": _vm.onInput
    }
  })], 1)], 1)], 1);
};
var __vue_staticRenderFns__$u = [];

/* style */
const __vue_inject_styles__$u = undefined;
/* scoped */
const __vue_scope_id__$u = undefined;
/* module identifier */
const __vue_module_identifier__$u = undefined;
/* functional template */
const __vue_is_functional_template__$u = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$u = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$u,
  staticRenderFns: __vue_staticRenderFns__$u
}, __vue_inject_styles__$u, __vue_script__$u, __vue_scope_id__$u, __vue_is_functional_template__$u, __vue_module_identifier__$u, false, undefined, undefined, undefined);

//
var script$t = {
  name: 'SegmentDebuggerPane',
  components: {
    PendoCard,
    PendoButton
  },
  directives: {
    PendoClipboard
  },
  props: {
    pipelineJson: {
      type: String,
      default: null
    }
  }
};

/* script */
const __vue_script__$t = script$t;

/* template */
var __vue_render__$t = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "segment-debugger-pane"
  }, [_c('div', {
    staticClass: "conditions-pipeline"
  }, [_c('pendo-card', [_c('pendo-button', {
    directives: [{
      name: "pendo-clipboard",
      rawName: "v-pendo-clipboard",
      value: _vm.pipelineJson,
      expression: "pipelineJson"
    }],
    attrs: {
      "theme": "app",
      "type": "tertiary",
      "size": "mini",
      "icon": "clipboard",
      "icon-size": "14"
    }
  }), _vm._v(" "), _c('pre', [_c('code', [_vm._v(_vm._s(_vm.pipelineJson))])])], 1)], 1)]);
};
var __vue_staticRenderFns__$t = [];

/* style */
const __vue_inject_styles__$t = function (inject) {
  if (!inject) return;
  inject("data-v-1f3a9631_0", {
    source: ".segment-debugger-pane .conditions-pipeline[data-v-1f3a9631]{position:relative}.segment-debugger-pane .conditions-pipeline pre[data-v-1f3a9631]{padding:9.5px;margin:0 0 10px;line-height:21px;white-space:pre-wrap;word-break:break-word}.segment-debugger-pane .conditions-pipeline .pendo-button[data-v-1f3a9631]{position:absolute;top:2px;right:8px}.segment-debugger-pane .conditions-pipeline .pendo-card[data-v-1f3a9631]{background-color:#f8f8f9}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$t = "data-v-1f3a9631";
/* module identifier */
const __vue_module_identifier__$t = undefined;
/* functional template */
const __vue_is_functional_template__$t = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$t = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$t,
  staticRenderFns: __vue_staticRenderFns__$t
}, __vue_inject_styles__$t, __vue_script__$t, __vue_scope_id__$t, __vue_is_functional_template__$t, __vue_module_identifier__$t, false, createInjector, undefined, undefined);

//
var script$s = {
  name: 'SegmentSuperPanel',
  components: {
    SegmentFlagInput: __vue_component__$u,
    SegmentDebuggerPane: __vue_component__$t,
    PendoIcon
  },
  directives: {
    PendoTooltip
  },
  props: {
    value: {
      type: String,
      default: null
    },
    pipelineJson: {
      type: String,
      default: null
    },
    segmentFlagInputDisabled: {
      type: Boolean,
      default: false
    }
  }
};

/* script */
const __vue_script__$s = script$s;

/* template */
var __vue_render__$s = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "segment-super-panel"
  }, [_c('div', {
    staticClass: "settings-panel--divider"
  }), _vm._v(" "), _c('div', {
    staticClass: "settings-panel__section"
  }, [_c('div', {
    staticClass: "section-label"
  }, [_vm._v("\n            Segment Flag "), _c('span', {
    staticClass: "super-only"
  }, [_vm._v("(Super Only)")]), _vm._v(" "), _c('pendo-icon', {
    directives: [{
      name: "pendo-tooltip",
      rawName: "v-pendo-tooltip",
      value: {
        arrow: true,
        multiline: true,
        content: 'Add a segment flag name to use this segment to hide or show features in code.'
      },
      expression: "{\n                    arrow: true,\n                    multiline: true,\n                    content: 'Add a segment flag name to use this segment to hide or show features in code.'\n                }"
    }],
    attrs: {
      "type": "info",
      "size": "14"
    }
  })], 1), _vm._v(" "), _c('segment-flag-input', _vm._g({
    attrs: {
      "disabled": _vm.segmentFlagInputDisabled,
      "value": _vm.value
    }
  }, _vm.$listeners))], 1), _vm._v(" "), _c('div', {
    staticClass: "settings-panel--divider"
  }), _vm._v(" "), _c('div', {
    staticClass: "settings-panel__section"
  }, [_vm._m(0), _vm._v(" "), _c('segment-debugger-pane', {
    attrs: {
      "pipeline-json": _vm.pipelineJson
    }
  })], 1)]);
};
var __vue_staticRenderFns__$s = [function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "section-label"
  }, [_vm._v("\n            Conditions "), _c('span', {
    staticClass: "super-only"
  }, [_vm._v("(Super Only)")])]);
}];

/* style */
const __vue_inject_styles__$s = function (inject) {
  if (!inject) return;
  inject("data-v-7ab679bc_0", {
    source: ".segment-super-panel .super-only[data-v-7ab679bc]{color:#9a9ca5;font-weight:400}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$s = "data-v-7ab679bc";
/* module identifier */
const __vue_module_identifier__$s = undefined;
/* functional template */
const __vue_is_functional_template__$s = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$s = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$s,
  staticRenderFns: __vue_staticRenderFns__$s
}, __vue_inject_styles__$s, __vue_script__$s, __vue_scope_id__$s, __vue_is_functional_template__$s, __vue_module_identifier__$s, false, createInjector, undefined, undefined);

const readOnlyKey = Symbol('readOnly');

var ReadOnlyMixin = {
  inject: {
    readOnlyInjected: {
      from: readOnlyKey
    }
  },
  computed: {
    readOnly() {
      return this.readOnlyInjected.value;
    }
  }
};

//
var script$r = {
  name: 'RelativeDateRange',
  components: {
    PendoInputNumber,
    PendoMultiselect
  },
  mixins: [ReadOnlyMixin],
  props: {
    value: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      granularityOptions: ['days', 'weeks', 'months']
    };
  },
  computed: {
    count() {
      return Number(this.value.count);
    }
  },
  created() {
    const count = Number.isNaN(this.count) ? 30 : this.count;
    this.onCountChange(count);
  },
  methods: {
    onCountChange(count) {
      if (count == null) {
        count = 0;
      }
      this.$emit('input', {
        field: 'count',
        value: count
      });
    },
    onGranularitySelect(option) {
      this.$emit('input', {
        field: 'granularity',
        value: option
      });
    }
  }
};

/* script */
const __vue_script__$r = script$r;

/* template */
var __vue_render__$r = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "relative-date-range"
  }, [_c('pendo-input-number', {
    staticClass: "count-input",
    attrs: {
      "value": _vm.value.count,
      "min": 0,
      "disabled": _vm.readOnly,
      "width": "65px"
    },
    on: {
      "change": _vm.onCountChange
    }
  }), _vm._v(" "), _c('pendo-multiselect', {
    staticClass: "granularity-select",
    attrs: {
      "value": _vm.value.granularity,
      "options": _vm.granularityOptions,
      "preselect-first": "",
      "min-trigger-width": 90,
      "max-trigger-width": 90,
      "min-menu-width": 90,
      "max-menu-width": 90,
      "disabled": _vm.readOnly
    },
    on: {
      "select": _vm.onGranularitySelect
    }
  })], 1);
};
var __vue_staticRenderFns__$r = [];

/* style */
const __vue_inject_styles__$r = function (inject) {
  if (!inject) return;
  inject("data-v-dc812c1a_0", {
    source: ".relative-date-range{display:grid;grid-gap:16px;grid-template-columns:auto 90px}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$r = undefined;
/* module identifier */
const __vue_module_identifier__$r = undefined;
/* functional template */
const __vue_is_functional_template__$r = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$r = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$r,
  staticRenderFns: __vue_staticRenderFns__$r
}, __vue_inject_styles__$r, __vue_script__$r, __vue_scope_id__$r, __vue_is_functional_template__$r, __vue_module_identifier__$r, false, createInjector, undefined, undefined);

//
var script$q = {
  name: 'SingleDate',
  components: {
    PendoDatePicker
  },
  mixins: [ReadOnlyMixin],
  inject: ['appFirstVisit'],
  props: {
    value: {
      type: null,
      default: null
    },
    field: {
      type: String,
      default: 'value'
    }
  },
  computed: {
    date() {
      return moment(this.value).toDate();
    },
    valid() {
      return moment(this.value).isValid() && !moment(this.value).isBefore(this.appFirstVisit);
    },
    yesterday() {
      return moment(Date.now()).subtract(1, 'day').toDate();
    }
  },
  created() {
    const value = this.valid ? this.date : this.yesterday;
    this.updateDate({
      value
    });
  },
  methods: {
    updateDate(date) {
      const value = formatTimeValue(date.value);
      this.$emit('input', {
        field: this.field,
        value
      });
    }
  }
};

/* script */
const __vue_script__$q = script$q;

/* template */
var __vue_render__$q = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('pendo-date-picker', {
    staticClass: "single-date",
    attrs: {
      "full-width": "",
      "is-required": "",
      "value": _vm.date,
      "min-date": _vm.appFirstVisit,
      "max-date": _vm.yesterday,
      "disabled": _vm.readOnly
    },
    on: {
      "select-date": _vm.updateDate
    }
  });
};
var __vue_staticRenderFns__$q = [];

/* style */
const __vue_inject_styles__$q = undefined;
/* scoped */
const __vue_scope_id__$q = undefined;
/* module identifier */
const __vue_module_identifier__$q = undefined;
/* functional template */
const __vue_is_functional_template__$q = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$q = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$q,
  staticRenderFns: __vue_staticRenderFns__$q
}, __vue_inject_styles__$q, __vue_script__$q, __vue_scope_id__$q, __vue_is_functional_template__$q, __vue_module_identifier__$q, false, undefined, undefined, undefined);

//
var script$p = {
  name: 'ApplicationRule',
  components: {
    PendoMultiselect,
    SingleDate: __vue_component__$q,
    RelativeDateRange: __vue_component__$r
  },
  mixins: [ReadOnlyMixin],
  props: {
    rule: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      appUsageTimeRangeOptions: APP_USAGE_TIME_RANGE_OPTIONS
    };
  },
  computed: {
    operatorOptions() {
      return [...SEGMENT_OPERATORS[this.rule.schema.schema]];
    }
  },
  methods: {
    appUsageTimeLabelObject(timeString) {
      return this.appUsageTimeRangeOptions.find(opt => opt.value === timeString);
    },
    handleAppUsageOperatorSelection($event) {
      this.$emit('input', {
        field: 'operator',
        value: $event
      });
    },
    handleAppUsageTimeSelection($event) {
      const {
        value
      } = $event;
      if (value === 'withinlast') {
        this.$emit('input', {
          field: 'value',
          value: {
            count: 30,
            granularity: 'days'
          }
        });
      } else {
        this.$emit('input', {
          field: 'value',
          value: 1
        });
      }
      this.$emit('input', {
        field: 'time',
        value
      });
    }
  }
};

/* script */
const __vue_script__$p = script$p;

/* template */
var __vue_render__$p = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "application-rule"
  }, [_c('pendo-multiselect', {
    staticClass: "rule-editor--operator",
    attrs: {
      "value": _vm.rule.operator,
      "allow-empty": false,
      "full-width": "",
      "value-key": "value",
      "options": _vm.operatorOptions,
      "disabled": !_vm.operatorOptions || _vm.readOnly,
      "placeholder": "Select Usage"
    },
    on: {
      "select": _vm.handleAppUsageOperatorSelection
    }
  }), _vm._v(" "), _c('pendo-multiselect', {
    staticClass: "application-rule--time-operator",
    attrs: {
      "value": _vm.appUsageTimeLabelObject(_vm.rule.time),
      "value-key": "value",
      "label-key": "label",
      "min-trigger-width": "100%",
      "allow-empty": false,
      "preselect-first": "",
      "options": _vm.appUsageTimeRangeOptions,
      "disabled": _vm.readOnly,
      "placeholder": "Select Time"
    },
    on: {
      "select": _vm.handleAppUsageTimeSelection
    }
  }), _vm._v(" "), _vm.rule.time === 'since' ? _c('single-date', {
    staticClass: "rule-editor--item",
    attrs: {
      "value": _vm.rule.value
    },
    on: {
      "input": function ($event) {
        return _vm.$emit('input', $event);
      }
    }
  }) : _vm._e(), _vm._v(" "), _vm.rule.time === 'withinlast' ? _c('relative-date-range', {
    staticClass: "rule-editor--item complex-rule",
    attrs: {
      "value": {
        count: _vm.rule.value.count,
        granularity: _vm.rule.value.granularity
      }
    },
    on: {
      "input": function ($event) {
        return _vm.$emit('input', $event);
      }
    }
  }) : _vm._e()], 1);
};
var __vue_staticRenderFns__$p = [];

/* style */
const __vue_inject_styles__$p = function (inject) {
  if (!inject) return;
  inject("data-v-0e9056ba_0", {
    source: ".application-rule[data-v-0e9056ba]{display:contents}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$p = "data-v-0e9056ba";
/* module identifier */
const __vue_module_identifier__$p = undefined;
/* functional template */
const __vue_is_functional_template__$p = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$p = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$p,
  staticRenderFns: __vue_staticRenderFns__$p
}, __vue_inject_styles__$p, __vue_script__$p, __vue_scope_id__$p, __vue_is_functional_template__$p, __vue_module_identifier__$p, false, createInjector, undefined, undefined);

//
//
//
//
//
//

var script$o = {
  name: 'RuleWarning'
};

/* script */
const __vue_script__$o = script$o;

/* template */
var __vue_render__$o = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('span', {
    staticClass: "rule-warning"
  }, [_vm._t("default")], 2);
};
var __vue_staticRenderFns__$o = [];

/* style */
const __vue_inject_styles__$o = function (inject) {
  if (!inject) return;
  inject("data-v-d583a3f2_0", {
    source: ".rule-warning[data-v-d583a3f2]{color:#cc3340;display:inline-block;font-size:.75rem;line-height:.875rem;margin-top:.5em}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$o = "data-v-d583a3f2";
/* module identifier */
const __vue_module_identifier__$o = undefined;
/* functional template */
const __vue_is_functional_template__$o = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$o = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$o,
  staticRenderFns: __vue_staticRenderFns__$o
}, __vue_inject_styles__$o, __vue_script__$o, __vue_scope_id__$o, __vue_is_functional_template__$o, __vue_module_identifier__$o, false, createInjector, undefined, undefined);

//
var script$n = {
  name: 'AbsoluteDateRange',
  components: {
    PendoDatePicker
  },
  mixins: [ReadOnlyMixin],
  props: {
    value: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    first() {
      return moment(this.value.first).toDate();
    },
    last() {
      return moment(this.value.last).toDate();
    },
    isFirstValid() {
      return moment(this.value.first).isValid();
    },
    isLastValid() {
      return moment(this.value.last).isValid();
    }
  },
  created() {
    let first = moment(this.value.first);
    let last = moment(this.value.last);
    if (!this.isFirstValid) {
      first = moment(Date.now()).subtract(1, 'days');
    }
    if (!this.isLastValid || last.isBefore(first)) {
      last = moment(first).add(1, 'days');
    }
    this.updateFirst({
      value: first
    });
    this.updateLast({
      value: last
    });
  },
  methods: {
    updateFirst(date) {
      const value = formatTimeValue(date.value);
      this.$emit('input', {
        field: 'first',
        value
      });
    },
    updateLast(date) {
      const value = formatTimeValue(date.value);
      this.$emit('input', {
        field: 'last',
        value
      });
    }
  }
};

/* script */
const __vue_script__$n = script$n;

/* template */
var __vue_render__$n = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "absolute-date-range"
  }, [_c('pendo-date-picker', {
    staticClass: "absolute-date-range__start",
    attrs: {
      "is-required": "",
      "value": _vm.first,
      "max-date": _vm.last,
      "disabled": _vm.readOnly,
      "full-width": ""
    },
    on: {
      "select-date": _vm.updateFirst
    }
  }), _vm._v(" "), _c('div', {
    staticClass: "absolute-date-range__and-label"
  }, [_vm._v("\n        and\n    ")]), _vm._v(" "), _c('pendo-date-picker', {
    staticClass: "absolute-date-range__end",
    attrs: {
      "is-required": "",
      "value": _vm.last,
      "min-date": _vm.first,
      "disabled": _vm.readOnly,
      "full-width": ""
    },
    on: {
      "select-date": _vm.updateLast
    }
  })], 1);
};
var __vue_staticRenderFns__$n = [];

/* style */
const __vue_inject_styles__$n = function (inject) {
  if (!inject) return;
  inject("data-v-91e67fb8_0", {
    source: ".absolute-date-range{align-items:center;grid-column:span 2;display:grid;grid-template-columns:1fr 36px 1fr}.absolute-date-range__and-label{color:#6a6c75;margin:auto}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$n = undefined;
/* module identifier */
const __vue_module_identifier__$n = undefined;
/* functional template */
const __vue_is_functional_template__$n = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$n = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$n,
  staticRenderFns: __vue_staticRenderFns__$n
}, __vue_inject_styles__$n, __vue_script__$n, __vue_scope_id__$n, __vue_is_functional_template__$n, __vue_module_identifier__$n, false, createInjector, undefined, undefined);

//
var script$m = {
  name: 'NumberInput',
  components: {
    PendoInputNumber
  },
  mixins: [ReadOnlyMixin],
  props: {
    // eslint-disable-next-line vue/require-default-prop
    value: {
      type: null
    },
    field: {
      type: String,
      default: 'value'
    },
    labels: {
      type: Object,
      default: () => ({})
    },
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: Infinity
    }
  },
  created() {
    const value = isNumber(this.value) ? this.value : 1;
    this.onNumberChange(value);
  },
  methods: {
    onNumberChange(value) {
      if (value == null) {
        value = 0;
      }
      this.$emit('input', {
        field: this.field,
        value
      });
    }
  }
};

/* script */
const __vue_script__$m = script$m;

/* template */
var __vue_render__$m = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('pendo-input-number', {
    staticClass: "number-input",
    class: _vm.field + "-number-input",
    attrs: {
      "value": _vm.value,
      "labels": _vm.labels,
      "disabled": _vm.readOnly,
      "min": _vm.min,
      "max": _vm.max
    },
    on: {
      "change": _vm.onNumberChange
    }
  });
};
var __vue_staticRenderFns__$m = [];

/* style */
const __vue_inject_styles__$m = undefined;
/* scoped */
const __vue_scope_id__$m = undefined;
/* module identifier */
const __vue_module_identifier__$m = undefined;
/* functional template */
const __vue_is_functional_template__$m = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$m = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$m,
  staticRenderFns: __vue_staticRenderFns__$m
}, __vue_inject_styles__$m, __vue_script__$m, __vue_scope_id__$m, __vue_is_functional_template__$m, __vue_module_identifier__$m, false, undefined, undefined, undefined);

//
var script$l = {
  name: 'FeatureRule',
  components: {
    PendoMultiselect,
    AbsoluteDateRange: __vue_component__$n,
    RelativeDateRange: __vue_component__$r,
    RuleWarning: __vue_component__$o,
    SingleDate: __vue_component__$q,
    NumberInput: __vue_component__$m
  },
  mixins: [ReadOnlyMixin],
  props: {
    computedData: {
      type: Object,
      required: true
    },
    config: {
      type: Object,
      required: true
    },
    rule: {
      type: Object,
      required: true
    }
  },
  computed: {
    featureListOptions() {
      return getEntitiesByAppSelection(this.rule.app, this.computedData.features, this.config);
    },
    operatorOptions() {
      return [...SEGMENT_OPERATORS[this.rule.schema.schema]];
    },
    featureTimeRangeOptions() {
      switch (this.rule.operator.value) {
        case 'lastclicked':
          return FEATURE_TIME_RANGE_LAST_CLICKED_OPTIONS;
        case 'notclicked':
          return FEATURE_TIME_RANGE_NOT_CLICKED_OPTIONS;
        case 'clicked':
        default:
          return FEATURE_TIME_RANGE_CLICKED_OPTIONS;
      }
    },
    showNumberInputComponent() {
      return this.rule.operator.value === 'clicked' && (this.rule.time === 'atmost' || this.rule.time === 'atleast');
    },
    showTimeComponent() {
      if (this.showNumberInputComponent) {
        return false;
      }
      const {
        rule
      } = this;
      const operatorValue = get(rule, 'operator.value');
      return !!(rule.feature && operatorValue && rule.time && rule.time !== 'ever');
    },
    timeComponent() {
      if (['withinlast', '!withinlast'].includes(this.rule.time)) {
        return getRelativeDateRangeConfig(this.rule, 'value');
      }
      if (['between'].includes(this.rule.time)) {
        return getAbsoluteDateRangeConfig(this.rule);
      }
      if (['before', 'since'].includes(this.rule.time)) {
        return getSingleDateConfig(this.rule, 'value');
      }
      return;
    },
    showTimeSelector() {
      const {
        rule
      } = this;
      return !!rule.feature;
    }
  },
  methods: {
    featureTimeLabelObject(timeString) {
      return this.featureTimeRangeOptions.find(opt => opt.value === timeString);
    },
    handleFeatureOperatorSelection($event) {
      const updates = [{
        field: 'operator',
        value: $event
      }];

      // handle switching to operator where `at least / at most` not supported
      if (this.rule.time === 'atleast' || this.rule.time === 'atmost') {
        if ($event.value === 'notclicked') {
          updates.push({
            field: 'time',
            value: 'ever'
          });
        }
        if ($event.value === 'lastclicked') {
          updates.push({
            field: 'time',
            value: 'before'
          });
        }
      }
      this.$emit('input', updates);
    },
    handleFeatureTimeSelection($event) {
      const {
        value
      } = $event;
      if (value === 'withinlast' || value === '!withinlast') {
        this.$emit('input', {
          field: 'value',
          value: {
            count: 30,
            granularity: 'days'
          }
        });
      } else if (value !== 'before' && value !== 'since') {
        this.$emit('input', {
          field: 'value',
          value: 1
        });
      }
      this.$emit('input', {
        field: 'time',
        value
      });
    },
    onFeatureSelect(feature) {
      this.$emit('input', [{
        field: 'feature',
        value: feature
      }, {
        field: 'featureDeleted',
        value: false
      }]);
    }
  }
};

/* script */
const __vue_script__$l = script$l;

/* template */
var __vue_render__$l = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "feature-rule"
  }, [_c('div', {
    staticClass: "rule-editor--item",
    class: {
      'feature-undefined': _vm.rule.featureDeleted
    }
  }, [_c('pendo-multiselect', {
    staticClass: "feature-rule--feature-select",
    attrs: {
      "value": _vm.rule.feature,
      "allow-empty": false,
      "options": _vm.featureListOptions,
      "searchable": true,
      "disabled": _vm.readOnly,
      "label-key": "displayName",
      "value-key": "id",
      "full-width": "",
      "placeholder": "Select Feature"
    },
    on: {
      "select": _vm.onFeatureSelect
    }
  }), _vm._v(" "), _vm.rule.featureDeleted ? _c('rule-warning', [_vm._v("\n            The original feature for this rule has been deleted\n        ")]) : _vm._e()], 1), _vm._v(" "), _c('pendo-multiselect', {
    staticClass: "rule-editor--operator",
    attrs: {
      "value": _vm.rule.operator,
      "allow-empty": false,
      "preselect-first": "",
      "full-width": "",
      "value-key": "value",
      "options": _vm.operatorOptions,
      "disabled": !_vm.operatorOptions || _vm.readOnly
    },
    on: {
      "select": _vm.handleFeatureOperatorSelection
    }
  }), _vm._v(" "), _c('pendo-multiselect', {
    staticClass: "feature-rule--feature-time-operator",
    attrs: {
      "value": _vm.featureTimeLabelObject(_vm.rule.time),
      "value-key": "value",
      "label-key": "label",
      "min-trigger-width": "100%",
      "allow-empty": false,
      "preselect-first": true,
      "options": _vm.featureTimeRangeOptions,
      "disabled": _vm.readOnly
    },
    on: {
      "select": _vm.handleFeatureTimeSelection
    }
  }), _vm._v(" "), _vm.showNumberInputComponent ? _c('number-input', {
    staticClass: "rule-editor--item complex-rule number-input--max-height",
    attrs: {
      "labels": {
        suffix: 'times'
      },
      "value": _vm.rule.value,
      "min": 1
    },
    on: {
      "input": function ($event) {
        return _vm.$emit('input', $event);
      }
    }
  }) : _vm._e(), _vm._v(" "), _vm.showTimeComponent ? _c(_vm.timeComponent.component, _vm._b({
    tag: "component",
    on: {
      "input": function ($event) {
        return _vm.$emit('input', $event);
      }
    }
  }, 'component', _vm.timeComponent.props, false)) : _vm._e()], 1);
};
var __vue_staticRenderFns__$l = [];

/* style */
const __vue_inject_styles__$l = function (inject) {
  if (!inject) return;
  inject("data-v-2f64b134_0", {
    source: ".feature-rule[data-v-2f64b134]{display:contents}.feature-rule .feature-undefined[data-v-2f64b134]  .pendo-multiselect__trigger{border:1px solid #cc3340}.number-input--max-height[data-v-2f64b134]{height:36px}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$l = "data-v-2f64b134";
/* module identifier */
const __vue_module_identifier__$l = undefined;
/* functional template */
const __vue_is_functional_template__$l = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$l = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$l,
  staticRenderFns: __vue_staticRenderFns__$l
}, __vue_inject_styles__$l, __vue_script__$l, __vue_scope_id__$l, __vue_is_functional_template__$l, __vue_module_identifier__$l, false, createInjector, undefined, undefined);

//
var script$k = {
  name: 'OperatorPicker',
  components: {
    PendoMultiselect
  },
  mixins: [ReadOnlyMixin],
  props: {
    operators: {
      type: Object,
      default() {
        return SEGMENT_OPERATORS;
      }
    },
    rule: {
      type: Object,
      required: true
    }
  },
  computed: {
    schema() {
      return this.rule.schema.schema;
    }
  }
};

/* script */
const __vue_script__$k = script$k;

/* template */
var __vue_render__$k = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('pendo-multiselect', {
    staticClass: "rule-editor--operator",
    attrs: {
      "value": _vm.rule.operator,
      "allow-empty": false,
      "preselect-first": true,
      "options": _vm.operators[_vm.schema] || [],
      "disabled": !_vm.operators[_vm.schema] || _vm.readOnly,
      "min-trigger-width": "100%",
      "value-key": "value"
    },
    on: {
      "select": function ($event) {
        return _vm.$emit('input', {
          field: 'operator',
          value: $event
        });
      }
    }
  });
};
var __vue_staticRenderFns__$k = [];

/* style */
const __vue_inject_styles__$k = undefined;
/* scoped */
const __vue_scope_id__$k = undefined;
/* module identifier */
const __vue_module_identifier__$k = undefined;
/* functional template */
const __vue_is_functional_template__$k = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$k = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$k,
  staticRenderFns: __vue_staticRenderFns__$k
}, __vue_inject_styles__$k, __vue_script__$k, __vue_scope_id__$k, __vue_is_functional_template__$k, __vue_module_identifier__$k, false, undefined, undefined, undefined);

//

/**
 * Used for purely cosmetic purposes for certain rule types.
 */
var script$j = {
  name: 'DisabledSingleOptionSelect',
  components: {
    PendoMultiselect
  },
  props: {
    option: {
      type: String,
      required: true
    }
  }
};

/* script */
const __vue_script__$j = script$j;

/* template */
var __vue_render__$j = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('pendo-multiselect', {
    attrs: {
      "disabled": true,
      "preselect-first": true,
      "options": [_vm.option],
      "value": _vm.option,
      "min-trigger-width": "100%"
    }
  });
};
var __vue_staticRenderFns__$j = [];

/* style */
const __vue_inject_styles__$j = undefined;
/* scoped */
const __vue_scope_id__$j = undefined;
/* module identifier */
const __vue_module_identifier__$j = undefined;
/* functional template */
const __vue_is_functional_template__$j = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$j = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$j,
  staticRenderFns: __vue_staticRenderFns__$j
}, __vue_inject_styles__$j, __vue_script__$j, __vue_scope_id__$j, __vue_is_functional_template__$j, __vue_module_identifier__$j, false, undefined, undefined, undefined);

//
var script$i = {
  name: 'GuideRule',
  components: {
    DisabledSingleOptionSelect: __vue_component__$j,
    OperatorPicker: __vue_component__$k,
    PendoGuideStatusOption,
    PendoMultiselect,
    RuleWarning: __vue_component__$o,
    RelativeDateRange: __vue_component__$r,
    AbsoluteDateRange: __vue_component__$n,
    SingleDate: __vue_component__$q,
    NumberInput: __vue_component__$m
  },
  mixins: [ReadOnlyMixin],
  props: {
    computedData: {
      type: Object,
      required: true
    },
    config: {
      type: Object,
      required: true
    },
    rule: {
      type: Object,
      required: true
    }
  },
  computed: {
    guidePickerOptions() {
      return getEntitiesByAppSelection(this.rule.app, this.computedData.guides, this.config);
    },
    guideTimeRangeOptions() {
      const operatorValue = get(this.rule, 'operator.value');
      switch (operatorValue) {
        case 'lastviewed':
          return GUIDE_TIME_RANGE_LAST_SEEN_OPTIONS;
        case 'notviewed':
          return GUIDE_TIME_RANGE_NOT_SEEN_OPTIONS;
        case 'viewed':
        default:
          return GUIDE_TIME_RANGE_SEEN_OPTIONS;
      }
    },
    guideTimeLabelObject() {
      return this.guideTimeRangeOptions.find(opt => opt.value === this.rule.time);
    },
    showNumberInputComponent() {
      return this.rule.operator.value === 'viewed' && (this.rule.time === 'atmost' || this.rule.time === 'atleast');
    },
    showTimeComponent() {
      if (this.showNumberInputComponent) {
        return false;
      }
      const {
        rule
      } = this;
      const operatorValue = get(rule, 'operator.value');
      return !!(rule.guide && operatorValue && rule.time && rule.time !== 'ever');
    },
    timeComponent() {
      if (['withinlast', '!withinlast', 'withinnext', '!withinnext'].includes(this.rule.time)) {
        return getRelativeDateRangeConfig(this.rule);
      }
      if (['between'].includes(this.rule.time)) {
        return getAbsoluteDateRangeConfig(this.rule);
      }
      if (['>=', '<='].includes(this.rule.time)) {
        return getSingleDateConfig(this.rule, 'first');
      }
      return;
    },
    showTimeSelector() {
      const {
        rule
      } = this;
      return !!rule.guide;
    }
  },
  methods: {
    handleGuideOperatorSelection({
      value
    }) {
      const updates = [{
        field: 'operator',
        value
      }];

      // handle switching to where `at least / at most` not supported
      if (this.rule.time === 'atleast' || this.rule.time === 'atmost') {
        if (value.value === 'lastviewed') {
          updates.push({
            field: 'time',
            value: '<='
          });
        } else {
          updates.push({
            field: 'time',
            value: 'ever'
          });
        }
      }

      // handle switching to `seen / not seen` where `between` not supported
      if (value.value !== 'lastviewed' && this.rule.time === 'between') {
        updates.push({
          field: 'time',
          value: 'ever'
        });
      }
      this.$emit('input', updates);
    },
    handleGuideTimeSelection($event) {
      const {
        value
      } = $event;
      // Clean up fields
      if (value !== 'between') {
        this.$emit('input', {
          field: 'last',
          value: undefined
        });
        if (value !== '>=' && value !== '<=') {
          this.$emit('input', {
            field: 'first',
            value: undefined
          });
        }
      }
      if (value !== 'withinlast' && value !== '!withinlast') {
        const fields = [{
          field: 'count',
          value: undefined
        }, {
          field: 'granularity',
          value: undefined
        }];
        this.$emit('input', fields);
      }
      this.$emit('input', {
        field: 'time',
        value
      });
    },
    onGuideSelect(guide) {
      this.$emit('input', [{
        field: 'guide',
        value: guide
      }, {
        field: 'guideDeleted',
        value: false
      }]);
    }
  }
};

/* script */
const __vue_script__$i = script$i;

/* template */
var __vue_render__$i = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "guide-rule"
  }, [_c('div', {
    class: {
      'guide-undefined': _vm.rule.guideDeleted
    }
  }, [_c('pendo-multiselect', {
    staticClass: "guide-rule--guide-select",
    attrs: {
      "value": _vm.rule.guide,
      "allow-empty": false,
      "options": _vm.guidePickerOptions,
      "searchable": true,
      "disabled": _vm.readOnly,
      "label-key": "name",
      "value-key": "id",
      "full-width": "",
      "placeholder": "Select Guide"
    },
    on: {
      "select": _vm.onGuideSelect
    },
    scopedSlots: _vm._u([{
      key: "option",
      fn: function (ref) {
        var option = ref.option;
        return [_c('pendo-guide-status-option', {
          attrs: {
            "tooltip": "",
            "option": option
          }
        })];
      }
    }, {
      key: "selectedLabel",
      fn: function (ref) {
        var option = ref.option;
        return [_c('pendo-guide-status-option', {
          attrs: {
            "tooltip": "",
            "option": option
          }
        })];
      }
    }])
  }), _vm._v(" "), _vm.rule.guideDeleted ? _c('rule-warning', [_vm._v("\n            The original guide for this rule has been deleted\n        ")]) : _vm._e()], 1), _vm._v(" "), _c('operator-picker', {
    attrs: {
      "rule": _vm.rule
    },
    on: {
      "input": _vm.handleGuideOperatorSelection
    }
  }), _vm._v(" "), _vm.showTimeSelector ? _c('pendo-multiselect', {
    staticClass: "guide-rule--time-operator",
    attrs: {
      "value": _vm.guideTimeLabelObject,
      "value-key": "value",
      "label-key": "label",
      "min-trigger-width": "100%",
      "allow-empty": false,
      "preselect-first": true,
      "options": _vm.guideTimeRangeOptions,
      "disabled": _vm.readOnly
    },
    on: {
      "select": _vm.handleGuideTimeSelection
    }
  }) : _vm._e(), _vm._v(" "), _vm.showNumberInputComponent ? _c('number-input', {
    staticClass: "rule-editor--item complex-rule number-input--max-height",
    attrs: {
      "labels": {
        suffix: 'times'
      },
      "value": _vm.rule.value,
      "min": 1
    },
    on: {
      "input": function ($event) {
        return _vm.$emit('input', $event);
      }
    }
  }) : _vm._e(), _vm._v(" "), _vm.showTimeComponent ? _c(_vm.timeComponent.component, _vm._b({
    tag: "component",
    on: {
      "input": function ($event) {
        return _vm.$emit('input', $event);
      }
    }
  }, 'component', _vm.timeComponent.props, false)) : _vm._e()], 1);
};
var __vue_staticRenderFns__$i = [];

/* style */
const __vue_inject_styles__$i = function (inject) {
  if (!inject) return;
  inject("data-v-1b25dba2_0", {
    source: ".guide-rule[data-v-1b25dba2]{display:contents}.guide-rule .guide-undefined[data-v-1b25dba2]  .pendo-multiselect__trigger{border:1px solid #cc3340}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$i = "data-v-1b25dba2";
/* module identifier */
const __vue_module_identifier__$i = undefined;
/* functional template */
const __vue_is_functional_template__$i = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$i = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$i,
  staticRenderFns: __vue_staticRenderFns__$i
}, __vue_inject_styles__$i, __vue_script__$i, __vue_scope_id__$i, __vue_is_functional_template__$i, __vue_module_identifier__$i, false, createInjector, undefined, undefined);

const determineElementType = () => {
  return ['if(isButton, "Button", elementType)', 'if(isDismiss, "Close Button", elementType)', 'if(isLink, "Link", elementType)', 'if(isSwipeLeft, "Swiped Left", elementType)', 'if(isSwipeRight, "Swiped Right", elementType)'].reduce((acc, item) => {
    return acc.replace('elementType', item);
  });
};
var guideElementsActivityAgg = {
  name: 'GuideElementsActivity',
  responseFormat: 'rows',
  build: ({
    guideId,
    currentIds,
    filters
  }) => {
    const {
      blacklist,
      includeAnonymous,
      languageCode,
      timeSeries
    } = filters;
    return operators.pipeline(operators.sources.guideEvents({
      guideId,
      timeSeries,
      blacklist
    }), operators.filter('eventSubType(type) == "guideActivity" && uiElementType != ""'), operators.languageFilter(languageCode), operators.identified(includeAnonymous), operators.evaluate({
      elementType: 'eventSubType(type)',
      isButton: 'if(uiElementType == "BUTTON", true, false)',
      isDismiss: 'if(contains(uiElementId, "pendo-close-guide-"), true, false)',
      isLink: 'if(uiElementType == "A", true, false)',
      isDeleted: `!contains(${JSON.stringify(currentIds)}, uiElementId)`,
      isSwipeLeft: 'if(uiElementType == "LEFT-SWIPER", true, false)',
      isSwipeRight: 'if(uiElementType == "RIGHT-SWIPER", true, false)'
    }), operators.evaluate({
      elementType: determineElementType()
    }), operators.fork(operators.pipeline(operators.group(['guideId', 'guideStepId', 'uiElementId', 'elementType', 'isDeleted'], operators.groupField('numClicks', operators.count(null)))), operators.pipeline(operators.group(['uiElementId', 'uiElementText'], operators.groupField('mostRecent', {
      max: 'browserTime'
    })), operators.sort(['mostRecent'])), operators.pipeline(operators.group(['uiElementId', 'uiElementActions'], operators.groupField('mostRecent', {
      max: 'browserTime'
    })), operators.sort(['mostRecent']))), operators.join('uiElementId'), operators.select({
      guideId: 'guideId',
      guideStepId: 'guideStepId',
      uiElementId: 'uiElementId',
      uiElementType: 'elementType',
      uiElementActions: 'uiElementActions',
      uiElementText: 'uiElementText',
      numClicks: 'numClicks',
      isDeleted: 'isDeleted'
    }));
  }
};

async function getContent(guide) {
  const guideWithContent = cloneDeep(guide);
  const stepsWithContent = await getGuideStepsContent(guideWithContent.steps);
  guideWithContent.steps = stepsWithContent;
  return guideWithContent;
}
async function getGuideStepsContent(steps) {
  const promises = steps.map(async step => {
    const stepContent = {};
    if (step.buildingBlocksUrl) {
      const response = await http.get(step.buildingBlocksUrl, {
        withCredentials: false,
        transformResponse: null
      });
      stepContent.buildingBlocks = response.data;
    }
    if (step.domUrl) {
      const response = await http.get(step.domUrl, {
        withCredentials: false,
        transformResponse: null
      });
      stepContent.dom = response.data;
    }
    return Object.assign(step, stepContent);
  });
  return Promise.all(promises);
}

// this function is duplicated from pendo-appengine -> guide-elements-table/utils.js
function processGuideElementsAggDropdown(rows, currentElementData) {
  const mergedData = merge(keyBy(rows, 'uiElementId'), keyBy(currentElementData, 'uiElementId'));
  return Object.values(mergedData).map(row => {
    const elementText = formatUiElementTextForDisplay(row.uiElementText, row.uiElementId);
    return {
      guideElementId: row.uiElementId,
      guideStepId: row.guideStepId,
      name: row.isDeleted ? `Deleted Element (${elementText})` : elementText,
      icon: {
        type: getUiElementTypeIcon(row.uiElementType)
      }
    };
  });
}

// this function is duplicated from pendo-appengine -> guide-elements-table/utils.js
function formatUiElementTextForDisplay(uiElementText, uiElementId) {
  if (!uiElementText) {
    return uiElementId || 'Unknown';
  }
  if (uiElementText === '×') {
    return 'Close Button';
  }
  return uiElementText;
}

// this function is duplicated from pendo-appengine -> guide-elements-table/utils.js
function getUiElementTypeIcon(elementType) {
  return {
    'Button': 'rectangle',
    'Close Button': 'x',
    'Dismiss Button': 'x',
    'Link': 'link'
  }[elementType];
}

// this function is duplicated from pendo-appengine -> guide-elements-table/utils.js
function getCurrentElementData(dom) {
  let elements = [];
  const isCorrectType = dom.type && (dom.type === 'button' || dom.type === 'a');
  const uiElementId = get(dom, 'props.id', false);
  if (isCorrectType && uiElementId) {
    const uiElementText = getUiElementText(dom);
    const uiElementType = getUiElementType(uiElementId, dom.type);
    let uiElementActions = get(dom, 'actions');
    if (dom.type === 'a' && !uiElementActions) {
      uiElementActions = [{
        action: 'openLink'
      }];
    }
    elements.push({
      uiElementId,
      uiElementText,
      uiElementType,
      uiElementActions
    });
  }
  if (dom.children) {
    dom.children.forEach(child => {
      elements = elements.concat(getCurrentElementData(child));
    });
  }
  return elements;
}

// this function is duplicated from pendo-appengine -> guide-elements-table/utils.js
function getUiElementText(dom) {
  const uiElementText = get(dom, 'content', false);
  if (uiElementText) {
    return uiElementText;
  }
  let concatenatedUiText = '';
  if (dom.children) {
    dom.children.forEach(child => {
      const uiText = getUiElementText(child);
      if (uiText) {
        concatenatedUiText = concatenatedUiText.concat(uiText);
      }
    });
  }
  return concatenatedUiText;
}

// this function is duplicated from pendo-appengine -> guide-elements-table/utils.js
function getUiElementType(uiElementId, type) {
  if (uiElementId.includes('pendo-close-guide-')) {
    return 'Close Button';
  }
  return {
    button: 'Button',
    a: 'Link'
  }[type];
}

//
var script$h = {
  name: 'GuideElementRule',
  components: {
    PendoGuideStatusOption,
    PendoIconOption,
    PendoMultiselect,
    RuleWarning: __vue_component__$o,
    RelativeDateRange: __vue_component__$r,
    SingleDate: __vue_component__$q,
    NumberInput: __vue_component__$m
  },
  mixins: [ReadOnlyMixin],
  props: {
    computedData: {
      type: Object,
      required: true
    },
    config: {
      type: Object,
      required: true
    },
    // eslint-disable-next-line vue/no-unused-properties
    filters: {
      type: Object,
      required: true
    },
    rule: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      guideElementsActivityAggStatus: null,
      guideElements: [],
      guideWithContent: undefined
    };
  },
  computed: {
    currentElementData() {
      return this.guideWithContent?.steps.reduce((acc, step) => {
        if (!step.dom) {
          return acc;
        }
        const stepElementData = getCurrentElementData(JSON.parse(step.dom)).map(element => {
          return {
            ...element,
            guideStepId: step.id
          };
        });
        acc.push(...stepElementData);
        return acc;
      }, []);
    },
    currentIds() {
      return this.currentElementData?.map(element => element.uiElementId);
    },
    operatorOptions() {
      return [...SEGMENT_OPERATORS[this.rule.schema.schema]];
    },
    timeRangeOptions() {
      return this.rule.operator?.value === 'clicked' ? GUIDE_ELEMENT_TIME_RANGE_CLICKED_OPTIONS : GUIDE_ELEMENT_TIME_RANGE_NOT_CLICKED_OPTIONS;
    },
    isGuideElementsLoading() {
      return this.guideElementsActivityAggStatus === LOADING_STATES.LOADING;
    },
    guidePickerOptions() {
      const guideOptions = getEntitiesByAppSelection(this.rule.app, this.computedData.guides, this.config).filter(guide => {
        const isResourceCenter = get(guide, 'attributes.resourceCenter') && guide.state !== 'public';
        const isP2Guide = get(guide, 'attributes.type') === 'building-block';
        return !isResourceCenter && isP2Guide;
      });
      if (this.rule.guideId && this.rule.guideElementId && !guideOptions.find(guide => guide.guideId === this.rule.guideId)) {
        guideOptions.push({
          id: this.rule.guideId,
          name: `Deleted Guide (${this.rule.guideId})`,
          state: 'deleted'
        });
      }
      return guideOptions;
    },
    guideStepOptions() {
      const {
        steps
      } = this.rule.guide;
      const options = steps.map((step, index) => {
        return {
          guideStepId: step.id,
          name: step.name ? `${step.name} (Step ${index + 1})` : `Step ${index + 1}`
        };
      });
      if (this.rule.guideStepId && !options.find(option => option.guideStepId === this.rule.guideStepId)) {
        options.push({
          guideStepId: this.rule.guideStepId,
          name: `Deleted Step (${this.rule.guideStepId})`
        });
      }
      return options;
    },
    guideElementOptions() {
      return this.filterElementsByStep(this.rule.guideStepId);
    },
    selectedGuideStep() {
      return this.guideStepOptions.find(guideStepOption => guideStepOption.guideStepId === this.rule.guideStepId);
    },
    selectedGuideElement() {
      return this.guideElements.find(guideElement => guideElement.guideElementId === this.rule.guideElementId);
    },
    noGuideDataText() {
      return getNoResourcesFoundText({
        rule: this.rule,
        isMultiApp: this.config.usesMultiApp,
        resourceType: 'guide'
      });
    },
    showValueComponent() {
      const {
        rule
      } = this;
      const operatorValue = get(rule, 'operator.value');
      return !!(operatorValue && rule.time && rule.time !== 'ever');
    },
    valueComponent() {
      if (this.rule.time === 'since') {
        return getSingleDateConfig(this.rule, 'value');
      }
      if (this.rule.time === 'withinlast') {
        return getRelativeDateRangeConfig(this.rule, 'value');
      }
      if (this.rule.operator.value === 'clicked' && (this.rule.time === 'atmost' || this.rule.time === 'atleast')) {
        const numberConfig = getNumberConfig(this.rule);
        return {
          ...numberConfig,
          props: {
            ...numberConfig.props,
            min: 1,
            labels: {
              suffix: 'times'
            }
          }
        };
      }
      return;
    },
    guide() {
      return this.rule.guide;
    }
  },
  watch: {
    async guide(newGuide) {
      await this.getGuideWithContent(newGuide);
    }
  },
  created() {
    this.setupGuideElementsActivityAgg();
    this.getGuideWithContent(this.rule.guide);
  },
  methods: {
    async getGuideWithContent(guide) {
      this.guideWithContent = guide ? await getContent(guide) : undefined;
    },
    timeLabelObject(timeString) {
      return this.timeRangeOptions.find(opt => opt.value === timeString);
    },
    handleOperatorSelection($event) {
      const fields = [{
        field: 'operator',
        value: $event
      }];

      // handle switching to `not clicked` where `at least / at most` not supported
      if ($event.value === 'notclicked' && (this.rule.time === 'atleast' || this.rule.time === 'atmost')) {
        fields.push({
          field: 'time',
          value: 'ever'
        });
      }
      this.$emit('input', fields);
    },
    handleTimeSelection($event) {
      const {
        value
      } = $event;
      const fields = [{
        field: 'time',
        value
      }];
      if (value === 'withinlast') {
        fields.push({
          field: 'value',
          value: {
            count: 30,
            granularity: 'days'
          }
        });
      } else {
        fields.push({
          field: 'value',
          value: 1
        });
      }
      this.$emit('input', fields);
    },
    setupGuideElementsActivityAgg() {
      this.guideElementsActivityAggStatus = LOADING_STATES.LOADING;
      const agg$ = connectAggregationToState(guideElementsActivityAgg, withComponent({
        guideId: 'rule.guide.id',
        currentIds: 'currentIds',
        filters: 'filters'
      }))({
        component: this
      }, {
        debounceRequests: true
      });
      agg$.subscribe(response => {
        const {
          status,
          value
        } = response;
        switch (status) {
          case LOADING_STATES.LOADING:
            {
              this.guideElementsActivityAggStatus = status;
              this.guideElements = [];
              break;
            }
          case LOADING_STATES.REJECTED:
          case LOADING_STATES.RESOLVED:
            {
              const guideElementsActivity = processGuideElementsAggDropdown(value, this.currentElementData);
              if (this.rule.guideElementId && !guideElementsActivity.find(element => element.guideElementId === this.rule.guideElementId)) {
                guideElementsActivity.push({
                  guideElementId: this.rule.guideElementId,
                  guideStepId: this.rule.guideStepId,
                  name: `Deleted Element (${this.rule.guideElementId})`
                });
              }
              this.guideElementsActivityAggStatus = guideElementsActivity.length ? LOADING_STATES.RESOLVED : LOADING_STATES.EMPTY;
              this.guideElements = guideElementsActivity;
              break;
            }
        }
      });
    },
    async onGuideSelect(guide) {
      const fields = [{
        field: 'guide',
        value: guide
      }, {
        field: 'guideElementId',
        value: null
      }, {
        field: 'guideElementName',
        value: null
      }, {
        field: 'guideDeleted',
        value: false
      }];

      // Set the guide step for guides with only 1 step
      if (guide.steps.length === 1 && this.rule.guideStepId !== guide.steps[0].id) {
        fields.push({
          field: 'guideStepId',
          value: guide.steps[0].id
        });
      } else if (guide.steps.length !== 1) {
        fields.push({
          field: 'guideStepId',
          value: null
        });
      }
      this.$emit('input', fields);
    },
    onGuideStepSelect({
      guideStepId
    }) {
      const fields = [{
        field: 'guideStepId',
        value: guideStepId
      }, {
        field: 'guideElementId',
        value: null
      }, {
        field: 'guideElementName',
        value: null
      }];
      this.$emit('input', fields);
    },
    onGuideElementSelect({
      guideElementId,
      name
    }) {
      const fields = [{
        field: 'guideElementId',
        value: guideElementId
      }, {
        field: 'guideElementName',
        value: name
      }];
      this.$emit('input', fields);
    },
    filterElementsByStep(stepId) {
      if (!stepId || stepId === '*') {
        return this.guideElements;
      }
      return this.guideElements.filter(element => element.guideStepId === stepId);
    }
  }
};

/* script */
const __vue_script__$h = script$h;

/* template */
var __vue_render__$h = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "guide-element-rule"
  }, [_c('div', {
    class: {
      'guide-undefined': _vm.rule.guideDeleted
    }
  }, [_c('pendo-multiselect', {
    staticClass: "guide-element-rule--guide-select",
    attrs: {
      "value": _vm.rule.guide,
      "allow-empty": false,
      "options": _vm.guidePickerOptions,
      "searchable": true,
      "disabled": _vm.readOnly,
      "label-key": "name",
      "value-key": "id",
      "full-width": "",
      "placeholder": "Select Guide"
    },
    on: {
      "select": _vm.onGuideSelect
    },
    scopedSlots: _vm._u([{
      key: "option",
      fn: function (ref) {
        var option = ref.option;
        return [_c('pendo-guide-status-option', {
          attrs: {
            "tooltip": "",
            "option": option
          }
        })];
      }
    }, {
      key: "selectedLabel",
      fn: function (ref) {
        var option = ref.option;
        return [_c('pendo-guide-status-option', {
          attrs: {
            "tooltip": "",
            "option": option
          }
        })];
      }
    }, {
      key: "noData",
      fn: function () {
        return [_c('div', {
          staticClass: "no-data-message"
        }, [_vm._v("\n                    " + _vm._s(_vm.noGuideDataText) + "\n                ")])];
      },
      proxy: true
    }])
  }), _vm._v(" "), _vm.rule.guideDeleted ? _c('rule-warning', [_vm._v("\n            The original guide for this rule has been deleted.\n        ")]) : _vm._e()], 1), _vm._v(" "), _vm.rule.guide ? [_vm.guideStepOptions.length > 1 ? _c('pendo-multiselect', {
    staticClass: "guide-element-rule--guide-step-select",
    attrs: {
      "value": _vm.selectedGuideStep,
      "full-width": "",
      "allow-empty": false,
      "options": _vm.guideStepOptions,
      "disabled": _vm.readOnly,
      "label-key": "name",
      "value-key": "guideStepId",
      "placeholder": "Select Guide Step"
    },
    on: {
      "select": _vm.onGuideStepSelect
    }
  }) : _vm._e(), _vm._v(" "), _vm.rule.guideStepId || !_vm.rule.guide.steps.length ? _c('pendo-multiselect', {
    staticClass: "guide-element-rule--guide-element-select",
    attrs: {
      "value": _vm.selectedGuideElement,
      "full-width": "",
      "extra-label-width": 90,
      "allow-empty": false,
      "options": _vm.guideElementOptions,
      "loading": _vm.isGuideElementsLoading,
      "disabled": _vm.readOnly,
      "label-key": "name",
      "value-key": "guideElementId",
      "placeholder": "Select Guide Element"
    },
    on: {
      "select": _vm.onGuideElementSelect
    },
    scopedSlots: _vm._u([{
      key: "noData",
      fn: function () {
        return [_c('div', {
          staticClass: "no-data-message"
        }, [_vm._v("\n                    No guide elements found. Please select a different step or guide.\n                ")])];
      },
      proxy: true
    }, {
      key: "selectedLabel",
      fn: function (ref) {
        var option = ref.option;
        return [_c('pendo-icon-option', {
          attrs: {
            "option": option
          }
        })];
      }
    }, {
      key: "option",
      fn: function (ref) {
        var option = ref.option;
        return [_c('pendo-icon-option', {
          attrs: {
            "option": option
          }
        })];
      }
    }], null, false, 248712039)
  }) : _vm._e()] : _vm._e(), _vm._v(" "), _vm.rule.guideElementId ? [_c('pendo-multiselect', {
    staticClass: "guide-element-rule--operator",
    attrs: {
      "value": _vm.rule.operator,
      "allow-empty": false,
      "preselect-first": "",
      "full-width": "",
      "value-key": "value",
      "options": _vm.operatorOptions,
      "disabled": !_vm.operatorOptions || _vm.readOnly
    },
    on: {
      "select": _vm.handleOperatorSelection
    }
  }), _vm._v(" "), _c('pendo-multiselect', {
    staticClass: "guide-element-rule--time-operator",
    attrs: {
      "value": _vm.timeLabelObject(_vm.rule.time),
      "value-key": "value",
      "label-key": "label",
      "min-trigger-width": "100%",
      "allow-empty": false,
      "preselect-first": true,
      "options": _vm.timeRangeOptions,
      "disabled": _vm.readOnly
    },
    on: {
      "select": _vm.handleTimeSelection
    }
  }), _vm._v(" "), _vm.showValueComponent ? _c(_vm.valueComponent.component, _vm._b({
    tag: "component",
    on: {
      "input": function ($event) {
        return _vm.$emit('input', $event);
      }
    }
  }, 'component', _vm.valueComponent.props, false)) : _vm._e()] : _vm._e()], 2);
};
var __vue_staticRenderFns__$h = [];

/* style */
const __vue_inject_styles__$h = function (inject) {
  if (!inject) return;
  inject("data-v-3461742a_0", {
    source: ".guide-element-rule[data-v-3461742a]{display:contents}.guide-element-rule .guide-undefined[data-v-3461742a]  .pendo-multiselect__trigger{border:1px solid #cc3340}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$h = "data-v-3461742a";
/* module identifier */
const __vue_module_identifier__$h = undefined;
/* functional template */
const __vue_is_functional_template__$h = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$h = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$h,
  staticRenderFns: __vue_staticRenderFns__$h
}, __vue_inject_styles__$h, __vue_script__$h, __vue_scope_id__$h, __vue_is_functional_template__$h, __vue_module_identifier__$h, false, createInjector, undefined, undefined);

/* eslint-disable no-await-in-loop */
const SAVED = 1;
const INITIALIZED = 5;
const UPLOADED = 10;
const STARTED = 20;
const COMPLETE = 100;
function postToGCS(url, file, data) {
  const fd = new FormData();
  Object.keys(data).forEach(key => {
    fd.append(key, data[key]);
  });
  fd.append('file', file); // must be the last entry

  return http.post(url, fd, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    withCredentials: false
  });
}
async function getId(file) {
  if (file.id) {
    return {
      id: file.id
    };
  }
  return http.post('/api/s/_SID_/entitytag/visitor', {
    name: file.name
  }).then(res => res.data);
}
async function uploadEntitytagCsv(fileId, postBody = {}) {
  const {
    name,
    requestId
  } = postBody;
  return http.post(`/api/s/_SID_/entitytag/visitor/${fileId}/uploadcsv`, {
    name,
    requestId
  }).then(res => res.data);
}
async function processEntitytagCsv(fileId, postBody = {}) {
  const {
    name,
    requestId,
    excludeHeader,
    gcsUrl
  } = postBody;
  return http.post(`/api/s/_SID_/entitytag/visitor/${fileId}/processcsv`, {
    name,
    requestId,
    excludeHeader,
    gcsUrl
  }).then(res => res.data);
}
async function uploadMetadataCsv(kind, group, postBody = {}) {
  const {
    name,
    requestId
  } = postBody;
  return http.post(`/api/s/_SID_/metadata/${kind}/${group}/uploadcsv`, {
    name,
    requestId
  }).then(res => res.data);
}
async function processMetadataCsv(kind, group, postBody = {}) {
  const {
    name,
    requestId,
    gcsUrl
  } = postBody;
  return http.post(`/api/s/_SID_/metadata/${kind}/${group}/processcsv`, {
    name,
    requestId,
    gcsUrl
  }).then(res => res.data);
}
async function pollStatus(statusUrl, notify, shouldLogStatus) {
  let status = {};
  try {
    status = await http.get(statusUrl).then(res => res.data);
  } catch (error) {
    throw error;
  }

  // Early status calls return {}
  if (!status.command) {
    return 0;
  }
  const {
    command
  } = status;
  if (command === 'progress' || command === 'processing') {
    const {
      entityTagsApplied,
      entityTagsTotal
    } = status;
    const progress = STARTED + Math.round(entityTagsApplied / entityTagsTotal * (100 - STARTED));
    notify({
      ...status,
      progress
    });
    if (shouldLogStatus) {
      // eslint-disable-next-line no-console
      console.info('[CSV Upload] Status: ', status);
    }
    return 0;
  }

  // status.command === 'finished || status.command === 'error'
  notify({
    progress: status
  });
  return 1;
}
function pollInterval(statusUrl, notify) {
  let pollingInterval;
  const pollingPromise = new Promise((resolve, reject) => {
    let timesPolled = 0;
    pollingInterval = window.setInterval(async () => {
      timesPolled++;
      const shouldLogStatus = !(timesPolled % 10);
      try {
        const isFinished = await pollStatus(statusUrl, notify, shouldLogStatus);
        if (!isFinished) {
          return;
        }
        window.clearInterval(pollingInterval);
        resolve();
      } catch (err) {
        reject(err);
      }
    }, 1000);
  });
  return {
    pollingPromise,
    pollingInterval
  };
}
async function coordinateEntitytagCsvUpload(file, notify = () => {}, params = {}) {
  const requestId = params.requestId || `req-${uuid()}`;
  let pollingInterval;
  try {
    const {
      id
    } = await getId(file);
    notify({
      progress: SAVED
    });
    file.id = id;
    const uploadPostBody = {
      name: file.name,
      requestId
    };
    const {
      uploadUrl,
      formData
    } = await uploadEntitytagCsv(file.id, uploadPostBody);
    notify({
      progress: INITIALIZED
    });
    await postToGCS(uploadUrl, file, formData);
    notify({
      progress: UPLOADED
    });
    const processPostBody = {
      requestId,
      name: file.name,
      gcsUrl: uploadUrl,
      excludeHeader: params.excludeHeader
    };
    await processEntitytagCsv(file.id, processPostBody);
    notify({
      progress: STARTED
    });
    const statusUrl = `/api/s/_SID_/entitytag/status/${requestId}`;
    const pollingData = pollInterval(statusUrl, notify);
    pollingInterval = pollingData.pollingInterval;
    const {
      pollingPromise
    } = pollingData;
    return {
      entityTagId: id,
      pollingPromise,
      pollingInterval
    };
  } catch (error) {
    notify({
      progress: {
        command: 'error',
        error
      }
    });
    window.clearInterval(pollingInterval);
    // eslint-disable-next-line no-console
    console.error('An unexpected error occurred while attempting to process a metadata csv upload', error);
    throw error;
  }
}
async function coordinateMetadataCsvUpload(file, kind, group, notify = () => {}, params = {}) {
  const requestId = params.requestId || `req-${uuid()}`;
  try {
    const uploadPostData = {
      requestId,
      name: file.name
    };
    const {
      uploadUrl,
      formData
    } = await uploadMetadataCsv(kind, group, uploadPostData);
    notify({
      progress: INITIALIZED
    });
    await postToGCS(uploadUrl, file, formData);
    notify({
      progress: UPLOADED
    });
    await processMetadataCsv(kind, group, {
      name: file.name,
      gcsUrl: uploadUrl,
      requestId
    });
    notify({
      progress: STARTED
    });
    const statusUrl = `/api/s/_SID_/metadata/status/${requestId}`;
    return pollInterval(statusUrl, notify);
  } catch (error) {
    notify({
      progress: COMPLETE
    });
    throw error;
  }
}

const PROGRESS_COLORS = {
  DEFAULT: {
    fill: '#EAECF1',
    stroke: '#EAECF1'
  },
  PROGRESS: {
    fill: '#128297',
    stroke: '#128297'
  },
  SUCCESS: {
    fill: '#00C583',
    stroke: '#00C583'
  },
  WARNING: {
    fill: '#DBA211',
    stroke: '#DBA211'
  },
  ERROR: {
    fill: '#DB1111',
    stroke: '#6A0000'
  }
};

//
var script$g = {
  name: 'CsvUpload',
  components: {
    PendoButton,
    PendoIcon,
    PendoTag,
    PendoAlert,
    PendoCheckbox,
    PendoModal,
    PendoProgressBar,
    PendoIconButton
  },
  mixins: [ReadOnlyMixin],
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    isIcon: {
      type: Boolean,
      default: false
    },
    hasExcludeHeaderCheck: {
      type: Boolean,
      default: true
    },
    title: {
      type: String,
      required: true
    },
    description: {
      type: String,
      default: ''
    },
    uploadType: {
      type: String,
      default: 'entitytags'
    },
    hasWarning: {
      type: Boolean,
      default: false
    },
    hasError: {
      type: Boolean,
      default: false
    },
    hideCancelOnSuccess: {
      type: Boolean,
      default: false
    },
    finishText: {
      type: String,
      default: 'Finish'
    }
  },
  data() {
    return {
      showUploadModal: false,
      someVisitorIdsNotMatched: false,
      visitorIdMismatchList: [],
      localIsFailed: false,
      isUploading: false,
      isSuccess: false,
      isDefaultState: true,
      file: null,
      percentComplete: 0,
      excludeHeader: false,
      tagId: this.value.tagId,
      uploadStatusInterval: null,
      isPaused: false
    };
  },
  computed: {
    isFailed() {
      return this.hasError || this.localIsFailed;
    },
    filename() {
      return this.value.filename;
    },
    isReady() {
      return this.percentComplete === 0;
    },
    progressBarColors() {
      // default -> success
      let complete = PROGRESS_COLORS.SUCCESS;

      // warning -> some requests passed, some failed
      if (this.hasWarning) {
        complete = PROGRESS_COLORS.WARNING;
      }

      // error -> some backend error
      if (this.isFailed) {
        complete = PROGRESS_COLORS.ERROR;
      }
      return {
        default: PROGRESS_COLORS.DEFAULT,
        progress: PROGRESS_COLORS.PROGRESS,
        complete
      };
    }
  },
  methods: {
    removeFile() {
      this.tagId = null;
      this.file = null;
      this.$emit('input', {
        field: 'tag',
        value: {
          tagId: null,
          filename: null
        }
      });
    },
    openModal() {
      this.showUploadModal = true;
    },
    closeModal() {
      this.showUploadModal = false;
    },
    setEntityTag() {
      this.$emit('input', {
        field: 'tag',
        value: {
          tagId: this.tagId,
          filename: this.file.name
        }
      });
      this.closeModal();
    },
    // modal methods
    chooseFile() {
      // clear input before uploading to ensure
      // change event is triggered for same file uploads
      this.$refs.fileInput.value = null;
      this.$refs.fileInput.click();
    },
    onFileChange(event) {
      this.file = event.target.files[0];
    },
    reset() {
      window.clearInterval(this.uploadStatusInterval);
      this.$emit('reset');
      this.isUploading = false;
      this.percentComplete = 0;
      this.excludeHeader = false;
      this.isSuccess = false;
      this.localIsFailed = false;
      this.file = null;
      this.errorObject = {};
      this.isPaused = false;
    },
    onProgress({
      progress
    }) {
      this.$emit('progress', progress);
      if (!isNaN(progress)) {
        this.percentComplete = progress;
        return;
      }
      const {
        done
      } = this.$refs.uploadProgressBar;
      const {
        warnings,
        command
      } = progress;
      if (command) {
        setTimeout(() => {
          this.isUploading = false;
        }, 1000);
      }
      if (command === 'pause') {
        this.isPaused = true;
      } else if (command === 'finish' || command === 'finish with error') {
        if (warnings) {
          this.$emit('warning', warnings);
        }
        if (command === 'finish with error') {
          this.$emit('warning', progress);
        }
        this.isSuccess = true;
        this.$emit('success', progress);
        done();
      } else if (command === 'error' || command === 'bad request') {
        this.$emit('error', progress);
        done();
      }
      return;
    },
    async uploadFile() {
      if (!this.file) {
        return;
      }
      this.localIsFailed = false;
      this.isSuccess = false;
      this.isUploading = true;

      // wait for progress bar to be rendered to DOM
      setTimeout(() => {
        this.$refs.uploadProgressBar.start();
      }, 0);
      let coordinationPromise;
      switch (this.uploadType) {
        case 'entitytags':
          coordinationPromise = coordinateEntitytagCsvUpload(this.file, this.onProgress, {
            excludeHeader: this.excludeHeader
          });
          break;
        case 'metadata':
          coordinationPromise = coordinateMetadataCsvUpload(this.file, 'visitor', 'custom', this.onProgress);
          break;
      }
      try {
        const {
          entityTagId,
          pollingPromise,
          pollingInterval
        } = await coordinationPromise;
        this.uploadStatusInterval = pollingInterval;

        // Reset state and clear interval if cancelled once the intial upload request resolves
        if (!this.isUploading) {
          this.reset();
          return;
        }
        await pollingPromise;

        // entityTagId will be undefined for metadata uploads
        this.tagId = entityTagId;
      } catch (error) {
        this.$emit('error', error);
      }
    },
    continueWithCsv() {
      this.isPaused = false;
      this.isUploading = true;
      this.$emit('continueWithCsv', this.onProgress);
    }
  }
};

/* script */
const __vue_script__$g = script$g;

/* template */
var __vue_render__$g = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "csv-upload"
  }, [_vm.isIcon ? _c('pendo-icon-button', {
    staticClass: "csv-upload__icon",
    attrs: {
      "disabled": _vm.readOnly,
      "tooltip": "Upload CSV",
      "icon": "upload",
      "icon-size": "16"
    },
    on: {
      "click": _vm.openModal
    }
  }) : !_vm.value.tagId ? _c('pendo-button', {
    attrs: {
      "disabled": _vm.readOnly,
      "theme": "app",
      "type": "secondary",
      "label": "Upload CSV"
    },
    on: {
      "click": _vm.openModal
    }
  }) : _vm._e(), _vm._v(" "), _vm.value.tagId ? _c('pendo-tag', {
    staticClass: "csv-upload__tag",
    attrs: {
      "disabled": _vm.readOnly,
      "type": "filter",
      "closable": true,
      "label": _vm.filename
    },
    on: {
      "close": _vm.removeFile
    }
  }) : _vm._e(), _vm._v(" "), _c('pendo-modal', {
    staticClass: "csv-upload-modal",
    attrs: {
      "visible": _vm.showUploadModal,
      "title": _vm.title,
      "height": "auto",
      "max-height": "80%"
    },
    on: {
      "close": _vm.closeModal,
      "closed": _vm.reset
    },
    scopedSlots: _vm._u([{
      key: "footer",
      fn: function () {
        return [_c('div', {
          staticClass: "csv-upload-modal__footer"
        }, [!_vm.isUploading ? [!(_vm.isSuccess && _vm.hideCancelOnSuccess) ? _c('pendo-button', {
          attrs: {
            "theme": "app",
            "type": "secondary",
            "label": "Cancel"
          },
          on: {
            "click": _vm.closeModal
          }
        }) : _vm._e(), _vm._v(" "), _vm.isSuccess ? _c('pendo-button', {
          attrs: {
            "theme": "app",
            "type": "primary",
            "label": _vm.finishText
          },
          on: {
            "click": _vm.setEntityTag
          }
        }) : _vm._e(), _vm._v(" "), _vm.isFailed ? _c('pendo-button', {
          attrs: {
            "theme": "app",
            "type": "primary",
            "label": "Upload Another CSV"
          },
          on: {
            "click": _vm.reset
          }
        }) : _vm._e(), _vm._v(" "), _vm.isPaused ? _c('pendo-button', {
          attrs: {
            "theme": "app",
            "type": "primary",
            "label": "Continue with this CSV"
          },
          on: {
            "click": _vm.continueWithCsv
          }
        }) : _vm._e()] : _vm._e(), _vm._v(" "), _vm.isReady || _vm.isUploading ? _c('pendo-button', {
          attrs: {
            "theme": "app",
            "disabled": _vm.isUploading || !_vm.file,
            "loading": _vm.isUploading,
            "label": _vm.isUploading ? 'Uploading...' : 'Upload File',
            "type": "primary"
          },
          on: {
            "click": _vm.uploadFile
          }
        }) : _vm._e()], 2)];
      },
      proxy: true
    }])
  }, [!_vm.isUploading && _vm.isReady ? [_c('div', {
    staticClass: "csv-upload-modal__description"
  }, [_vm._v("\n                " + _vm._s(_vm.description) + "\n            ")]), _vm._v(" "), _c('div', [_c('input', {
    ref: "fileInput",
    attrs: {
      "type": "file",
      "hidden": "true",
      "accept": ".csv"
    },
    on: {
      "change": _vm.onFileChange
    }
  }), _vm._v(" "), _c('div', {
    staticClass: "csv-upload-modal__file-format-note"
  }, [_c('pendo-icon', {
    attrs: {
      "type": "info",
      "size": "14"
    }
  }), _c('span', [_vm._v("\n                            File must be new line delimited.\n                            "), _c('a', {
    attrs: {
      "href": "https://adoptsupport.pendo.io/hc/en-us/articles/4406529611035-Metadata-Overview",
      "target": "_blank"
    }
  }, [_vm._v("More file format details.\n                            ")])])], 1), _vm._v(" "), _c('div', {
    staticClass: "csv-upload-modal__file-upload"
  }, [_c('pendo-button', {
    attrs: {
      "theme": "app",
      "type": "secondary",
      "label": "Choose File"
    },
    on: {
      "click": _vm.chooseFile
    }
  }), _vm._v(" "), _vm.file ? _c('pendo-tag', {
    staticClass: "csv-upload__tag",
    attrs: {
      "type": "filter",
      "closable": true,
      "label": _vm.file.name
    },
    on: {
      "close": _vm.removeFile
    }
  }) : _vm._e()], 1), _vm._v(" "), _vm.hasExcludeHeaderCheck ? _c('pendo-checkbox', {
    staticClass: "csv-upload-modal__exclude-header",
    attrs: {
      "label": "Exclude row 1 (column header)"
    },
    model: {
      value: _vm.excludeHeader,
      callback: function ($$v) {
        _vm.excludeHeader = $$v;
      },
      expression: "excludeHeader"
    }
  }) : _vm._e()], 1)] : _vm._e(), _vm._v(" "), _vm.isUploading ? _c('div', [_c('div', {
    staticClass: "csv-upload-modal__progress"
  }, [_c('pendo-progress-bar', {
    ref: "uploadProgressBar",
    attrs: {
      "width": 360,
      "value": _vm.percentComplete,
      "colors": _vm.progressBarColors,
      "trickle": ""
    }
  }), _vm._v(" "), _c('div', {
    staticClass: "csv-upload-modal__progress-status"
  }, [_vm._v("\n                    Uploading " + _vm._s(_vm.file.name) + "...\n                ")])], 1)]) : _vm._e(), _vm._v(" "), !_vm.isUploading ? [_c('div', [_vm.isSuccess ? _c('pendo-alert', {
    staticClass: "csv-upload-modal__success-alert",
    attrs: {
      "title": "CSV Uploaded",
      "type": "success"
    }
  }, [_vm.isSuccess ? _vm._t("success-message") : _vm._e()], 2) : _vm._e(), _vm._v(" "), _vm.isFailed ? _c('pendo-alert', {
    staticClass: "csv-upload-modal__failed-alert",
    attrs: {
      "type": "error"
    }
  }, [_c('div', [_c('strong', [_vm._v(_vm._s(_vm.file.name))]), _vm._v(" failed to upload.\n                    ")]), _vm._v(" "), _vm._t("error-message")], 2) : _vm._e()], 1), _vm._v(" "), _vm.hasWarning ? _c('pendo-alert', {
    attrs: {
      "type": "warning"
    }
  }, [_vm._t("warning-message")], 2) : _vm._e()] : _vm._e()], 2)], 1);
};
var __vue_staticRenderFns__$g = [];

/* style */
const __vue_inject_styles__$g = function (inject) {
  if (!inject) return;
  inject("data-v-2c5558f4_0", {
    source: ".csv-upload{display:flex;align-items:center}.csv-upload .csv-upload__icon{margin-right:8px;padding-right:8px}.csv-upload .csv-upload__tag,.csv-upload-modal .csv-upload__tag{height:36px;line-height:34px;font-size:14px;text-transform:none;overflow:hidden}.csv-upload .csv-upload__tag span,.csv-upload-modal .csv-upload__tag span{overflow:hidden;text-overflow:ellipsis}.csv-upload-modal__limit-exceeded{margin-top:8px}.csv-upload-modal__description{margin-bottom:16px}.csv-upload-modal__exclude-header{margin-top:8px}.csv-upload-modal__footer{display:flex;justify-content:flex-end;align-items:center}.csv-upload-modal__file-upload{display:flex}.csv-upload-modal__file-upload .csv-upload__tag{margin-left:8px}.csv-upload-modal__file-format-note,.csv-upload-modal__mismatch-alert{display:flex;align-items:flex-start}.csv-upload-modal__file-format-note .pendo-icon,.csv-upload-modal__mismatch-alert .pendo-icon{margin-top:2px}.csv-upload-modal__file-format-note span,.csv-upload-modal__mismatch-alert span{margin-left:6px}.csv-upload-modal__file-format-note{margin-bottom:16px}.csv-upload-modal__progress{display:grid;grid-gap:16px}.csv-upload-modal__progress-status{color:#6a6c75;font-weight:600;text-align:center}.pendo-alert.csv-upload-modal__failure-alert,.pendo-alert.csv-upload-modal__status-warning,.pendo-alert.csv-upload-modal__success-alert{font-weight:700;margin-bottom:16px}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$g = undefined;
/* module identifier */
const __vue_module_identifier__$g = undefined;
/* functional template */
const __vue_is_functional_template__$g = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$g = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$g,
  staticRenderFns: __vue_staticRenderFns__$g
}, __vue_inject_styles__$g, __vue_script__$g, __vue_scope_id__$g, __vue_is_functional_template__$g, __vue_module_identifier__$g, false, createInjector, undefined, undefined);

//
var script$f = {
  name: 'SegmentCsvUpload',
  components: {
    CsvUpload: __vue_component__$g
  },
  props: {
    value: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      errorObject: null,
      hasWarning: false,
      existingVisitorsFound: 0,
      totalVisitorsInCSV: 0,
      csvLimit: '10,000,000',
      csvLimitExceeded: false,
      hasConflicts: false,
      someVisitorIdsNotMatched: false,
      visitorIdMismatchList: null
    };
  },
  computed: {
    errorHasMessage() {
      return get(this.errorObject, 'status') && get(this.errorObject, 'message');
    }
  },
  methods: {
    onError(error) {
      const {
        status,
        statusText
      } = error;
      const errorMessage = error.error || error.data || '';
      const errorObject = {
        message: errorMessage,
        status,
        statusText
      };
      switch (status) {
        case 413:
          errorObject.statusText = 'Request Entity Too Large';
          errorObject.erroMessage = 'CSV has exceeded the limit. Try uploading a CSV with less entries';
          break;
        case 400:
          if (errorMessage.includes('Csv contains greater than')) {
            try {
              // format string as millions
              this.csvLimit = errorMessage.match(/[0-9]+/)[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            } catch (err) {
              this.csvLimit = '10,000,000';
            }
            this.csvLimitExceeded = true;
          }
          break;
        case 409:
          this.hasConflicts = true;
          break;
      }
      this.errorObject = errorObject;
    },
    onWarning(warning) {
      const warningData = get(warning, 'errors');

      // This message is specific to adoptv1
      // Adoptv2 and engage do not explicitly surface warnings here
      if (get(warningData, '0.type') === 'VISITOR_IDS_NOT_FOUND') {
        this.hasWarning = true;
        this.someVisitorIdsNotMatched = true;
        this.visitorIdMismatchList = get(warningData, '0.visitors');
        return;
      }
    },
    onSuccess(success) {
      const existingVisitorsFound = parseInt(success.checkFound);
      const totalVisitorsInCSV = parseInt(success.checkTotal);
      if (isNaN(existingVisitorsFound) || isNaN(totalVisitorsInCSV)) {
        return;
      }
      if (existingVisitorsFound < totalVisitorsInCSV) {
        this.hasWarning = true;
        this.existingVisitorsFound = existingVisitorsFound;
        this.totalVisitorsInCSV = totalVisitorsInCSV;
        this.someVisitorIdsNotMatched = true;
      }
    },
    resetUploadStatus() {
      this.errorObject = null;
      this.hasWarning = false;
      this.hasConflicts = false;
      this.csvLimitExceeded = false;
      this.visitorIdMismatchList = null;
    }
  }
};

/* script */
const __vue_script__$f = script$f;

/* template */
var __vue_render__$f = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('csv-upload', {
    attrs: {
      "title": "Upload Visitor CSV",
      "description": "Select and upload a file to create segments or target guides to a specific, static group of visitors.",
      "value": _vm.value,
      "has-error": !!_vm.errorObject,
      "has-warning": _vm.hasWarning,
      "finish-text": "Apply to segment"
    },
    on: {
      "input": function ($event) {
        return _vm.$emit('input', $event);
      },
      "reset": _vm.resetUploadStatus,
      "error": _vm.onError,
      "warning": _vm.onWarning,
      "success": _vm.onSuccess
    },
    scopedSlots: _vm._u([{
      key: "error-message",
      fn: function () {
        return [_vm.errorHasMessage ? _c('div', [_c('strong', [_vm._v(_vm._s(_vm.errorObject.status) + ": ")]), _vm._v("\n            " + _vm._s(_vm.errorObject.message) + ".\n        ")]) : _vm._e(), _vm._v(" "), _vm.csvLimitExceeded ? _c('div', [_vm._v("\n            CSV exceeds the amount of supported entries (" + _vm._s(_vm.csvLimit) + ").\n        ")]) : _vm._e(), _vm._v(" "), _vm.hasConflicts ? _c('div', [_vm._v("\n            Another list is currently being uploaded to your account. Please retry your upload again in a few\n            minutes.\n        ")]) : _vm._e()];
      },
      proxy: true
    }, {
      key: "warning-message",
      fn: function () {
        return [_vm.someVisitorIdsNotMatched ? _c('div', [_c('div', [_vm._v("\n                We could not find matches for all visitor entries. Your segment using this CSV will only affect the\n                matched visitors (" + _vm._s(_vm.existingVisitorsFound.toLocaleString()) + "/" + _vm._s(_vm.totalVisitorsInCSV.toLocaleString()) + ")\n            ")]), _vm._v(" "), _vm.visitorIdMismatchList ? _c('div', {
          staticClass: "visitor-mismatch"
        }, [_c('strong', [_vm._v("Unmatched Entries")]), _vm._v(" "), _vm._l(_vm.visitorIdMismatchList, function (visitorId) {
          return _c('div', {
            key: visitorId
          }, [_vm._v("\n                    " + _vm._s(visitorId) + "\n                ")]);
        })], 2) : _vm._e()]) : _vm._e()];
      },
      proxy: true
    }])
  });
};
var __vue_staticRenderFns__$f = [];

/* style */
const __vue_inject_styles__$f = function (inject) {
  if (!inject) return;
  inject("data-v-1e049d2e_0", {
    source: ".visitor-mismatch[data-v-1e049d2e]{margin-top:12px}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$f = "data-v-1e049d2e";
/* module identifier */
const __vue_module_identifier__$f = undefined;
/* functional template */
const __vue_is_functional_template__$f = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$f = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$f,
  staticRenderFns: __vue_staticRenderFns__$f
}, __vue_inject_styles__$f, __vue_script__$f, __vue_scope_id__$f, __vue_is_functional_template__$f, __vue_module_identifier__$f, false, createInjector, undefined, undefined);

//
var script$e = {
  name: 'StringInput',
  components: {
    PendoInput
  },
  mixins: [ReadOnlyMixin],
  props: {
    value: {
      type: null,
      default: null
    },
    field: {
      type: String,
      default: 'value'
    },
    placeholder: {
      type: String,
      default: 'Type value here'
    }
  },
  created() {
    const value = isString(this.value) ? this.value : '';
    this.onInputChange(value);
  },
  methods: {
    // use change event to emit update on blur of input instead of every single keystroke
    onInputChange(value) {
      this.$emit('input', {
        field: this.field,
        value
      });
    }
  }
};

/* script */
const __vue_script__$e = script$e;

/* template */
var __vue_render__$e = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('pendo-input', {
    staticClass: "string-input",
    class: _vm.field + "-string-input",
    attrs: {
      "placeholder": _vm.placeholder,
      "value": _vm.value,
      "disabled": _vm.readOnly,
      "debounce": ""
    },
    on: {
      "input": _vm.onInputChange
    }
  });
};
var __vue_staticRenderFns__$e = [];

/* style */
const __vue_inject_styles__$e = undefined;
/* scoped */
const __vue_scope_id__$e = undefined;
/* module identifier */
const __vue_module_identifier__$e = undefined;
/* functional template */
const __vue_is_functional_template__$e = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$e = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$e,
  staticRenderFns: __vue_staticRenderFns__$e
}, __vue_inject_styles__$e, __vue_script__$e, __vue_scope_id__$e, __vue_is_functional_template__$e, __vue_module_identifier__$e, false, undefined, undefined, undefined);

function queryMetadataValues(kind, group, field, query) {
  return http.get(`api/s/_SID_/metadata/${kind}/${group}/schema/${field}/values?filter=${query}`);
}

//
var script$d = {
  name: 'MetadataValueSelect',
  components: {
    PendoAlert,
    PendoMultiselect
  },
  mixins: [ReadOnlyMixin],
  props: {
    field: {
      type: String,
      default: 'value'
    },
    kind: {
      type: String,
      default: ''
    },
    group: {
      type: String,
      default: ''
    },
    fieldName: {
      type: String,
      default: ''
    },
    value: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      isLoading: true,
      initialLimitExceeded: false,
      initialOptions: [],
      limitExceeded: false,
      options: [],
      searchValue: ''
    };
  },
  computed: {
    showSearchHeader() {
      return this.searchValue === '' && this.initialLimitExceeded;
    },
    maxMenuHeight() {
      const defaultHeight = 302;
      const searchNoticeHeight = 100;
      const limitNoticeHeight = 30;
      if (this.showSearchHeader) {
        return defaultHeight + searchNoticeHeight + limitNoticeHeight;
      }
      return this.limitExceeded ? defaultHeight + limitNoticeHeight : defaultHeight;
    }
  },
  async created() {
    this.getOptionsDebounce = debounce(this.getOptions, 500);
    await this.getOptions('');
    this.initialLimitExceeded = this.limitExceeded;
    if (this.initialLimitExceeded) {
      this.initialOptions = clone(this.options);
    }
    const value = isString(this.value) ? this.value : '';
    this.onOptionSelected(value);
  },
  methods: {
    async getOptions(query) {
      if (query === '' && this.initialOptions.length !== 0) {
        this.limitExceeded = this.initialLimitExceeded;
        this.options = clone(this.initialOptions);
        this.isLoading = false;
        return;
      }
      this.isLoading = true;
      try {
        const {
          data
        } = await queryMetadataValues(this.kind, this.group, this.fieldName, query);
        this.limitExceeded = data.limitExceeded;
        this.options = data.values;
      } catch {
        PendoNotification({
          type: 'error',
          message: 'Unable to fetch metadata values',
          duration: 5000
        });
      }
      this.isLoading = false;
    },
    onInputValueChanged(inputValue) {
      if (this.initialLimitExceeded) {
        this.searchValue = inputValue;
        this.isLoading = true;
        this.getOptionsDebounce(inputValue);
      }
    },
    onOptionSelected(value) {
      this.$emit('input', {
        field: this.field,
        value
      });
    }
  }
};

/* script */
const __vue_script__$d = script$d;

/* template */
var __vue_render__$d = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('pendo-multiselect', {
    attrs: {
      "allow-empty": false,
      "append-to-body": "",
      "loading": _vm.isLoading,
      "disable-while-loading": false,
      "disabled": _vm.readOnly,
      "extra-label-width": 60,
      "internal-search": !_vm.limitExceeded,
      "max-menu-height": _vm.maxMenuHeight,
      "min-trigger-width": "80px",
      "options": _vm.options,
      "placeholder": "Type value here",
      "searchable": "",
      "taggable": "",
      "tag-placeholder": "(new)",
      "value": _vm.value
    },
    on: {
      "input-value-change": _vm.onInputValueChanged,
      "select": _vm.onOptionSelected,
      "tag": _vm.onOptionSelected
    },
    scopedSlots: _vm._u([{
      key: "header",
      fn: function () {
        return [_vm.showSearchHeader ? _c('div', {
          staticClass: "metadata-value-select--search-notice",
          attrs: {
            "data-cy": "metadata-value-select--search-notice"
          }
        }, [_c('pendo-alert', {
          attrs: {
            "show-icon": false
          }
        }, [_vm._v("\n                Begin your search with the "), _c('strong', [_vm._v("first case-sensitive letters")]), _vm._v(" of a value (e.g. “Pen” for\n                “Pendo”)\n            ")])], 1) : _vm._e(), _vm._v(" "), _vm.limitExceeded ? _c('p', {
          staticClass: "metadata-value-select--limit-notice",
          attrs: {
            "data-cy": "metadata-value-select--limit-notice"
          }
        }, [_vm._v("\n            Showing first 400 results only\n        ")]) : _vm._e()];
      },
      proxy: true
    }, {
      key: "noData",
      fn: function () {
        return [_c('p', {
          staticClass: "metadata-value-select--empty-state"
        }, [_vm._v("\n            No options available\n        ")])];
      },
      proxy: true
    }])
  });
};
var __vue_staticRenderFns__$d = [];

/* style */
const __vue_inject_styles__$d = function (inject) {
  if (!inject) return;
  inject("data-v-4ed08010_0", {
    source: ".metadata-value-select--empty-state[data-v-4ed08010]{color:#6a6c75;font-size:14px;line-height:21px;margin:0;text-align:center}.metadata-value-select--limit-notice[data-v-4ed08010]{font-weight:700;margin:0;padding:8px 16px;text-align:center}.metadata-value-select--search-notice[data-v-4ed08010]{padding:16px 16px 8px}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$d = "data-v-4ed08010";
/* module identifier */
const __vue_module_identifier__$d = undefined;
/* functional template */
const __vue_is_functional_template__$d = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$d = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$d,
  staticRenderFns: __vue_staticRenderFns__$d
}, __vue_inject_styles__$d, __vue_script__$d, __vue_scope_id__$d, __vue_is_functional_template__$d, __vue_module_identifier__$d, false, createInjector, undefined, undefined);

//
var script$c = {
  name: 'MetadataRule',
  components: {
    PendoMultiselect,
    PendoInputNumber,
    PendoInput,
    PendoDatePicker,
    AbsoluteDateRange: __vue_component__$n,
    SegmentCsvUpload: __vue_component__$f,
    NumberInput: __vue_component__$m,
    RelativeDateRange: __vue_component__$r,
    SingleDate: __vue_component__$q,
    StringInput: __vue_component__$e,
    MetadataValueSelect: __vue_component__$d
  },
  mixins: [ReadOnlyMixin],
  props: {
    computedData: {
      type: Object,
      required: true
    },
    rule: {
      type: Object,
      required: true
    }
  },
  computed: {
    metadata() {
      return this.computedData.filteredSchemaList.find(({
        id
      }) => this.rule.schema.id === id);
    },
    operatorOptions() {
      const options = [...SEGMENT_OPERATORS[this.rule.schema.schema]];
      if (this.rule.schema.id === 'visitor_auto_id') {
        return options;
      }
      return options.filter(option => !isCsvUploadOperator(option.value));
    },
    ruleWithLatestMetadataOptions() {
      const rule = cloneDeep(this.rule);
      rule.schema.neverIndex = this.metadata?.neverIndex;
      return rule;
    },
    showValueComponent() {
      return this.rule.operator && !this.rule.operator.unary;
    },
    valueComponent() {
      const rule = this.ruleWithLatestMetadataOptions;
      const schema = rule.schema.schema === 'list' ? rule.schema.elementType : rule.schema.schema;
      switch (schema) {
        case 'integer':
        case 'float':
        case 'number':
        case 'aggregation':
          return getNumberConfig(rule, 'value');
        case 'time':
        case 'date':
          if (['withinLast', '!withinLast', 'withinNext', '!withinNext'].includes(rule.operator.value)) {
            return getRelativeDateRangeConfig(rule, 'value');
          }
          if (['between'].includes(rule.operator.value)) {
            return getAbsoluteDateRangeConfig(rule);
          }
          if (['>=', '<='].includes(rule.operator.value)) {
            return getSingleDateConfig(rule, 'value');
          }
          return;
        default:
          if (isCsvUploadOperator(rule.operator.value)) {
            return getSegmentCsvConfig(rule);
          }
          return getStringConfig(rule, 'value');
      }
    }
  },
  created() {
    const isBoolean = get(this.rule, 'schema.schema') === 'boolean';
    const {
      value,
      operator
    } = this.rule;
    if (isNil(value) && isBoolean) {
      this.$emit('input', {
        field: 'operator',
        value: operator
      });
    }
  }
};

/* script */
const __vue_script__$c = script$c;

/* template */
var __vue_render__$c = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "metadata-rule"
  }, [_c('pendo-multiselect', {
    staticClass: "rule-editor--operator",
    attrs: {
      "value": _vm.rule.operator,
      "allow-empty": false,
      "preselect-first": "",
      "full-width": "",
      "value-key": "value",
      "options": _vm.operatorOptions,
      "disabled": !_vm.operatorOptions || _vm.readOnly
    },
    on: {
      "select": function ($event) {
        return _vm.$emit('input', {
          field: 'operator',
          value: $event
        });
      }
    }
  }), _vm._v(" "), _vm.showValueComponent ? _c(_vm.valueComponent.component, _vm._b({
    tag: "component",
    on: {
      "input": function ($event) {
        return _vm.$emit('input', $event);
      }
    }
  }, 'component', _vm.valueComponent.props, false)) : _vm._e()], 1);
};
var __vue_staticRenderFns__$c = [];

/* style */
const __vue_inject_styles__$c = function (inject) {
  if (!inject) return;
  inject("data-v-08960d02_0", {
    source: ".metadata-rule[data-v-08960d02]{display:contents}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$c = "data-v-08960d02";
/* module identifier */
const __vue_module_identifier__$c = undefined;
/* functional template */
const __vue_is_functional_template__$c = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$c = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$c,
  staticRenderFns: __vue_staticRenderFns__$c
}, __vue_inject_styles__$c, __vue_script__$c, __vue_scope_id__$c, __vue_is_functional_template__$c, __vue_module_identifier__$c, false, createInjector, undefined, undefined);

//
var script$b = {
  name: 'PageRule',
  components: {
    AbsoluteDateRange: __vue_component__$n,
    NumberInput: __vue_component__$m,
    PendoMultiselect,
    RelativeDateRange: __vue_component__$r,
    RuleWarning: __vue_component__$o,
    SingleDate: __vue_component__$q
  },
  mixins: [ReadOnlyMixin],
  props: {
    computedData: {
      type: Object,
      required: true
    },
    config: {
      type: Object,
      required: true
    },
    rule: {
      type: Object,
      required: true
    }
  },
  computed: {
    filteredPageList() {
      const {
        pages
      } = this.computedData;
      if (this.config.hasAdoptCustomPageSegments) {
        return pages;
      }
      return pages.filter(page => !page.isChildPage);
    },
    isPageOrphaned() {
      const {
        rule
      } = this;
      return rule.page && !rule.isPageShared && !rule.isCustomPage;
    },
    pageListPickerOptions() {
      return getEntitiesByAppSelection(this.rule.app, this.filteredPageList, this.config);
    },
    operatorOptions() {
      return [...SEGMENT_OPERATORS[this.rule.schema.schema]];
    },
    pageTimeRangeOptions() {
      switch (this.rule.operator.value) {
        case 'lastviewed':
          return PAGE_TIME_RANGE_LAST_VIEWED_OPTIONS;
        case 'notviewed':
          return PAGE_TIME_RANGE_NOT_VIEWED_OPTIONS;
        case 'viewed':
        default:
          return PAGE_TIME_RANGE_VIEWED_OPTIONS;
      }
    },
    showNumberInputComponent() {
      return this.rule.operator.value === 'viewed' && (this.rule.time === 'atmost' || this.rule.time === 'atleast');
    },
    showTimeComponent() {
      if (this.showNumberInputComponent) {
        return false;
      }
      const {
        rule
      } = this;
      const operatorValue = get(rule, 'operator.value');
      return !!(rule.page && operatorValue && rule.time && rule.time !== 'ever');
    },
    timeComponent() {
      if (['withinlast', '!withinlast'].includes(this.rule.time)) {
        return getRelativeDateRangeConfig(this.rule, 'value');
      }
      if (['between'].includes(this.rule.time)) {
        return getAbsoluteDateRangeConfig(this.rule);
      }
      if (['before', 'since'].includes(this.rule.time)) {
        return getSingleDateConfig(this.rule, 'value');
      }
      return;
    },
    showTimeSelector() {
      const {
        rule
      } = this;
      return !!rule.page;
    }
  },
  methods: {
    pageTimeLabelObject(timeString) {
      return this.pageTimeRangeOptions.find(opt => opt.value === timeString);
    },
    handlePageOperatorSelection($event) {
      const updates = [{
        field: 'operator',
        value: $event
      }];
      // handle switching to operator where `at least / at most` not supported
      if (this.rule.time === 'atleast' || this.rule.time === 'atmost') {
        if ($event.value === 'notviewed') {
          updates.push({
            field: 'time',
            value: 'ever'
          });
        }
        if ($event.value === 'lastviewed') {
          updates.push({
            field: 'time',
            value: 'before'
          });
        }
      }
      this.$emit('input', updates);
    },
    handlePageTimeSelection($event) {
      const {
        value
      } = $event;
      if (value === 'withinlast' || value === '!withinlast') {
        this.$emit('input', {
          field: 'value',
          value: {
            count: 30,
            granularity: 'days'
          }
        });
      } else if (value !== 'before' && value !== 'since') {
        this.$emit('input', {
          field: 'value',
          value: 1
        });
      }
      this.$emit('input', {
        field: 'time',
        value
      });
    },
    onPageSelect(page) {
      this.$emit('input', [{
        field: 'page',
        value: page
      }, {
        field: 'isPageShared',
        value: true
      }, {
        field: 'pageDeleted',
        value: false
      }]);
    }
  }
};

/* script */
const __vue_script__$b = script$b;

/* template */
var __vue_render__$b = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "page-rule"
  }, [_c('div', {
    class: {
      'page-undefined': _vm.rule.pageDeleted
    }
  }, [_c('pendo-multiselect', {
    staticClass: "page-rule--page-select",
    attrs: {
      "value": _vm.rule.page,
      "allow-empty": false,
      "options": _vm.pageListPickerOptions,
      "searchable": true,
      "disabled": _vm.readOnly,
      "label-key": "displayName",
      "value-key": "id",
      "full-width": "",
      "placeholder": "Select Page"
    },
    on: {
      "select": _vm.onPageSelect
    }
  }), _vm._v(" "), _vm.rule.pageDeleted ? _c('rule-warning', [_vm._v("\n            The original page for this rule has been deleted\n        ")]) : _vm.isPageOrphaned ? _c('rule-warning', [_vm._v("\n            The page used by this rule is no longer being shared.\n        ")]) : _vm._e()], 1), _vm._v(" "), _c('pendo-multiselect', {
    staticClass: "rule-editor--operator",
    attrs: {
      "value": _vm.rule.operator,
      "allow-empty": false,
      "preselect-first": "",
      "full-width": "",
      "value-key": "value",
      "options": _vm.operatorOptions,
      "disabled": !_vm.operatorOptions || _vm.readOnly
    },
    on: {
      "select": _vm.handlePageOperatorSelection
    }
  }), _vm._v(" "), _c('pendo-multiselect', {
    staticClass: "page-rule--page-time-operator",
    attrs: {
      "value": _vm.pageTimeLabelObject(_vm.rule.time),
      "value-key": "value",
      "label-key": "label",
      "min-trigger-width": "100%",
      "allow-empty": false,
      "preselect-first": true,
      "options": _vm.pageTimeRangeOptions,
      "disabled": _vm.readOnly
    },
    on: {
      "select": _vm.handlePageTimeSelection
    }
  }), _vm._v(" "), _vm.showNumberInputComponent ? _c('number-input', {
    staticClass: "rule-editor--item complex-rule number-input--max-height",
    attrs: {
      "labels": {
        suffix: 'times'
      },
      "value": _vm.rule.value,
      "min": 1
    },
    on: {
      "input": function ($event) {
        return _vm.$emit('input', $event);
      }
    }
  }) : _vm._e(), _vm._v(" "), _vm.showTimeComponent ? _c(_vm.timeComponent.component, _vm._b({
    tag: "component",
    on: {
      "input": function ($event) {
        return _vm.$emit('input', $event);
      }
    }
  }, 'component', _vm.timeComponent.props, false)) : _vm._e()], 1);
};
var __vue_staticRenderFns__$b = [];

/* style */
const __vue_inject_styles__$b = function (inject) {
  if (!inject) return;
  inject("data-v-f1d9358a_0", {
    source: ".page-rule[data-v-f1d9358a]{display:contents}.page-rule .page-undefined[data-v-f1d9358a]  .pendo-multiselect__trigger{border:1px solid #cc3340}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$b = "data-v-f1d9358a";
/* module identifier */
const __vue_module_identifier__$b = undefined;
/* functional template */
const __vue_is_functional_template__$b = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$b = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$b,
  staticRenderFns: __vue_staticRenderFns__$b
}, __vue_inject_styles__$b, __vue_script__$b, __vue_scope_id__$b, __vue_is_functional_template__$b, __vue_module_identifier__$b, false, createInjector, undefined, undefined);

//
var script$a = {
  name: 'SimpleValueInputs',
  components: {
    PendoDatePicker,
    PendoInput,
    PendoInputNumber,
    PendoMultiselect
  },
  mixins: [ReadOnlyMixin],
  props: {
    config: {
      type: Object,
      required: true
    },
    operators: {
      type: Object,
      default() {
        return SEGMENT_OPERATORS;
      }
    },
    rule: {
      type: Object,
      required: true
    }
  },
  computed: {
    isDateRangeValue() {
      const {
        operator,
        value
      } = this.rule;
      return value && operator && (operator.value === 'between' || operator.value === '!between');
    },
    isUnitCountValue() {
      const {
        operator,
        value
      } = this.rule;
      return value && operator && (operator.value === 'withinLast' || operator.value === '!withinLast');
    },
    maxDate() {
      const {
        operator
      } = this.rule;
      if (!operator || !operator.label) {
        return null;
      }
      if (operator.label === 'since') {
        return this.yesterday;
      }
      return null;
    },
    valueComponent() {
      const {
        rule
      } = this;
      if (rule.schema.schema === 'poll') {
        return {
          [INPUT_OPTIONS_MAP.PICK_LIST]: 'PendoMultiselect',
          [INPUT_OPTIONS_MAP.FREE_FORM]: 'PendoInput',
          [INPUT_OPTIONS_MAP.NUMBER_SCALE]: 'PendoMultiselect',
          [INPUT_OPTIONS_MAP.POSITIVE_NEGATIVE]: 'PendoMultiselect'
        }[rule.poll.attributes.type];
      }
      return {
        string: 'PendoInput',
        list: 'PendoInput',
        integer: 'PendoInputNumber',
        float: 'PendoInputNumber',
        time: 'PendoDatePicker'
      }[rule.schema.schema];
    },
    valueComponentProps() {
      const {
        rule
      } = this;
      const {
        schema: {
          schema
        }
      } = rule;
      const types = ['PickList', 'PositiveNegative'];
      if (schema === 'poll' && types.includes(rule.poll.attributes.type)) {
        return {
          class: 'simple-value-inputs--value',
          allowEmpty: false,
          fullWidth: true,
          options: map(rule.poll.idResponses, (label, id) => id),
          formatOptionLabel(id) {
            return rule.poll.idResponses[id];
          }
        };
      }
      if (schema === 'poll' && rule.poll.attributes.type === 'NumberScale') {
        return {
          class: 'simple-value-inputs--value',
          allowEmpty: false,
          fullWidth: true,
          options: map(rule.poll.numericResponses, value => parseInt(value))
        };
      }
      const props = {
        time: {
          shortcuts: false,
          class: 'simple-value-inputs--time',
          type: this.isDateRangeValue ? 'range' : 'single',
          minDate: this.config?.data?.appFirstVisit,
          maxDate: this.maxDate
        },
        float: {
          precision: 2
        }
      }[schema];
      return {
        class: 'simple-value-inputs--value',
        disabled: !this.operators[schema] || this.readOnly,
        ...props
      };
    },
    yesterday() {
      return moment(Date.now()).subtract(1, 'day').toDate();
    }
  }
};

/* script */
const __vue_script__$a = script$a;

/* template */
var __vue_render__$a = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "simple-value-inputs"
  }, [!_vm.isUnitCountValue ? _c('div', {
    staticClass: "rule-editor--item"
  }, [_c(_vm.valueComponent, _vm._b({
    tag: "component",
    attrs: {
      "value": _vm.rule.value
    },
    on: {
      "input": function ($event) {
        return _vm.$emit('input', {
          field: 'value',
          value: $event
        });
      }
    }
  }, 'component', _vm.valueComponentProps, false))], 1) : _vm._e(), _vm._v(" "), _vm.isUnitCountValue ? _c('div', {
    staticClass: "rule-editor--item complex-rule"
  }, [_c('pendo-input-number', {
    attrs: {
      "value": _vm.rule.value.count,
      "min": 1,
      "disabled": _vm.readOnly
    },
    on: {
      "change": function ($event) {
        return _vm.$emit('input', {
          field: 'count',
          value: $event
        });
      }
    }
  }), _vm._v(" "), _c('pendo-multiselect', {
    attrs: {
      "value": _vm.rule.value.granularity,
      "allow-empty": false,
      "preselect-first": true,
      "min-trigger-width": 90,
      "min-menu-width": 90,
      "disabled": _vm.readOnly,
      "options": ['days', 'weeks', 'months']
    },
    on: {
      "select": function ($event) {
        return _vm.$emit('input', {
          field: 'granularity',
          value: $event
        });
      }
    }
  })], 1) : _vm._e()]);
};
var __vue_staticRenderFns__$a = [];

/* style */
const __vue_inject_styles__$a = function (inject) {
  if (!inject) return;
  inject("data-v-06ac9292_0", {
    source: ".simple-value-inputs[data-v-06ac9292]{display:contents}.simple-value-inputs--time[data-v-06ac9292]{width:100%}.simple-value-inputs--time>div>span[data-v-06ac9292]{display:block!important}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$a = "data-v-06ac9292";
/* module identifier */
const __vue_module_identifier__$a = undefined;
/* functional template */
const __vue_is_functional_template__$a = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$a = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$a,
  staticRenderFns: __vue_staticRenderFns__$a
}, __vue_inject_styles__$a, __vue_script__$a, __vue_scope_id__$a, __vue_is_functional_template__$a, __vue_module_identifier__$a, false, createInjector, undefined, undefined);

//
var script$9 = {
  name: 'PollRule',
  components: {
    PendoGuideStatusOption,
    PendoMultiselect,
    RuleWarning: __vue_component__$o,
    SimpleValueInputs: __vue_component__$a,
    RelativeDateRange: __vue_component__$r,
    AbsoluteDateRange: __vue_component__$n,
    SingleDate: __vue_component__$q,
    NumberInput: __vue_component__$m
  },
  mixins: [ReadOnlyMixin],
  props: {
    computedData: {
      type: Object,
      required: true
    },
    config: {
      type: Object,
      required: true
    },
    operators: {
      type: Object,
      required: true
    },
    rule: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      pollTimeRangeOptions: POLL_TIME_RANGE_OPTIONS
    };
  },
  computed: {
    pollGuideListPickerOptions() {
      return getEntitiesByAppSelection(this.rule.app, this.computedData.guidesWithPolls, this.config);
    },
    pollOperatorPickerOptions() {
      const options = this.operators[this.rule.schema.schema] || [];
      if (this.rule?.poll?.attributes?.type !== INPUT_OPTIONS_MAP.FREE_FORM) {
        return options.filter(option => option.value !== 'contains' && option.value !== '!contains');
      }
      return options;
    },
    pollQuestionPickerOptions() {
      return getPollsForGuide(this.rule.guide);
    },
    pollTimeLabelObject() {
      return this.pollTimeRangeOptions.find(opt => opt.value === this.rule.time);
    },
    showOperatorField() {
      const {
        rule
      } = this;
      if (!rule.guide || !rule.poll) {
        return false;
      }
      return true;
    },
    showTimeComponent() {
      const {
        rule
      } = this;
      const operatorValue = get(rule, 'operator.value');
      return !!(rule.poll && operatorValue && rule.time && rule.time !== 'ever');
    },
    timeComponent() {
      if (['withinlast', '!withinlast', 'withinnext', '!withinnext'].includes(this.rule.time)) {
        return getRelativeDateRangeConfig(this.rule);
      }
      if (['between'].includes(this.rule.time)) {
        return getAbsoluteDateRangeConfig(this.rule);
      }
      if (['>=', '<='].includes(this.rule.time)) {
        return getSingleDateConfig(this.rule, 'first');
      }
      return;
    },
    showTimeSelector() {
      const {
        rule
      } = this;
      return !!rule.poll;
    },
    showValueField() {
      const {
        rule
      } = this;
      return !!rule.poll && !/^(not)?responded$/.test(rule.operator && rule.operator.value);
    }
  },
  methods: {
    handlePollTimeSelection($event) {
      const {
        value
      } = $event;
      // Clean up fields
      if (value !== 'between') {
        this.$emit('input', {
          field: 'last',
          value: undefined
        });
        if (value !== '>=' && value !== '<=') {
          this.$emit('input', {
            field: 'first',
            value: undefined
          });
        }
      }
      if (value !== 'withinlast' && value !== '!withinlast') {
        const fields = [{
          field: 'count',
          value: undefined
        }, {
          field: 'granularity',
          value: undefined
        }];
        this.$emit('input', fields);
      }
      this.$emit('input', {
        field: 'time',
        value
      });
    },
    onGuideSelect(guide) {
      this.$emit('input', [{
        field: 'guide',
        value: guide
      }, {
        field: 'guideDeleted',
        value: false
      }]);
    }
  }
};

/* script */
const __vue_script__$9 = script$9;

/* template */
var __vue_render__$9 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "poll-rule"
  }, [_c('div', {
    class: {
      'guide-undefined': _vm.rule.guideDeleted
    }
  }, [_c('pendo-multiselect', {
    staticClass: "poll-rule--guide-select",
    attrs: {
      "value": _vm.rule.guide,
      "allow-empty": false,
      "options": _vm.pollGuideListPickerOptions,
      "searchable": true,
      "disabled": _vm.readOnly,
      "label-key": "name",
      "value-key": "id",
      "full-width": "",
      "placeholder": "Select Poll"
    },
    on: {
      "select": _vm.onGuideSelect
    },
    scopedSlots: _vm._u([{
      key: "option",
      fn: function (ref) {
        var option = ref.option;
        return [_c('pendo-guide-status-option', {
          attrs: {
            "tooltip": "",
            "option": option
          }
        })];
      }
    }, {
      key: "selectedLabel",
      fn: function (ref) {
        var option = ref.option;
        return [_c('pendo-guide-status-option', {
          attrs: {
            "tooltip": "",
            "option": option
          }
        })];
      }
    }])
  }), _vm._v(" "), _vm.rule.guideDeleted ? _c('rule-warning', [_vm._v("\n            The original guide for this rule has been deleted\n        ")]) : _vm._e()], 1), _vm._v(" "), _vm.rule.guide ? _c('pendo-multiselect', {
    staticClass: "poll-rule--poll-select",
    attrs: {
      "value": _vm.rule.poll,
      "allow-empty": false,
      "options": _vm.pollQuestionPickerOptions,
      "searchable": true,
      "preselect-first": true,
      "disabled": _vm.readOnly,
      "placeholder": "Select Question",
      "label-key": "question",
      "value-key": "id",
      "full-width": ""
    },
    on: {
      "select": function ($event) {
        return _vm.$emit('input', {
          field: 'poll',
          value: $event
        });
      }
    }
  }) : _vm._e(), _vm._v(" "), _vm.showOperatorField ? _c('pendo-multiselect', {
    staticClass: "rule-editor--operator",
    attrs: {
      "value": _vm.rule.operator,
      "allow-empty": false,
      "preselect-first": true,
      "options": _vm.pollOperatorPickerOptions,
      "disabled": !_vm.operators[_vm.rule.schema.schema] || _vm.readOnly,
      "min-trigger-width": "100%",
      "value-key": "value"
    },
    on: {
      "select": function ($event) {
        return _vm.$emit('input', {
          field: 'operator',
          value: $event
        });
      }
    }
  }) : _vm._e(), _vm._v(" "), _vm.showValueField ? _c('simple-value-inputs', _vm._g({
    attrs: {
      "config": _vm.config,
      "operators": _vm.operators,
      "rule": _vm.rule
    }
  }, _vm.$listeners)) : _vm._e(), _vm._v(" "), _vm.showTimeSelector ? _c('pendo-multiselect', {
    staticClass: "poll-rule--page-time-operator",
    attrs: {
      "value": _vm.pollTimeLabelObject,
      "value-key": "value",
      "label-key": "label",
      "min-trigger-width": "100%",
      "allow-empty": false,
      "preselect-first": true,
      "options": _vm.pollTimeRangeOptions,
      "disabled": _vm.readOnly
    },
    on: {
      "select": _vm.handlePollTimeSelection
    }
  }) : _vm._e(), _vm._v(" "), _vm.showTimeComponent ? _c(_vm.timeComponent.component, _vm._b({
    tag: "component",
    on: {
      "input": function ($event) {
        return _vm.$emit('input', $event);
      }
    }
  }, 'component', _vm.timeComponent.props, false)) : _vm._e()], 1);
};
var __vue_staticRenderFns__$9 = [];

/* style */
const __vue_inject_styles__$9 = function (inject) {
  if (!inject) return;
  inject("data-v-893abb22_0", {
    source: ".poll-rule[data-v-893abb22]{display:contents}.poll-rule .guide-undefined[data-v-893abb22]  .pendo-multiselect__trigger{border:1px solid #cc3340}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$9 = "data-v-893abb22";
/* module identifier */
const __vue_module_identifier__$9 = undefined;
/* functional template */
const __vue_is_functional_template__$9 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$9 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$9,
  staticRenderFns: __vue_staticRenderFns__$9
}, __vue_inject_styles__$9, __vue_script__$9, __vue_scope_id__$9, __vue_is_functional_template__$9, __vue_module_identifier__$9, false, createInjector, undefined, undefined);

//
var script$8 = {
  name: 'ResourceCenterRule',
  components: {
    DisabledSingleOptionSelect: __vue_component__$j,
    OperatorPicker: __vue_component__$k,
    PendoMultiselect,
    RuleWarning: __vue_component__$o
  },
  mixins: [ReadOnlyMixin],
  props: {
    computedData: {
      type: Object,
      required: true
    },
    config: {
      type: Object,
      required: true
    },
    rule: {
      type: Object,
      required: true
    }
  },
  computed: {
    modulesForResourceCenter() {
      const {
        rule
      } = this;
      const {
        app
      } = rule;
      const appId = this.config.usesMultiApp ? get(app, 'id') : this.config.appId;
      const homeViewId = get(this.computedData.draftResourceCentersMap, `${appId}.homeView.id`);
      return this.computedData.rcModules.filter(guide => {
        const resourceCenterId = get(guide, 'attributes.resourceCenter.homeViewId') || get(guide, 'attributes.resourceCenter.originId');
        return resourceCenterId === homeViewId;
      }).map(guide => {
        const isHomeView = get(guide, 'attributes.resourceCenter.isTopLevel');
        return Object.assign(guide, {
          guideId: guide.id,
          // can't be named moduleId because guidesSeenEver source only accepts 'guideId'
          name: isHomeView ? 'Resource Center Home' : guide.name
        });
      });
    },
    showCosmeticEver() {
      const {
        rule
      } = this;
      const operatorValue = get(rule, 'operator.value');
      return ['viewed', 'notviewed'].includes(operatorValue);
    }
  },
  methods: {
    onGuideSelect(guide) {
      this.$emit('input', [{
        field: 'guide',
        value: guide
      }, {
        field: 'guideDeleted',
        value: false
      }]);
    }
  }
};

/* script */
const __vue_script__$8 = script$8;

/* template */
var __vue_render__$8 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "resource-center-rule"
  }, [_c('div', {
    class: {
      'guide-undefined': _vm.rule.guideDeleted
    }
  }, [_c('pendo-multiselect', {
    staticClass: "resource-center-rule--rc-module-select",
    attrs: {
      "value": _vm.rule.guide,
      "allow-empty": false,
      "options": _vm.modulesForResourceCenter,
      "searchable": true,
      "disabled": _vm.readOnly,
      "label-key": "name",
      "value-key": "id",
      "full-width": "",
      "extra-label-width": 60,
      "placeholder": "Select Module"
    },
    on: {
      "select": _vm.onGuideSelect
    }
  }), _vm._v(" "), _vm.rule.guideDeleted ? _c('rule-warning', [_vm._v("\n            The original module for this rule has been deleted\n        ")]) : _vm._e()], 1), _vm._v(" "), _c('operator-picker', _vm._g({
    attrs: {
      "rule": _vm.rule
    }
  }, _vm.$listeners)), _vm._v(" "), _vm.showCosmeticEver ? _c('disabled-single-option-select', {
    attrs: {
      "option": "ever"
    }
  }) : _vm._e()], 1);
};
var __vue_staticRenderFns__$8 = [];

/* style */
const __vue_inject_styles__$8 = function (inject) {
  if (!inject) return;
  inject("data-v-101bb3a2_0", {
    source: ".resource-center-rule[data-v-101bb3a2]{display:contents}.resource-center-rule .guide-undefined[data-v-101bb3a2]  .pendo-multiselect__trigger{border:1px solid #cc3340}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$8 = "data-v-101bb3a2";
/* module identifier */
const __vue_module_identifier__$8 = undefined;
/* functional template */
const __vue_is_functional_template__$8 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$8 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$8,
  staticRenderFns: __vue_staticRenderFns__$8
}, __vue_inject_styles__$8, __vue_script__$8, __vue_scope_id__$8, __vue_is_functional_template__$8, __vue_module_identifier__$8, false, createInjector, undefined, undefined);

function parseSegmentSummary({
  segment,
  appsMap = {},
  guidesMap = {},
  pagesMap = {},
  featuresMap = {},
  workflowsMap = {},
  segmentsMap = {}
} = {}) {
  try {
    if (!segment || segment.id === 'everyone' || segment.id == null) {
      return [[{
        text: 'Everyone in your app will see this'
      }]];
    }
    if (segment.createdByApi) {
      return [[{
        text: 'Visitors contained within an API-managed list'
      }]];
    }
    const {
      filters
    } = segment.definition;
    return filters.reduce((summary, filter) => {
      let rule;
      if (filter.or) {
        rule = filter.or.map(getFilterSummary({
          appsMap,
          guidesMap,
          pagesMap,
          featuresMap,
          workflowsMap,
          segmentsMap
        }));
      } else {
        rule = [getFilterSummary({
          appsMap,
          guidesMap,
          pagesMap,
          featuresMap,
          workflowsMap,
          segmentsMap
        })(filter)];
      }
      return [...summary, rule];
    }, []);
  } catch {
    return [[{
      schema: 'Unknown',
      text: 'Unknown segment'
    }]];
  }
}

// Careful: this is getting used elsewhere outside of this repo; order matters
function getInlineSegmentSummary(segment, guidesMap, pagesMap, featuresMap, workflowsMap, segmentsMap, appsMap) {
  if (!segment || segment.id === 'everyone' || segment.id == null) {
    return 'Everyone';
  }
  const summary = parseSegmentSummary({
    segment,
    appsMap,
    guidesMap,
    pagesMap,
    featuresMap,
    workflowsMap,
    segmentsMap
  });
  return summary.map(rule => {
    if (rule.length > 1) {
      return rule.map(orRule => orRule.text).join(' OR ');
    }
    return rule[0].text;
  }).join(' AND ');
}
function getFilterSummary({
  guidesMap = {},
  pagesMap = {},
  featuresMap = {},
  workflowsMap = {},
  segmentsMap = {},
  appsMap = {}
} = {}) {
  return filter => {
    const {
      name,
      schema,
      operator,
      type
    } = migrateRule(filter);
    if (!operator || !SEGMENT_OPERATORS[schema]) {
      return {
        schema,
        text: 'Unsupported Segment'
      };
    }
    const operatorSchema = schema === 'workflow' && operator === 'notcompleted' ? 'workflowNonRS' : schema;
    const {
      label
    } = findSchemaOperator(operator, operatorSchema);
    const guide = guidesMap[filter.guideId];
    const page = pagesMap[filter.pageId];
    const feature = featuresMap[filter.featureId];
    const workflow = workflowsMap[filter.workflowId];
    const segment = segmentsMap[filter.segmentId];
    const app = appsMap[filter.selectedApp?.id];
    const guideNotFound = /^guide$|^poll$/.test(schema) && !guide;
    const pageNotFound = schema === 'page' && !page;
    const featureNotFound = schema === 'feature' && !feature;
    const workflowNotFound = schema === 'workflow' && !workflow;
    const segmentNotFound = schema === 'segment' && !segment;
    const appNotFound = schema === 'application' && !app;
    if (guideNotFound || pageNotFound || featureNotFound || workflowNotFound || segmentNotFound || appNotFound) {
      const entityId = filter[`${schema}Id`];
      return {
        schema,
        text: getMissingEntityText(schema, entityId)
      };
    }
    if (schema === 'application') {
      const appSentenceInfo = `${capitalize(type)} "${app.displayName}"`;
      const usedNotUsed = label;
      let operatorDescription = filter.time.trim();
      if (filter.time === 'withinlast') {
        operatorDescription = `within last ${filter.value.count} ${filter.value.granularity}`;
      }
      if (filter.time === 'since') {
        operatorDescription = `since ${formatTimeFilterSummary(filter.value)}`;
      }
      return {
        schema,
        text: `${appSentenceInfo} ${usedNotUsed} ${operatorDescription}`
      };
    }
    if (schema === 'segment') {
      return {
        schema,
        id: filter[`${schema}Id`],
        text: `${capitalize(type)} ${label} "${segment.name}"`.trim()
      };
    }
    if (schema === 'guide') {
      const guideSentenceInfo = `${capitalize(type)} "${guide.name}"`;
      const seenNotSeen = label;
      let operatorDescription = filter.time.trim();
      if (filter.time === 'withinlast') {
        operatorDescription = `within last ${filter.count} ${filter.granularity}`;
      }
      if (filter.time === '!withinlast') {
        operatorDescription = `not within last ${filter.count} ${filter.granularity}`;
      }
      if (filter.time === '<=') {
        operatorDescription = `before ${formatTimeFilterSummary(filter.first)}`;
      }
      if (filter.time === '>=') {
        operatorDescription = `since ${formatTimeFilterSummary(filter.first)}`;
      }
      if (filter.time === 'atleast') {
        operatorDescription = `at least ${filter.value} ${pluralize('time', filter.value)}`;
      }
      if (filter.time === 'atmost') {
        operatorDescription = `at most ${filter.value} ${pluralize('time', filter.value)}`;
      }
      if (filter.time === 'between') {
        operatorDescription = `between ${formatTimeFilterSummary(filter.first)} and ${formatTimeFilterSummary(filter.last)}`;
      }
      return {
        schema,
        text: `${guideSentenceInfo} ${seenNotSeen} ${operatorDescription}`.trim()
      };
    }
    if (schema === 'page') {
      const pageSentenceInfo = `${capitalize(type)} "${page.displayName}"`;
      const seenNotSeen = label;
      let operatorDescription = filter.time.trim();
      if (filter.time === 'withinlast') {
        operatorDescription = `within last ${filter.value.count} ${filter.value.granularity}`;
      }
      if (filter.time === '!withinlast') {
        operatorDescription = `not within last ${filter.value.count} ${filter.value.granularity}`;
      }
      if (filter.time === 'since') {
        operatorDescription = `since ${formatTimeFilterSummary(filter.value)}`;
      }
      if (filter.time === 'before') {
        operatorDescription = `before ${formatTimeFilterSummary(filter.value)}`;
      }
      if (filter.time === 'atleast') {
        operatorDescription = `at least ${filter.value} ${pluralize('time', filter.value)}`;
      }
      if (filter.time === 'atmost') {
        operatorDescription = `at most ${filter.value} ${pluralize('time', filter.value)}`;
      }
      if (filter.time === 'between') {
        operatorDescription = `between ${formatTimeFilterSummary(filter.value.first)} and ${formatTimeFilterSummary(filter.value.last)}`;
      }
      return {
        schema,
        text: `${pageSentenceInfo} ${seenNotSeen} ${operatorDescription}`
      };
    }
    if (schema === 'feature') {
      const featureSentenceInfo = `${capitalize(type)} "${feature.displayName}"`;
      const seenNotSeen = label;
      let operatorDescription = filter.time.trim();
      if (filter.time === 'withinlast') {
        operatorDescription = `within last ${filter.value.count} ${filter.value.granularity}`;
      }
      if (filter.time === '!withinlast') {
        operatorDescription = `not within last ${filter.value.count} ${filter.value.granularity}`;
      }
      if (filter.time === 'since') {
        operatorDescription = `since ${formatTimeFilterSummary(filter.value)}`;
      }
      if (filter.time === 'before') {
        operatorDescription = `before ${formatTimeFilterSummary(filter.value)}`;
      }
      if (filter.time === 'atleast') {
        operatorDescription = `at least ${filter.value} ${pluralize('time', filter.value)}`;
      }
      if (filter.time === 'atmost') {
        operatorDescription = `at most ${filter.value} ${pluralize('time', filter.value)}`;
      }
      if (filter.time === 'between') {
        operatorDescription = `between ${formatTimeFilterSummary(filter.value.first)} and ${formatTimeFilterSummary(filter.value.last)}`;
      }
      return {
        schema,
        text: `${featureSentenceInfo} ${seenNotSeen} ${operatorDescription}`
      };
    }
    if (schema === 'poll') {
      const poll = find(guide.polls, {
        id: filter.pollId
      });
      if (!poll) {
        return {
          schema,
          text: getMissingEntityText(schema, filter.pollId)
        };
      }
      const question = get(poll, 'question', 'the poll');
      let responseVal = '';
      if (filter.value != null && !['responded', 'not responded'].includes(label)) {
        responseVal = parseValueFromDefinition(filter.value, 'string');
        responseVal = ` ${poll.idResponses ? poll.idResponses[responseVal] : responseVal}`;
      }
      let timeVal = '';
      switch (filter.time) {
        case '>=':
          {
            timeVal = `since ${formatTimeFilterSummary(filter.first)}`;
            break;
          }
        case '<=':
          {
            timeVal = `before ${formatTimeFilterSummary(filter.first)}`;
            break;
          }
        case 'withinlast':
          {
            timeVal = `within last ${filter.count} ${filter.granularity}`;
            break;
          }
        case '!withinlast':
          {
            timeVal = `not within last ${filter.count} ${filter.granularity}`;
            break;
          }
        case 'between':
          {
            timeVal = `between ${formatTimeFilterSummary({
              first: filter.first,
              last: filter.last
            })}`;
            break;
          }
        default:
          {
            timeVal = 'ever';
          }
      }
      return {
        schema,
        text: `Response to "${question}" ${label}${responseVal} ${timeVal}`.trim()
      };
    }
    if (schema === 'workflow') {
      let operatorDescription = filter.time.trim();
      if (filter.time === 'withinlast') {
        operatorDescription = `within last ${filter.value.count} ${filter.value.granularity}`;
      }
      if (filter.time === 'since') {
        operatorDescription = `since ${formatTimeFilterSummary(filter.value)}`;
      }
      if (filter.time === 'atleast') {
        operatorDescription = `at least ${filter.value} ${pluralize('time', filter.value)}`;
      }
      if (filter.time === 'atmost') {
        operatorDescription = `at most ${filter.value} ${pluralize('time', filter.value)}`;
      }
      return {
        schema,
        text: `${capitalize(type)} "${workflow.name}" ${label} ${operatorDescription}`.trim()
      };
    }
    if (schema === 'guideElement') {
      const sentenceInfo = `Guide Element "${filter.guideElementName ?? filter.guideElementId}"`;
      const seenNotSeen = label;
      let operatorDescription = filter.time.trim();
      if (filter.time === 'withinlast') {
        operatorDescription = `within last ${filter.value.count} ${filter.value.granularity}`;
      }
      if (filter.time === 'since') {
        operatorDescription = `since ${formatTimeFilterSummary(filter.value)}`;
      }
      if (filter.time === 'atleast') {
        operatorDescription = `at least ${filter.value} ${pluralize('time', filter.value)}`;
      }
      if (filter.time === 'atmost') {
        operatorDescription = `at most ${filter.value} ${pluralize('time', filter.value)}`;
      }
      return {
        schema,
        text: `${sentenceInfo} ${seenNotSeen} ${operatorDescription}`
      };
    }
    if (isCsvUploadOperator(operator)) {
      const filename = parseValueFromDefinition(filter.value, schema, operator);
      const operatorDescription = {
        'listContains': 'contained within list',
        '!listContains': 'not contained within list'
      }[operator];
      return {
        schema,
        text: `${name} ${operatorDescription} "${filename}"`.trim()
      };
    }
    const value = getFilterValue(filter);
    let val = parseValueFromDefinition(value, schema, operator);
    if (schema === 'time') {
      val = formatTimeFilterSummary(val);
    }
    if (schema === 'boolean') {
      val = '';
    }
    return {
      schema,
      text: `${name} ${label} ${val}`.trim()
    };
  };
}
function getMissingEntityText(type, id) {
  return `Error: ${type} ${id} not found`;
}
function getFilterValue({
  value,
  granularity,
  count
}) {
  // default segments use a different format?
  if (value == null && granularity != null && count != null) {
    return {
      count: -count,
      granularity
    };
  }
  return value;
}

//
var script$7 = {
  name: 'SegmentSummary',
  components: {
    PendoIcon,
    PendoAlert
  },
  props: {
    segment: {
      type: Object,
      required: false,
      default: null
    },
    showName: {
      type: Boolean,
      default: true
    },
    showLabel: {
      type: Boolean,
      default: true
    },
    guidesMap: {
      type: Object,
      default: () => ({})
    },
    pagesMap: {
      type: Object,
      default: () => ({})
    },
    featuresMap: {
      type: Object,
      default: () => ({})
    },
    workflowsMap: {
      type: Object,
      default: () => ({})
    },
    segmentsMap: {
      type: Object,
      default: () => ({})
    },
    appsMap: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    nestedProps() {
      return {
        ...this.$props,
        segment: null,
        showName: false,
        showLabel: false
      };
    },
    segmentIsEmpty() {
      return isEmpty(this.segment);
    },
    summary() {
      return parseSegmentSummary({
        segment: cloneDeep(this.segment),
        // remove vuex ties
        guidesMap: this.guidesMap,
        pagesMap: this.pagesMap,
        featuresMap: this.featuresMap,
        workflowsMap: this.workflowsMap,
        segmentsMap: this.segmentsMap,
        appsMap: this.appsMap
      });
    }
  },
  methods: {
    getIconFromSchema(schema) {
      let icon = '';
      switch (schema) {
        case 'application':
          icon = 'activity';
          break;
        case 'poll':
          icon = 'list';
          break;
        case 'time':
          icon = 'calendar';
          break;
        case 'page':
          icon = 'page';
          break;
        case 'feature':
          icon = 'feature';
          break;
        case 'workflow':
          icon = 'adopt-workflows';
          break;
        case 'segment':
          icon = 'pie-chart';
          break;
        case 'guideElement':
          icon = 'guideElement';
          break;
        case 'string':
        default:
          icon = 'user';
          break;
      }
      return icon;
    },
    getNestedSegmentById(id) {
      return find(this.segmentsMap, {
        id
      }) || null;
    }
  }
};

/* script */
const __vue_script__$7 = script$7;

/* template */
var __vue_render__$7 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "segment-summary"
  }, [_vm.showName ? _c('div', {
    staticClass: "segment-summary--header"
  }, [_c('span', {
    staticClass: "segment-summary--title"
  }, [_vm._v("\n            Segment\n        ")]), _vm._v(" "), _c('span', {
    staticClass: "segment-summary--header--name"
  }, [_vm._v("\n            " + _vm._s(_vm.segmentIsEmpty ? 'Unknown' : _vm.segment.name) + "\n        ")])]) : _vm._e(), _vm._v(" "), _c('div', {
    staticClass: "segment-summary--rule-set"
  }, [_vm.showLabel ? _c('span', {
    staticClass: "segment-summary--title"
  }, [_vm._v("\n            Rules Summary\n        ")]) : _vm._e(), _vm._v(" "), _vm.segmentIsEmpty ? _c('div', [_c('pendo-alert', {
    attrs: {
      "type": "warning"
    }
  }, [_vm._v("\n                This Segment used by this Guide was deleted. This Guide will not be displayed to any Visitors until\n                the Segment is changed.\n            ")])], 1) : _vm._e(), _vm._v(" "), !_vm.segmentIsEmpty ? _c('div', _vm._l(_vm.summary, function (rule, index) {
    return _c('div', {
      key: "segment-rule-" + index,
      staticClass: "segment-summary--rule"
    }, _vm._l(rule, function (orRule, orIndex) {
      return _c('div', {
        key: "segment-rule-" + index + "-" + orIndex,
        staticClass: "segment-summary--or-rule"
      }, [_c('div', {
        staticClass: "segment-summary--or-rule--summary"
      }, [orRule.schema ? _c('pendo-icon', {
        staticClass: "segment-summary--or-rule--summary--icon",
        attrs: {
          "type": _vm.getIconFromSchema(orRule.schema),
          "size": "12"
        }
      }) : _vm._e(), _vm._v(" "), _c('span', [_vm._v("\n                            " + _vm._s(orRule.text) + "\n                        ")])], 1), _vm._v(" "), orRule.schema === 'segment' ? _c('div', {
        staticClass: "nested-summary"
      }, [_c('segment-summary', _vm._b({
        attrs: {
          "segment": _vm.getNestedSegmentById(orRule.id)
        }
      }, 'segment-summary', _vm.nestedProps, false))], 1) : _vm._e(), _vm._v(" "), orIndex < rule.length - 1 ? _c('div', {
        staticClass: "segment-summary--or-rule--divider"
      }, [_vm._v("\n                        OR\n                    ")]) : _vm._e()]);
    }), 0);
  }), 0) : _vm._e()])]);
};
var __vue_staticRenderFns__$7 = [];

/* style */
const __vue_inject_styles__$7 = function (inject) {
  if (!inject) return;
  inject("data-v-28bb7b94_0", {
    source: ".segment-summary--header{margin-bottom:1em}.segment-summary--title{font-weight:700;display:block;margin-bottom:.5em}.segment-summary--rule{display:flex;flex-flow:column nowrap}.segment-summary--rule::after{content:\"AND\";display:block;margin-top:1em;margin-bottom:1.2em;font-size:.875em}.segment-summary--rule:last-of-type::after{content:\"\";display:none}.segment-summary--or-rule{margin-bottom:.5em}.segment-summary--or-rule:last-of-type{margin-bottom:0}.segment-summary--or-rule--summary{display:flex;flex-flow:row nowrap;align-items:center;margin-bottom:.5em}.segment-summary--or-rule--summary--icon{margin-right:.5em}.segment-summary--or-rule--divider{font-size:.875em;line-height:normal}.segment-summary .nested-summary{margin-left:20px}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$7 = undefined;
/* module identifier */
const __vue_module_identifier__$7 = undefined;
/* functional template */
const __vue_is_functional_template__$7 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$7 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$7,
  staticRenderFns: __vue_staticRenderFns__$7
}, __vue_inject_styles__$7, __vue_script__$7, __vue_scope_id__$7, __vue_is_functional_template__$7, __vue_module_identifier__$7, false, createInjector, undefined, undefined);

//
var script$6 = {
  name: 'SegmentRule',
  components: {
    OperatorPicker: __vue_component__$k,
    PendoCollapse,
    PendoIcon,
    PendoMultiselect,
    PendoMultiselectOption,
    RuleWarning: __vue_component__$o,
    SegmentSummary: __vue_component__$7
  },
  directives: {
    PendoTooltip
  },
  mixins: [ReadOnlyMixin],
  props: {
    computedData: {
      type: Object,
      required: true
    },
    rule: {
      type: Object,
      required: true
    },
    segment: {
      type: Object,
      required: false,
      default: () => null
    }
  },
  data() {
    return {
      activePanel: null,
      panels: [{
        name: 'rules',
        title: 'rules',
        titleSlotName: 'rulesTitle'
      }]
    };
  },
  computed: {
    segmentPickerOptions() {
      const appendSegmentOptionMetadata = option => {
        let tooltipContent = '';
        if (!option.shared) {
          tooltipContent = 'Segment cannot be selected: Visibility set to Only Me';
        } else if (option.compound) {
          tooltipContent = 'Segment cannot be selected: Segment is already referenced by another segment';
        } else if (!option.definition?.filters?.length) {
          tooltipContent = 'Segment cannot be selected: A segment with no rules cannot be nested';
        }
        return {
          disabled: !!tooltipContent,
          tooltipContent
        };
      };
      return this.computedData.segments.filter(segment => this.segment?.id !== segment.id).map(segment => {
        return {
          ...segment,
          ...appendSegmentOptionMetadata(segment)
        };
      });
    },
    isSegmentOrphaned() {
      const {
        rule
      } = this;
      return rule.segment && !rule.isSegmentShared;
    },
    ruleCount() {
      const {
        filters
      } = this.rule?.segment?.definition || {};
      if (!filters) {
        return 0;
      }
      return filters.reduce((count, filter) => count + (filter.or?.length || 1), 0);
    },
    ruleCountText() {
      return `(Includes ${this.ruleCount} ${pluralize('rule', this.ruleCount)})`;
    }
  },
  created() {
    if (this.isSegmentInvalid(this.rule.segment)) {
      this.onSegmentSelect(this.rule.segment);
    }
  },
  methods: {
    isSegmentInvalid(segment) {
      if (!segment) {
        return false;
      }
      return getRulesFromSegment(segment).some(orRules => {
        return orRules.some(rule => {
          return ['guide', 'workflow', 'feature', 'page', 'rcModule'].some(type => {
            const entityType = type === 'rcModule' ? 'guide' : type;
            const idType = type === 'rcModule' ? 'resourceCenter' : type;
            if (rule[`${entityType}Id`] && rule.schema.id === idType) {
              const entity = this.computedData[`${type}sMap`][rule[`${entityType}Id`]];
              return !entity;
            }
            return false;
          });
        });
      });
    },
    onSegmentSelect(segment) {
      this.$emit('input', [{
        field: 'segment',
        value: segment
      }, {
        field: 'isSegmentShared',
        value: true
      }, {
        field: 'segmentDeleted',
        value: false
      }, {
        field: 'segmentInvalid',
        value: this.isSegmentInvalid(segment)
      }]);
    }
  }
};

/* script */
const __vue_script__$6 = script$6;

/* template */
var __vue_render__$6 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "segment-rule"
  }, [_c('operator-picker', _vm._g({
    attrs: {
      "rule": _vm.rule
    }
  }, _vm.$listeners)), _vm._v(" "), _c('div', {
    class: {
      'segment-undefined': _vm.rule.segmentDeleted
    }
  }, [_c('pendo-multiselect', {
    staticClass: "segment-rule--segment-select",
    attrs: {
      "value": _vm.rule.segment,
      "allow-empty": false,
      "options": _vm.segmentPickerOptions,
      "searchable": true,
      "disabled": _vm.readOnly,
      "label-key": "name",
      "value-key": "id",
      "full-width": "",
      "placeholder": "Select Segment"
    },
    on: {
      "select": _vm.onSegmentSelect
    },
    scopedSlots: _vm._u([{
      key: "option",
      fn: function (ref) {
        var option = ref.option;
        var index = ref.index;
        return [_c('pendo-multiselect-option', {
          directives: [{
            name: "pendo-tooltip",
            rawName: "v-pendo-tooltip",
            value: {
              content: option.tooltipContent,
              placement: 'top',
              arrow: true,
              multiline: true
            },
            expression: "{\n                        content: option.tooltipContent,\n                        placement: 'top',\n                        arrow: true,\n                        multiline: true\n                    }"
          }],
          attrs: {
            "option": option,
            "index": index
          }
        })];
      }
    }])
  }), _vm._v(" "), _vm.rule.segmentDeleted ? _c('rule-warning', [_vm._v("\n            The original segment for this rule has been deleted.\n        ")]) : _vm.isSegmentOrphaned ? _c('rule-warning', [_vm._v("\n            The segment used by this rule is no longer being shared.\n        ")]) : _vm.rule.segmentInvalid ? _c('rule-warning', [_vm._v("\n            The segment used by this rule is invalid.\n        ")]) : _vm._e()], 1), _vm._v(" "), _vm.rule.segment && !_vm.rule.segmentInvalid ? _c('div', {
    staticClass: "segment-summary"
  }, [_c('pendo-collapse', {
    attrs: {
      "panels": _vm.panels
    },
    scopedSlots: _vm._u([{
      key: "rulesTitle",
      fn: function () {
        return [_c('pendo-icon', {
          staticClass: "segment-link-icon",
          attrs: {
            "type": "link",
            "size": "12"
          }
        }), _vm._v(" "), _c('span', {
          staticClass: "segment-link-text"
        }, [_vm._v(_vm._s(_vm.rule.segment.name))]), _vm._v(" "), _c('span', {
          staticClass: "rule-count"
        }, [_vm._v(_vm._s(_vm.ruleCountText))])];
      },
      proxy: true
    }, {
      key: "rules",
      fn: function () {
        return [_c('segment-summary', {
          attrs: {
            "data-cy": "segment-card-segment-summary",
            "show-name": false,
            "show-label": false,
            "segment": _vm.rule.segment,
            "guides-map": _vm.computedData.guidesMap,
            "pages-map": _vm.computedData.pagesMap,
            "features-map": _vm.computedData.featuresMap,
            "workflows-map": _vm.computedData.workflowsMap,
            "segments-map": _vm.computedData.segmentsMap,
            "apps-map": _vm.computedData.appsMap
          }
        })];
      },
      proxy: true
    }], null, false, 123791548),
    model: {
      value: _vm.activePanel,
      callback: function ($$v) {
        _vm.activePanel = $$v;
      },
      expression: "activePanel"
    }
  })], 1) : _vm._e()], 1);
};
var __vue_staticRenderFns__$6 = [];

/* style */
const __vue_inject_styles__$6 = function (inject) {
  if (!inject) return;
  inject("data-v-72fa3045_0", {
    source: ".segment-rule[data-v-72fa3045]{display:contents}.segment-rule .segment-undefined[data-v-72fa3045]  .pendo-multiselect__trigger{border:1px solid #cc3340}.segment-summary[data-v-72fa3045]{grid-column:1/span 3}.segment-summary .segment-link[data-v-72fa3045]{display:flex;align-items:center;overflow:hidden;text-overflow:ellipsis}.segment-summary .segment-link-icon[data-v-72fa3045]{margin-right:5px}.segment-summary .segment-link-text[data-v-72fa3045]{white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.segment-summary .rule-count[data-v-72fa3045]{color:#6a6c75;white-space:nowrap}[data-v-72fa3045] .pendo-collapse-item__header{padding:0;border:0;height:32px;line-height:32px;grid-gap:4px;justify-content:start}[data-v-72fa3045] .pendo-collapse-item__content{padding-bottom:0;padding-top:12px}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$6 = "data-v-72fa3045";
/* module identifier */
const __vue_module_identifier__$6 = undefined;
/* functional template */
const __vue_is_functional_template__$6 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$6 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$6,
  staticRenderFns: __vue_staticRenderFns__$6
}, __vue_inject_styles__$6, __vue_script__$6, __vue_scope_id__$6, __vue_is_functional_template__$6, __vue_module_identifier__$6, false, createInjector, undefined, undefined);

//
var script$5 = {
  name: 'WorkflowRule',
  components: {
    PendoIcon,
    PendoMultiselect,
    PendoPopover,
    RuleWarning: __vue_component__$o,
    RelativeDateRange: __vue_component__$r,
    SingleDate: __vue_component__$q,
    NumberInput: __vue_component__$m
  },
  mixins: [ReadOnlyMixin],
  props: {
    computedData: {
      type: Object,
      required: true
    },
    rule: {
      type: Object,
      required: true
    },
    config: {
      type: Object,
      required: true
    }
  },
  computed: {
    operatorOptions() {
      return [...SEGMENT_OPERATORS[this.config.showWorkflowRicherSegmentation ? this.rule.schema.schema : 'workflowNonRS']];
    },
    workflowOptions() {
      return this.computedData.workflows.filter(workflow => {
        if (workflow.workflowSteps.length !== 2) {
          return false;
        }
        return !!this.computedData[`${workflow.workflowSteps[0].countableKind.toLowerCase()}sMap`][workflow.workflowSteps[0].countableId] && !!this.computedData[`${workflow.workflowSteps[1].countableKind.toLowerCase()}sMap`][workflow.workflowSteps[1].countableId];
      }).sort((a, b) => {
        const nameA = a.name.toUpperCase();
        const nameB = b.name.toUpperCase();
        if (nameA < nameB) {
          return -1;
        }
        return nameA > nameB ? 1 : 0;
      });
    },
    workflowTimeRangeOptions() {
      return this.rule.operator.value === 'completed' || this.rule.operator.value === 'incomplete' ? WORKFLOW_TIME_RANGE_COMPLETED_INCOMPLETE_OPTIONS : WORKFLOW_TIME_RANGE_NOT_STARTED_OPTIONS;
    },
    showValueComponent() {
      return this.config.showWorkflowRicherSegmentation && this.rule.time !== 'ever' && this.rule.time;
    },
    valueComponent() {
      switch (this.rule.time) {
        case 'atleast':
        case 'atmost':
          return getNumberConfig(this.rule, 'value');
        case 'since':
          return getSingleDateConfig(this.rule, 'value');
        case 'withinlast':
          return getRelativeDateRangeConfig(this.rule, 'value');
        default:
          return;
      }
    }
  },
  created() {
    if (this.config.showWorkflowRicherSegmentation && !this.rule.value) {
      if (this.config.workflowWithinLastDateRange) {
        this.handleWorkflowTimeSelection({
          label: 'within last',
          value: 'withinlast'
        });
      }
      const incompleteOption = this.operatorOptions.find(opt => opt.value === 'incomplete');
      this.handleProgressOperatorSelection(incompleteOption);
    }
  },
  methods: {
    handleProgressOperatorSelection($event) {
      const updates = [{
        field: 'operator',
        value: $event
      }];
      // handle switching to `not started` where `at least / at most` not supported
      if (this.config.showWorkflowRicherSegmentation && $event.value === 'notStarted' && (this.rule.time === 'atleast' || this.rule.time === 'atmost')) {
        updates.push({
          field: 'time',
          value: 'ever'
        });
      }
      this.$emit('input', updates);
    },
    onWorkflowSelect(workflow) {
      this.$emit('input', [{
        field: 'workflow',
        value: workflow
      }, {
        field: 'workflowDeleted',
        value: false
      }]);
    },
    workflowTimeLabelObject(timeString) {
      return this.workflowTimeRangeOptions.find(opt => opt.value === timeString);
    },
    handleWorkflowTimeSelection($event) {
      const {
        value
      } = $event;

      // only 'time' type to require value as an array
      if (value === 'withinlast') {
        const globalDateRange = this.config.workflowWithinLastDateRange;
        this.$emit('input', {
          field: 'value',
          value: {
            count: globalDateRange || 30,
            granularity: 'days'
          }
        });
      } else {
        this.$emit('input', {
          field: 'value',
          value: 1
        });
      }
      this.$emit('input', {
        field: 'time',
        value
      });
    },
    handleTimeDataChange($event) {
      const {
        field,
        value
      } = $event;
      if (this.rule.time === 'withinlast') {
        const unmodifiedField = field === 'count' ? 'granularity' : 'count';
        this.$emit('input', {
          field: 'value',
          value: {
            [field]: value,
            [unmodifiedField]: this.rule.value[unmodifiedField]
          }
        });
      } else {
        this.$emit('input', $event);
      }
    }
  }
};

/* script */
const __vue_script__$5 = script$5;

/* template */
var __vue_render__$5 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "workflow-rule"
  }, [_c('div', {
    class: {
      'workflow-undefined': _vm.rule.workflowDeleted
    }
  }, [_c('pendo-multiselect', {
    staticClass: "workflow-rule--workflow-select",
    attrs: {
      "value": _vm.rule.workflow,
      "allow-empty": false,
      "options": _vm.workflowOptions,
      "searchable": true,
      "disabled": _vm.readOnly,
      "label-key": "name",
      "value-key": "id",
      "full-width": "",
      "placeholder": "Select Workflow"
    },
    on: {
      "select": _vm.onWorkflowSelect
    }
  }), _vm._v(" "), _vm.rule.workflowDeleted ? _c('rule-warning', [_vm._v("\n            The original workflow for this rule has been deleted\n        ")]) : _vm._e()], 1), _vm._v(" "), _c('pendo-multiselect', {
    staticClass: "rule-editor--operator",
    attrs: {
      "value": _vm.rule.operator,
      "allow-empty": false,
      "full-width": "",
      "value-key": "value",
      "options": _vm.operatorOptions,
      "disabled": !_vm.operatorOptions || _vm.readOnly,
      "placeholder": "Select Usage"
    },
    on: {
      "select": _vm.handleProgressOperatorSelection
    },
    scopedSlots: _vm._u([{
      key: "suffix",
      fn: function (ref) {
        var open = ref.open;
        return [_c('pendo-icon', {
          staticClass: "pendo-multiselect__caret",
          class: {
            'is-active': open
          },
          attrs: {
            "type": "chevron-down",
            "size": "14"
          }
        }), _vm._v(" "), _c('pendo-popover', {
          attrs: {
            "arrow": "",
            "offset": "14"
          },
          scopedSlots: _vm._u([{
            key: "popover",
            fn: function () {
              return [_c('div', {
                staticClass: "info-tooltip-body"
              }, [_c('p', [_c('strong', [_vm._v("Completed:")]), _vm._v(" Visitors who have completed this workflow.")]), _vm._v(" "), _vm.config.showWorkflowRicherSegmentation ? _c('p', [_c('strong', [_vm._v("Incomplete:")]), _vm._v(" Visitors who did not complete the workflow.\n                        ")]) : _vm._e(), _vm._v(" "), _vm.config.showWorkflowRicherSegmentation ? _c('p', [_c('strong', [_vm._v("Not Started:")]), _vm._v(" Visitors that did not start the workflow.\n                        ")]) : _vm._e(), _vm._v(" "), !_vm.config.showWorkflowRicherSegmentation ? _c('p', [_c('strong', [_vm._v("Incomplete or Not Started:")]), _vm._v(" Visitors who did not complete the workflow\n                            (Incomplete) or visitors that did not start the workflow (Not Started).\n                        ")]) : _vm._e()])];
            },
            proxy: true
          }], null, true)
        }, [_c('div', {
          staticClass: "info-tooltip"
        }, [_c('pendo-icon', {
          attrs: {
            "size": "14",
            "type": "info"
          }
        })], 1)])];
      }
    }])
  }), _vm._v(" "), _vm.config.showWorkflowRicherSegmentation ? _c('pendo-multiselect', {
    staticClass: "worflow-rule--workflow-time-operator",
    attrs: {
      "value": _vm.workflowTimeLabelObject(_vm.rule.time),
      "value-key": "value",
      "label-key": "label",
      "min-trigger-width": "100%",
      "allow-empty": false,
      "options": _vm.workflowTimeRangeOptions,
      "disabled": _vm.readOnly
    },
    on: {
      "select": _vm.handleWorkflowTimeSelection
    }
  }) : _vm._e(), _vm._v(" "), _vm.showValueComponent ? _c(_vm.valueComponent.component, _vm._b({
    tag: "component",
    staticClass: "rule-editor--item complex-rule",
    on: {
      "input": _vm.handleTimeDataChange
    }
  }, 'component', _vm.valueComponent.props, false)) : _vm._e()], 1);
};
var __vue_staticRenderFns__$5 = [];

/* style */
const __vue_inject_styles__$5 = function (inject) {
  if (!inject) return;
  inject("data-v-05f90ac0_0", {
    source: ".workflow-rule[data-v-05f90ac0]{display:contents}.workflow-rule .workflow-undefined[data-v-05f90ac0]  .pendo-multiselect__trigger{border:1px solid #cc3340}.info-tooltip[data-v-05f90ac0]{align-items:center;display:flex;height:100%;margin:0 8px}.info-tooltip-body[data-v-05f90ac0]{padding:16px;width:286px}.info-tooltip-body *[data-v-05f90ac0]{font-size:14px}.info-tooltip-body[data-v-05f90ac0] :first-child{margin-top:0}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$5 = "data-v-05f90ac0";
/* module identifier */
const __vue_module_identifier__$5 = undefined;
/* functional template */
const __vue_is_functional_template__$5 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$5 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$5,
  staticRenderFns: __vue_staticRenderFns__$5
}, __vue_inject_styles__$5, __vue_script__$5, __vue_scope_id__$5, __vue_is_functional_template__$5, __vue_module_identifier__$5, false, createInjector, undefined, undefined);

const DEFAULT_TIMESERIES = {
  period: 'dayRange',
  first: 'now()',
  count: -30
};
const DEFAULT_EXCLUDELIST = 'apply';
const DEFAULT_LANGUAGE_CODE = null;
const DEFAULT_INCLUDE_ANONYMOUS = 'visitorId';

//

// Combines all selections under single value key for now
const SINGLE_VALUE_SCHEMAS = ['time', 'feature', 'page', 'guideElement', 'application'];
var script$4 = {
  name: 'RuleEditor',
  components: {
    ApplicationRule: __vue_component__$p,
    FeatureRule: __vue_component__$l,
    GuideRule: __vue_component__$i,
    GuideElementRule: __vue_component__$h,
    MetadataRule: __vue_component__$c,
    PageRule: __vue_component__$b,
    PendoButton,
    PendoIconOption,
    PendoMultiselect,
    PollRule: __vue_component__$9,
    SegmentRule: __vue_component__$6,
    ResourceCenterRule: __vue_component__$8,
    WorkflowRule: __vue_component__$5,
    PendoAppDisplay
  },
  directives: {
    PendoTooltip
  },
  mixins: [ReadOnlyMixin],
  props: {
    config: {
      type: Object,
      required: true
    },
    computedData: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    operators: {
      type: Object,
      required: true
    },
    removable: {
      type: Boolean,
      default: false
    },
    rule: {
      type: Object,
      required: true
    },
    segment: {
      type: Object,
      required: false,
      default: () => null
    },
    hasParentSegments: {
      type: Boolean,
      required: true
    },
    nestedSegmentCount: {
      type: Number,
      required: true
    }
  },
  computed: {
    appOptionsForAppUsage() {
      return this.computedData.appOptions.filter(app => !!app.id);
    },
    appOptionsList() {
      if (this.ruleComponent === 'application-rule') {
        return this.appOptionsForAppUsage;
      }
      const defaultOption = [{
        displayName: 'All Applications',
        id: null,
        resourceCenterId: null
      }];
      const groupedOptions = [{
        label: 'default',
        options: defaultOption
      }, {
        label: 'divider',
        options: this.appOptionsForAppUsage
      }];
      return groupedOptions;
    },
    filters() {
      return {
        timeSeries: this.config.filters?.timeSeries || DEFAULT_TIMESERIES,
        includeAnonymous: this.config.filters?.includeAnonymous || DEFAULT_INCLUDE_ANONYMOUS,
        blacklist: this.config.filters?.blacklist || DEFAULT_EXCLUDELIST,
        languageCode: this.config.filters?.languageCode || DEFAULT_LANGUAGE_CODE
      };
    },
    groupedSchemaList() {
      const productUsage = ['guide', 'guideElement', 'resourceCenter', 'page', 'feature', 'poll', 'segment'];
      if (this.config.usesMultiApp) {
        productUsage.push('application');
      }
      if (this.config.hasWorkflow) {
        productUsage.push('workflow');
      }
      if (Array.isArray(this.computedData.filteredSchemaList)) {
        let productUsageFields = this.computedData.filteredSchemaList.filter(schema => {
          return productUsage.includes(schema.id);
        });
        productUsageFields = this.addSegmentLimitTooltip(productUsageFields);
        const metaFields = this.computedData.filteredSchemaList.filter(schema => {
          return !productUsage.includes(schema.id);
        });
        return [{
          id: 'product-usage-header',
          label: 'Product Usage',
          options: productUsageFields
        }, {
          id: 'visitor-data-header',
          label: 'Visitor Data',
          options: metaFields
        }];
      }
      return [];
    },
    preselectFirstApp() {
      return this.ruleComponent !== 'application-rule';
    },
    resourceCenterAppOptions() {
      return this.computedData.appOptions.filter(app => !!app.resourceCenterId);
    },
    ruleComponent() {
      const {
        rule
      } = this;

      // enable these types as we separate them out
      const {
        schema,
        id
      } = rule.schema;
      switch (schema) {
        case 'guide':
          if (id === 'resourceCenter') {
            return 'resource-center-rule';
          }
          return 'guide-rule';
        case 'poll':
          return 'poll-rule';
        case 'guideElement':
          return 'guide-element-rule';
        case 'page':
          return 'page-rule';
        case 'feature':
          return 'feature-rule';
        case 'segment':
          return 'segment-rule';
        case 'application':
          return 'application-rule';
        case 'workflow':
          return 'workflow-rule';
        case 'aggregation':
          return null;
        default:
          return 'metadata-rule';
      }
    },
    showAppChooser() {
      const {
        ruleComponent,
        config
      } = this;
      if (!config.usesMultiApp) {
        return false;
      }
      const ruleComponentsWithAppSelections = ['guide-rule', 'guide-element-rule', 'page-rule', 'feature-rule', 'poll-rule', 'application-rule'];
      return ruleComponentsWithAppSelections.includes(ruleComponent);
    },
    showRCAppChooser() {
      if (!this.config.usesMultiApp) {
        return false;
      }
      return this.ruleComponent === 'resource-center-rule';
    }
  },
  methods: {
    onAppSelect(selectedApp) {
      const {
        rule
      } = this;
      const existingAppId = get(rule, 'app.id');
      if (selectedApp.id === existingAppId) {
        return;
      }
      const updates = {};
      const baseProperties = ['app', 'id', 'appDeleted', 'schema'];
      Object.keys(rule).forEach(prop => {
        if (baseProperties.includes(prop)) {
          return;
        }
        updates[prop] = undefined;
      });
      const validOperators = this.operators[rule.schema.schema] || [];
      updates.operator = validOperators[0];
      if (this.ruleComponent === 'application-rule') {
        updates.time = null;
      }
      updates.app = selectedApp;

      // Technically this probably isn't needed, but we'll do it for uniformity
      updates.appDeleted = false;
      this.$emit('input', updates);
    },
    onSchemaSelect(schema) {
      // if schema isn't the same, build a new rule with correct defaults
      if (schema.id !== this.rule.schema.id) {
        this.$emit('input', generateRule(this.operators, schema));
      } else {
        this.$emit('input', {
          schema
        });
      }
    },
    onRuleUpdate($event) {
      const {
        rule
      } = this;
      const {
        schema
      } = rule.schema;
      const updates = {};
      const changes = Array.isArray($event) ? $event : [$event];
      changes.forEach(({
        field,
        value
      }) => {
        switch (field) {
          case 'operator':
            Object.assign(updates, this.onOperatorChange(value));
            break;
          case 'count':
          case 'granularity':
          case 'first':
          case 'last':
            if (SINGLE_VALUE_SCHEMAS.includes(schema)) {
              updates.value = {
                ...rule.value,
                [field]: value
              };
            } else {
              updates[field] = value;
            }
            break;
          case 'tag':
            updates.tagId = value.tagId;
            updates.value = value.filename;
            this.$emit('update:showCsvUploadWarning', !!value.tagId);
            break;
          case 'time':
            updates.time = value;
            break;
          default:
            updates[field] = value;
            break;
        }
      });
      this.$emit('input', updates);
    },
    onOperatorChange(operator) {
      const {
        rule
      } = this;
      const {
        value,
        operator: previousOperator,
        schema
      } = rule;
      const updates = {};
      const wasCsvOperator = isCsvUploadOperator(previousOperator.value);
      const isCsvOperator = isCsvUploadOperator(operator.value);
      const shouldRestoreOriginalSchemaType = wasCsvOperator && !isCsvOperator;
      updates.operator = operator;
      if (operator.multivalue) {
        const value = operator.value === 'between' ? {
          first: null,
          last: null
        } : {
          count: 30,
          granularity: 'days'
        };
        updates.value = value;
      } else if (!operator.multivalue && previousOperator.multivalue) {
        updates.value = null;
      } else if (schema.schema === 'boolean') {
        updates.value = true;
      } else {
        updates.value = value || getDefaultValueForSchema(schema);
      }
      if (shouldRestoreOriginalSchemaType) {
        const {
          type
        } = this.getSchemaById(schema.id);
        if (type) {
          updates.schema = {
            ...schema,
            type
          };
        }
      }
      return updates;
    },
    addSegmentLimitTooltip(schemaOptions) {
      return schemaOptions.map(schema => {
        if (schema.id === 'segment' && this.nestedSegmentCount >= SEGMENT_RULE_LIMIT) {
          return {
            ...schema,
            disabled: true,
            tooltipContent: "You've reached the limit of segments you can use"
          };
        }
        if (schema.id === 'segment' && this.hasParentSegments) {
          return {
            ...schema,
            disabled: true,
            tooltipContent: 'Segments that are already nested cannot contain nested segment rules'
          };
        }
        return schema;
      });
    },
    getSchemaById(id) {
      if (Array.isArray(this.computedData.filteredSchemaList)) {
        return this.computedData.filteredSchemaList.find(schema => schema.id === id);
      }
      return undefined;
    }
  }
};

/* script */
const __vue_script__$4 = script$4;

/* template */
var __vue_render__$4 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "rule-editor"
  }, [_vm.removable && _vm.index === 0 && !_vm.readOnly ? _c('pendo-button', {
    staticClass: "rule-editor--remove",
    class: {
      'rule-editor--first-rule': _vm.index === 0
    },
    attrs: {
      "icon": "x",
      "type": "tertiary",
      "size": "mini"
    },
    on: {
      "click": function ($event) {
        return _vm.$emit('remove');
      }
    }
  }) : _vm._e(), _vm._v(" "), _vm.removable && _vm.index !== 0 ? _c('div', {
    staticClass: "rule-editor--or-separator"
  }, [_c('span', {
    staticClass: "rule-editor--or-separator--text"
  }, [_vm._v("\n            OR\n        ")]), _vm._v(" "), !_vm.readOnly ? _c('pendo-button', {
    staticClass: "rule-editor--remove",
    attrs: {
      "icon": "x",
      "type": "tertiary",
      "size": "mini"
    },
    on: {
      "click": function ($event) {
        return _vm.$emit('remove');
      }
    }
  }) : _vm._e()], 1) : _vm._e(), _vm._v(" "), _c('div', {
    staticClass: "rule-editor--items"
  }, [_c('pendo-multiselect', {
    staticClass: "rule-editor--schema",
    attrs: {
      "value": _vm.rule.schema,
      "allow-empty": false,
      "options": _vm.groupedSchemaList,
      "disabled": _vm.readOnly,
      "full-width": "",
      "label-key": "name",
      "value-key": "id"
    },
    on: {
      "select": _vm.onSchemaSelect
    },
    scopedSlots: _vm._u([{
      key: "optionGroup",
      fn: function (ref) {
        var option = ref.option;
        return [option.id === 'product-usage-header' ? _c('div', {
          staticClass: "group-header-label"
        }, [_vm._v("\n                    " + _vm._s(option.label) + "\n                ")]) : _c('div', {
          staticClass: "group-header-label"
        }, [_vm._v("\n                    " + _vm._s(option.label) + " (" + _vm._s(option.options.length) + ")\n                ")])];
      }
    }, {
      key: "option",
      fn: function (ref) {
        var option = ref.option;
        return [_c('pendo-icon-option', {
          directives: [{
            name: "pendo-tooltip",
            rawName: "v-pendo-tooltip",
            value: {
              content: option.tooltipContent,
              placement: 'top',
              arrow: true
            },
            expression: "{\n                        content: option.tooltipContent,\n                        placement: 'top',\n                        arrow: true\n                    }"
          }],
          attrs: {
            "option": option
          }
        })];
      }
    }, {
      key: "selectedLabel",
      fn: function (ref) {
        var option = ref.option;
        return [_c('pendo-icon-option', {
          attrs: {
            "option": option
          }
        })];
      }
    }])
  }), _vm._v(" "), _vm.showAppChooser ? _c('pendo-multiselect', {
    staticClass: "rule-editor--app-select",
    attrs: {
      "value": _vm.rule.app,
      "allow-empty": false,
      "options": _vm.appOptionsList,
      "searchable": true,
      "disabled": _vm.readOnly,
      "preselect-first": _vm.preselectFirstApp,
      "label-key": "displayName",
      "value-key": "id",
      "full-width": "",
      "placeholder": "Select Application"
    },
    on: {
      "select": _vm.onAppSelect
    },
    scopedSlots: _vm._u([{
      key: "option",
      fn: function (ref) {
        var option = ref.option;
        return [_c('pendo-app-display', {
          attrs: {
            "show-app-icons": !!option.id && _vm.config.showAppIcons,
            "apps": option
          }
        })];
      }
    }, {
      key: "selectedLabel",
      fn: function (ref) {
        var option = ref.option;
        return [_c('pendo-app-display', {
          attrs: {
            "show-app-icons": !!option.id && _vm.config.showAppIcons,
            "apps": option
          }
        })];
      }
    }], null, false, 2010321125)
  }) : _vm._e(), _vm._v(" "), _vm.showRCAppChooser ? _c('pendo-multiselect', {
    staticClass: "rule-editor--app-select",
    attrs: {
      "value": _vm.rule.app,
      "allow-empty": false,
      "options": _vm.resourceCenterAppOptions,
      "searchable": true,
      "disabled": _vm.readOnly,
      "label-key": "displayName",
      "value-key": "id",
      "full-width": "",
      "placeholder": "Select Application"
    },
    on: {
      "select": _vm.onAppSelect
    },
    scopedSlots: _vm._u([{
      key: "option",
      fn: function (ref) {
        var option = ref.option;
        return [_c('pendo-app-display', {
          attrs: {
            "apps": option,
            "show-app-icons": _vm.config.showAppIcons
          }
        })];
      }
    }, {
      key: "selectedLabel",
      fn: function (ref) {
        var option = ref.option;
        return [_c('pendo-app-display', {
          attrs: {
            "apps": option,
            "show-app-icons": _vm.config.showAppIcons
          }
        })];
      }
    }], null, false, 3084612997)
  }) : _vm._e(), _vm._v(" "), _c(_vm.ruleComponent, {
    tag: "component",
    attrs: {
      "computed-data": _vm.computedData,
      "config": _vm.config,
      "operators": _vm.operators,
      "rule": _vm.rule,
      "segment": _vm.segment,
      "filters": _vm.filters
    },
    on: {
      "input": _vm.onRuleUpdate
    }
  })], 1)], 1);
};
var __vue_staticRenderFns__$4 = [];

/* style */
const __vue_inject_styles__$4 = function (inject) {
  if (!inject) return;
  inject("data-v-4c39d80b_0", {
    source: ".rule-editor--item.complex-rule{display:grid;grid-template-columns:auto auto;grid-gap:16px}.rule-editor--remove svg{stroke:#6a6c75}.rule-editor--remove:hover{cursor:pointer}",
    map: undefined,
    media: undefined
  }), inject("data-v-4c39d80b_1", {
    source: ".rule-editor--items[data-v-4c39d80b]{display:grid;grid-template-columns:repeat(auto-fill,minmax(160px,1fr));grid-gap:16px;margin-bottom:1.5em}.rule-editor--or-separator[data-v-4c39d80b]{display:flex;flex-flow:row nowrap;justify-content:space-between;align-items:center;margin-bottom:1.5em;border-top:1px solid #dadce5;padding-top:1.5em}.rule-editor--or-separator--text[data-v-4c39d80b]{color:#6a6c75;font-weight:700}.rule-editor--first-rule[data-v-4c39d80b]{float:right}.rule-editor--remove[data-v-4c39d80b]{height:14px;line-height:14px}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$4 = "data-v-4c39d80b";
/* module identifier */
const __vue_module_identifier__$4 = undefined;
/* functional template */
const __vue_is_functional_template__$4 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$4 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$4,
  staticRenderFns: __vue_staticRenderFns__$4
}, __vue_inject_styles__$4, __vue_script__$4, __vue_scope_id__$4, __vue_is_functional_template__$4, __vue_module_identifier__$4, false, createInjector, undefined, undefined);

//
var script$3 = {
  name: 'SegmentEditor',
  components: {
    PendoAlert,
    PendoCard,
    PendoDivider,
    PendoButton,
    RuleEditor: __vue_component__$4
  },
  provide() {
    // define the property to make the injected value reactive
    const readOnly = {};
    Object.defineProperty(readOnly, 'value', {
      enumerable: true,
      get: () => this.readOnly
    });
    return {
      [readOnlyKey]: readOnly
    };
  },
  props: {
    config: {
      type: Object,
      required: true
    },
    isVisible: {
      type: Boolean,
      required: true
    },
    segment: {
      type: Object,
      required: false,
      default: () => null
    },
    readOnly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      operators: SEGMENT_OPERATORS,
      rules: []
    };
  },
  computed: {
    apps() {
      return this.config?.data?.apps || [];
    },
    schemas() {
      return this.config?.data?.schemas || [];
    },
    guidesMap() {
      return this.config?.data?.guides?.map || {};
    },
    guides() {
      return this.config?.data?.guides?.filteredList?.(this.guidesMap) || Object.values(this.guidesMap);
    },
    guidesWithPolls() {
      return this.guides.filter(guide => getPollsForGuide(guide).length);
    },
    rcModulesMap() {
      return this.config?.data?.rcModules?.map || {};
    },
    rcModules() {
      return this.config?.data?.rcModules?.filteredList?.(this.rcModulesMap) || Object.values(this.rcModulesMap);
    },
    features() {
      return this.config?.data?.features?.filteredList?.(this.featuresMap) || Object.values(this.featuresMap);
    },
    featuresMap() {
      return this.config?.data?.features?.map || {};
    },
    workflowsMap() {
      return this.config?.data?.workflows?.map || {};
    },
    workflows() {
      return this.config?.data?.workflows?.filteredList?.(this.workflowsMap) || Object.values(this.workflowsMap);
    },
    draftResourceCenters() {
      return this.config?.data?.draftResourceCenters?.filteredList?.(this.draftResourceCentersMap) || Object.values(this.draftResourceCentersMap);
    },
    draftResourceCentersMap() {
      return this.config?.data?.draftResourceCenters?.map || {};
    },
    parentSegments() {
      if (this.segment?.id) {
        return this.segments.filter(segment => segment.compound?.includes(this.segment.id)) || [];
      }
      return [];
    },
    hasParentSegments() {
      return this.parentSegments.length > 0;
    },
    nestedSegmentCount() {
      return reduce(this.rules, (sum, rule) => {
        const innerSum = reduce(rule, (orSum, orRule) => {
          if (isNestedSegmentRule(orRule)) {
            return orSum + 1;
          }
          return orSum;
        }, 0);
        return sum + innerSum;
      }, 0);
    },
    segmentsMap() {
      return this.config?.data?.segments?.map || {};
    },
    segments() {
      return this.config?.data?.segments?.filteredList?.(this.segmentsMap) || Object.values(this.segmentsMap);
    },
    pagesMap() {
      return this.config?.data?.pages?.map || {};
    },
    pages() {
      return this.config?.data?.pages?.filteredList?.(this.pagesMap) || Object.values(this.pagesMap);
    },
    appsMap() {
      return keyBy(this.appOptions, 'id');
    },
    appOptions() {
      const defaultOption = [{
        displayName: 'All Applications',
        id: null,
        resourceCenterId: null
      }];
      const appOptions = this.apps.map(app => {
        return {
          ...app,
          resourceCenterId: get(this.draftResourceCentersMap, `${app.id}.homeView.id`)
        };
      });
      return defaultOption.concat(appOptions);
    },
    sharedData() {
      return pick(this, ['appsMap', 'appOptions', 'features', 'featuresMap', 'filteredSchemaList', 'segments', 'segmentsMap', 'pages', 'pagesMap', 'guides', 'guidesMap', 'guidesWithPolls', 'draftResourceCenters', 'draftResourceCentersMap', 'rcModules', 'rcModulesMap', 'workflows', 'workflowsMap']);
    },
    filteredSchemaList() {
      return this.schemas.filter(schema => {
        const isPendoGroup = schema.group === 'pendo';
        const excludePendoGroupMetadataList = ['visitor_pendo_designerenabled', 'visitor_pendo_donotprocess'];
        if (!this.config.usesMultiApp && schema.id === 'application') {
          return false;
        }
        if (schema.id === 'workflow' && (!this.config.hasWorkflow || this.workflows.length === 0)) {
          return false;
        }
        if (schema.id === 'resourceCenter' && !this.config.hasResourceCenter) {
          return false;
        }
        if (schema.schema === 'poll' && this.guidesWithPolls.length <= 0) {
          return false;
        }
        if (schema.schema === 'segment' && (this.segments.length === 0 || this.segments.length === 1 && this.segment && this.segment.id === this.segments[0].id)) {
          return false;
        }
        if (isPendoGroup && excludePendoGroupMetadataList.includes(schema.id)) {
          return false;
        }
        return true;
      }).map(option => {
        return {
          ...option,
          icon: {
            type: this.getSchemaIcon(option.schema)
          }
        };
      });
    },
    usesLastVisitSchema() {
      return this.rules.some(orRules => orRules.some(orRule => {
        return orRule.schema.type === 'metadata.auto.lastvisit';
      }));
    }
  },
  watch: {
    rules(newRules) {
      this.$emit('update-rules', newRules);
    },
    nestedSegmentCount(newCount) {
      this.$emit('update-nested-segment-count', newCount);
    }
  },
  mounted() {
    this.initRules();
  },
  methods: {
    initRules() {
      if (this.segment) {
        this.rules = getRulesFromSegment(this.segment).map(this.mapIncomingGuideRules);
      } else {
        const schema = get(this, 'filteredSchemaList[0]');
        this.rules = [[generateRule(this.operators, schema)]];
      }
    },
    removeRule(index, orIndex) {
      if (orIndex == null) {
        this.rules.splice(index, 1);
      } else {
        this.rules[index].splice(orIndex, 1);
      }
    },
    updateOrRule(updates, ruleIndex, orIndex) {
      const rule = this.rules[ruleIndex][orIndex];
      if (updates.id) {
        // if update includes an id, the user is selecting a
        // different "schema" -> metadata, page, feature, guide, etc.
        // reset existing rule and start fresh
        this.$set(this.rules[ruleIndex], orIndex, {
          key: rule.key,
          ...updates
        });
      } else {
        // update individual keys on the existing rule
        Object.entries(updates).forEach(([key, value]) => {
          if (value === undefined) {
            this.$delete(rule, key);
          } else {
            this.$set(rule, key, value);
          }
        });
      }
    },
    addOrRule(index) {
      const currentRuleSet = this.rules[index];
      const previousRule = currentRuleSet[currentRuleSet.length - 1];
      if (this.nestedSegmentCount >= SEGMENT_RULE_LIMIT && previousRule.schema.type === 'segment') {
        currentRuleSet.push(this.generateRule());
      } else {
        const newRule = this.duplicateRule(previousRule);
        if (newRule.tagId) {
          delete newRule.tagId;
          delete newRule.value;
        }
        currentRuleSet.push(newRule);
      }
    },
    addRule() {
      this.rules.push([this.generateRule()]);
    },
    generateRule(schema = get(this, 'filteredSchemaList[0]')) {
      return generateRule(this.operators, schema);
    },
    duplicateRule(rule) {
      return {
        ...cloneDeep(rule),
        id: uuid()
      };
    },
    getSchemaIcon(schema) {
      const icon = {
        poll: 'list',
        time: 'calendar',
        guide: 'map',
        guideElement: 'guideElement',
        aggregation: 'user',
        page: 'file',
        feature: 'feature',
        application: 'activity',
        workflow: 'adopt-workflows',
        segment: 'pie-chart'
      }[schema];
      return icon || 'user';
    },
    mapIncomingGuideRules(rules) {
      // Note: See `migration` util to manage further mappings for legacy rules.
      return rules.map(rule => {
        if (rule.guideId) {
          rule.guide = this.guidesMap[rule.guideId];
          rule.guideDeleted = !rule.guide;
          delete rule.guideId;
        }
        if (rule.pollId) {
          rule.poll = getPollsForGuide(rule.guide).find(poll => poll.id === rule.pollId);
          delete rule.pollId;
        }
        if (rule.segmentId) {
          rule.segment = this.segmentsMap[rule.segmentId];
          rule.segmentDeleted = !rule.segment;
          rule.isSegmentShared = get(rule.segment, 'shared', false);
          delete rule.segmentId;
        }
        if (rule.pageId) {
          rule.page = this.pagesMap[rule.pageId];
          rule.pageDeleted = !rule.page;
          rule.isPageShared = get(rule.page, 'trainingSettings.enabled', false);
          rule.isCustomPage = get(rule.page, 'rootVersionId', null);
          delete rule.pageId;
        }
        if (rule.featureId) {
          rule.feature = this.featuresMap[rule.featureId];
          rule.featureDeleted = !rule.feature;
          delete rule.featureId;
        }
        if (rule.workflowId) {
          rule.workflow = this.workflowsMap[rule.workflowId];
          rule.workflowDeleted = !rule.workflow;
          delete rule.workflowId;
        }
        if (get(rule, 'schema.icon') && get(rule, 'schema.schema')) {
          rule.schema.icon = {
            type: this.getSchemaIcon(rule.schema.schema)
          };
        }

        // Careful, `selectedApp: { id: null }` is VALID as "all applications"
        // So check for the existence of "selectedApp" instead
        if (get(rule, 'schema.selectedApp')) {
          const app = this.appOptions.find(opt => opt.id === rule.schema.selectedApp.id);
          rule.app = app;
          rule.appDeleted = !rule.app;
          delete rule.schema.selectedApp;
        }
        return rule;
      });
    }
  }
};

/* script */
const __vue_script__$3 = script$3;

/* template */
var __vue_render__$3 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _vm.isVisible ? _c('div', {
    staticClass: "segment-editor"
  }, [_vm.config.guideTargeting && _vm.usesLastVisitSchema ? _c('div', {
    staticClass: "segment-editor--illegal-rule-warning"
  }, [_c('pendo-alert', {
    attrs: {
      "type": "error"
    }
  }, [_vm._v("\n            Segments containing the “Last Visit” rule cannot be used to target guides. Please remove this rule\n            before saving the segment.\n            "), _c('a', {
    attrs: {
      "target": "_blank",
      "href": "https://adoptpartners.pendo.io/hc/en-us/articles/360046163511-Segments"
    }
  }, [_vm._v("Learn More")]), _vm._v(".\n        ")])], 1) : _vm._e(), _vm._v(" "), _vm._l(_vm.rules, function (rule, index) {
    return _c('span', {
      key: "segment-rule-" + index,
      staticClass: "rules-card"
    }, [_c('pendo-card', {
      staticClass: "segment-rule",
      attrs: {
        "title": "Rule " + (index + 1),
        "body-min-height": "0"
      },
      scopedSlots: _vm._u([{
        key: "headerRight",
        fn: function () {
          return [_vm.rules.length > 1 && !_vm.readOnly ? _c('pendo-button', {
            staticClass: "segment-rule--remove",
            attrs: {
              "icon": "trash-2",
              "size": "mini",
              "type": "tertiary"
            },
            on: {
              "click": function ($event) {
                return _vm.removeRule(index);
              }
            }
          }) : _vm._e()];
        },
        proxy: true
      }], null, true)
    }, [_vm._v(" "), _vm._l(rule, function (orRule, orIndex) {
      return _c('div', {
        key: orRule.id,
        staticClass: "segment-rule--row"
      }, [_c('rule-editor', {
        attrs: {
          "config": _vm.config,
          "computed-data": _vm.sharedData,
          "index": orIndex,
          "removable": rule.length > 1,
          "rule": orRule,
          "operators": _vm.operators,
          "nested-segment-count": _vm.nestedSegmentCount,
          "has-parent-segments": _vm.hasParentSegments,
          "segment": _vm.segment
        },
        on: {
          "remove": function ($event) {
            return _vm.removeRule(index, orIndex);
          },
          "update:showCsvUploadWarning": function ($event) {
            return _vm.$emit('showCsvUploadWarning', $event);
          },
          "input": function ($event) {
            return _vm.updateOrRule($event, index, orIndex);
          }
        }
      })], 1);
    }), _vm._v(" "), !_vm.readOnly ? _c('pendo-button', {
      staticClass: "segment-builder--create-or-rule",
      attrs: {
        "theme": "app",
        "type": "link",
        "prefix-icon": "plus-circle",
        "label": "OR"
      },
      on: {
        "click": function ($event) {
          return _vm.addOrRule(index);
        }
      }
    }) : _vm._e()], 2), _vm._v(" "), _vm.rules.length > 1 && _vm.rules.length !== index + 1 ? _c('pendo-divider', {
      attrs: {
        "height": "56px",
        "stroke": "#DADCE5"
      },
      scopedSlots: _vm._u([{
        key: "start",
        fn: function () {
          return [_c('div', {
            staticClass: "segment-editor--rule-seperator"
          }, [_vm._v("\n                    AND\n                ")])];
        },
        proxy: true
      }], null, true)
    }) : _vm._e()], 1);
  }), _vm._v(" "), _vm.rules.length ? _c('div', {
    staticClass: "segment-editor--and-separator"
  }, [!_vm.readOnly ? _c('pendo-button', {
    staticClass: "segment-editor--create-and-rule",
    attrs: {
      "theme": "app",
      "type": "link",
      "prefix-icon": "plus-circle",
      "label": "AND"
    },
    on: {
      "click": _vm.addRule
    }
  }) : _vm._e()], 1) : _vm._e()], 2) : _vm._e();
};
var __vue_staticRenderFns__$3 = [];

/* style */
const __vue_inject_styles__$3 = function (inject) {
  if (!inject) return;
  inject("data-v-2647b21b_0", {
    source: ".segment-editor{overflow-y:auto;max-width:1140px}.segment-editor--illegal-rule-warning{margin-bottom:2em}.segment-editor .segment-rule--remove svg{stroke:#6a6c75}.segment-editor .segment-rule--remove:hover{cursor:pointer}.segment-editor .segment-rule--selected-label{display:flex;flex-flow:row nowrap;justify-content:flex-start;align-items:center}.segment-editor .segment-rule--selected-label .pendo-icon{margin-right:.5em}.segment-editor--rule-seperator{color:#6a6c75;font-weight:600;font-size:14px}.segment-editor--and-separator{display:flex;flex-flow:row nowrap;justify-content:center;align-items:center;margin-top:1em}.segment-editor--and-separator button{width:100%;overflow:hidden}.segment-editor--and-separator button:after{background-color:#dadce5;content:\"\";display:inline-block;height:1px;position:relative;vertical-align:middle;width:100%}.segment-editor--and-separator button:after{left:1em}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$3 = undefined;
/* module identifier */
const __vue_module_identifier__$3 = undefined;
/* functional template */
const __vue_is_functional_template__$3 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$3 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$3,
  staticRenderFns: __vue_staticRenderFns__$3
}, __vue_inject_styles__$3, __vue_script__$3, __vue_scope_id__$3, __vue_is_functional_template__$3, __vue_module_identifier__$3, false, createInjector, undefined, undefined);

//
var script$2 = {
  name: 'EngageSegmentOverlay',
  components: {
    PendoButton,
    PendoEmptyState
  },
  props: {
    segment: {
      type: Object,
      required: true
    }
  },
  methods: {
    openSegmentInEngage() {
      const baseUrl = generateEngageUrlByEnv(env());
      window.open(`${baseUrl}/segments?segment=${this.segment.id}`, '_blank');
    }
  }
};

/* script */
const __vue_script__$2 = script$2;

/* template */
var __vue_render__$2 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "engage-segment-overlay"
  }, [_c('pendo-empty-state', {
    attrs: {
      "background-color": "#F4F4F7",
      "title": "Edit segment in Engage",
      "description": "This segment was created in Engage. To edit this segment, open Engage and edit the segment there."
    },
    scopedSlots: _vm._u([{
      key: "icon",
      fn: function () {
        return [_c('svg', {
          attrs: {
            "width": "73",
            "height": "72",
            "viewBox": "0 0 73 72",
            "fill": "none",
            "xmlns": "http://www.w3.org/2000/svg"
          }
        }, [_c('rect', {
          attrs: {
            "x": "5.5",
            "y": "17",
            "width": "50",
            "height": "50",
            "rx": "4",
            "fill": "#C7C8F6"
          }
        }), _vm._v(" "), _c('path', {
          attrs: {
            "d": "M51.0296 7.82345C54.7981 4.05496 60.908 4.05496 64.6765 7.82344C68.445 11.5919 68.445 17.7018 64.6765 21.4703L30.5593 55.5875L13.4577 60.2708C12.7106 60.4754 12.0245 59.7893 12.2291 59.0422L16.9124 41.9407L51.0296 7.82345Z",
            "fill": "#4546E0"
          }
        }), _vm._v(" "), _c('path', {
          attrs: {
            "fill-rule": "evenodd",
            "clip-rule": "evenodd",
            "d": "M62.793 26.7072L45.793 9.70718L47.2072 8.29297L64.2072 25.293L62.793 26.7072Z",
            "fill": "#060119"
          }
        }), _vm._v(" "), _c('path', {
          attrs: {
            "fill-rule": "evenodd",
            "clip-rule": "evenodd",
            "d": "M58.793 30.7072L41.793 13.7072L43.2072 12.293L60.2072 29.293L58.793 30.7072Z",
            "fill": "#060119"
          }
        })])];
      },
      proxy: true
    }, {
      key: "actions",
      fn: function () {
        return [_c('pendo-button', {
          attrs: {
            "type": "primary",
            "label": "Open in Engage"
          },
          on: {
            "click": _vm.openSegmentInEngage
          }
        })];
      },
      proxy: true
    }])
  })], 1);
};
var __vue_staticRenderFns__$2 = [];

/* style */
const __vue_inject_styles__$2 = undefined;
/* scoped */
const __vue_scope_id__$2 = undefined;
/* module identifier */
const __vue_module_identifier__$2 = undefined;
/* functional template */
const __vue_is_functional_template__$2 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$2,
  staticRenderFns: __vue_staticRenderFns__$2
}, __vue_inject_styles__$2, __vue_script__$2, __vue_scope_id__$2, __vue_is_functional_template__$2, __vue_module_identifier__$2, false, undefined, undefined, undefined);

//
var script$1 = {
  name: 'VisitorTypeOptions',
  components: {
    PendoRadioGroup,
    PendoRadio
  },
  directives: {
    PendoTooltip
  },
  props: {
    value: {
      type: String,
      default: null
    },
    disableAnonymousOption: {
      type: Boolean,
      required: true
    },
    readOnly: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    disableAnonymousOptionTooltip() {
      return {
        multiline: true,
        content: this.disableAnonymousOption ? 'Changing visitor data type for this segment is disabled' : null
      };
    }
  },
  methods: {
    onChange(value) {
      this.$emit('input', value);
    }
  }
};

/* script */
const __vue_script__$1 = script$1;

/* template */
var __vue_render__$1 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "anon-visitor-options"
  }, [_c('div', {
    staticClass: "settings-panel--divider"
  }), _vm._v(" "), _c('div', {
    staticClass: "settings-panel__section"
  }, [_c('pendo-radio-group', {
    attrs: {
      "labels": {
        top: 'Select Visitor Data'
      },
      "value": _vm.value,
      "disabled": _vm.readOnly
    },
    on: {
      "change": _vm.onChange
    }
  }, [_c('div', {
    directives: [{
      name: "pendo-tooltip",
      rawName: "v-pendo-tooltip",
      value: _vm.disableAnonymousOptionTooltip,
      expression: "disableAnonymousOptionTooltip"
    }]
  }, [_c('div', {
    staticClass: "identified-visitors-only"
  }, [_c('pendo-radio', {
    attrs: {
      "disabled": _vm.disableAnonymousOption,
      "value": "visitorId",
      "label": "Identified Visitors Only"
    }
  })], 1), _vm._v(" "), _c('div', {
    staticClass: "anonymous-visitors-only"
  }, [_c('pendo-radio', {
    attrs: {
      "disabled": _vm.disableAnonymousOption,
      "value": "!visitorId",
      "label": "Anonymous Visitors Only"
    }
  })], 1)]), _vm._v(" "), _c('div', {
    staticClass: "all-visitors"
  }, [_c('pendo-radio', {
    attrs: {
      "disabled": _vm.disableAnonymousOption,
      "value": null,
      "label": "All Visitors"
    }
  })], 1)])], 1)]);
};
var __vue_staticRenderFns__$1 = [];

/* style */
const __vue_inject_styles__$1 = undefined;
/* scoped */
const __vue_scope_id__$1 = undefined;
/* module identifier */
const __vue_module_identifier__$1 = undefined;
/* functional template */
const __vue_is_functional_template__$1 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1,
  staticRenderFns: __vue_staticRenderFns__$1
}, __vue_inject_styles__$1, __vue_script__$1, __vue_scope_id__$1, __vue_is_functional_template__$1, __vue_module_identifier__$1, false, undefined, undefined, undefined);

//
var script = {
  name: 'SegmentBuilder',
  components: {
    EngageSegmentOverlay: __vue_component__$2,
    PendoButton,
    PendoCard,
    PendoDrawer,
    PendoInput,
    SegmentStatsPanel: __vue_component__$v,
    SegmentSuperPanel: __vue_component__$s,
    SegmentEditor: __vue_component__$3,
    VisitorTypeOptions: __vue_component__$1
  },
  provide() {
    return {
      appFirstVisit: this.config?.data?.appFirstVisit
    };
  },
  props: {
    editingSegment: {
      type: Object,
      default: null
    },
    config: {
      type: Object,
      required: true
    },
    isVisible: {
      type: Boolean,
      default: false
    },
    isSaving: {
      type: Boolean,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    showSuperPanel: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      operators: SEGMENT_OPERATORS,
      name: '',
      rules: [],
      segmentFlagValue: '',
      nestedSegmentCount: 0,
      invalidSegmentFlag: false,
      attemptedSubmit: false,
      showCsvUploadWarning: false,
      selectedVisitorType: this.config.canShowAnonymous ? null : 'visitorId'
    };
  },
  computed: {
    isEngageSegment() {
      return this.editingSegment?.id && isEngageSegment(this.editingSegment);
    },
    pipelineJson() {
      return JSON.stringify(this.pipeline, null, 2);
    },
    isEditing() {
      return this.editingSegment !== null;
    },
    title() {
      return this.editingSegment ? 'Edit Segment' : 'Create Segment';
    },
    showSaveButton() {
      return !this.readOnly && !this.isEngageSegment;
    },
    saveButtonLabel() {
      return this.editingSegment ? 'Save Segment' : 'Create Segment';
    },
    closeButtonLabel() {
      return this.readOnly || this.isEngageSegment ? 'Close' : 'Cancel';
    },
    builtSegment() {
      const segmentInfo = {
        rules: this.rules,
        name: this.name,
        flagName: this.segmentFlagValue,
        selectedVisitorType: this.selectedVisitorType
      };
      if (!this.config.usesMultiApp) {
        segmentInfo.appId = this.config.appId;
      }
      const segment = validateAndBuildSegment(segmentInfo);
      return segment;
    },
    pipeline() {
      if (!this.builtSegment) {
        return null;
      }
      const {
        compound,
        pipelines,
        pipeline
      } = this.builtSegment;
      if (pipelines) {
        return {
          compound,
          pipelines
        };
      }
      return pipeline;
    },
    invalidSegment() {
      return !this.isPipelineValid || this.config.guideTargeting && this.usesLastVisitSchema || this.invalidSegmentFlag || !this.name;
    },
    isPipelineValid() {
      return !!this.builtSegment && !!this.pipeline;
    },
    usesLastVisitSchema() {
      return this.rules.some(orRules => orRules.some(orRule => {
        return orRule.schema.type === 'metadata.auto.lastvisit';
      }));
    },
    createdBySomeoneElse() {
      const {
        createdByUser
      } = this.editingSegment;
      return createdByUser && this.config?.userId !== createdByUser.id;
    },
    segment() {
      if (this.editingSegment) {
        // Work with segment that's not tied to vuex store
        return cloneDeep(this.editingSegment);
      }
      return null;
    },
    disableAnonymousOption() {
      return this.rules.some(orRules => orRules.some(rule => get(rule, 'schema.type', '') === 'segment'));
    }
  },
  created() {
    initializeHttpWrapper(this.config.pendoHttpWrapperConfig);
    initializeAggConnect({
      http,
      env: window.appEnvId === 'prod' ? 'production' : window.appEnvId
    });
  },
  methods: {
    onUpdateRules(rules) {
      this.rules = rules;
      if (this.disableAnonymousOption && this.config.canShowAnonymous) {
        this.selectedVisitorType = null;
      }
    },
    initSegmentData() {
      if (this.editingSegment) {
        this.name = this.editingSegment.name;
        this.segmentFlagValue = this.editingSegment?.flagName;
        this.selectedVisitorType = getSegmentVisitorType(this.editingSegment);
      }
    },
    save() {
      this.attemptedSubmit = true;
      const segment = Object.assign({}, this.builtSegment);
      if (this.editingSegment) {
        segment.id = this.editingSegment.id;
        // when we build a segment we only create certain properties
        const initialSegment = cloneDeep(pick(this.editingSegment, ['name', 'id', 'definition', 'pipeline', 'shared']));
        const noChanges = isEqual(initialSegment, segment);
        if (noChanges) {
          this.close();
          return;
        }
      }
      this.$emit('save', {
        segment
      });
    },
    resetLocalState() {
      this.name = '';
      this.rules = [];
      this.segmentFlagValue = '';
      this.showCsvUploadWarning = false;
    },
    close() {
      this.$emit('close');
    },
    updateVisitorType(newType) {
      this.selectedVisitorType = newType;
    }
  }
};

/* script */
const __vue_script__ = script;

/* template */
var __vue_render__ = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('pendo-drawer', {
    attrs: {
      "visible": _vm.isVisible,
      "title": _vm.title,
      "size": "fullscreen",
      "class-name": "segment-builder"
    },
    on: {
      "open": _vm.initSegmentData,
      "closed": _vm.resetLocalState,
      "close": _vm.close
    },
    scopedSlots: _vm._u([{
      key: "body",
      fn: function () {
        return [_vm.isEngageSegment ? _c('engage-segment-overlay', {
          attrs: {
            "segment": _vm.editingSegment
          }
        }) : _vm._e(), _vm._v(" "), !_vm.isEngageSegment ? [_c('div', {
          staticClass: "settings-panel"
        }, [_c('div', {
          staticClass: "settings-panel__inner"
        }, [_c('pendo-card', {
          attrs: {
            "body-min-height": "0"
          }
        }, [_c('div', {
          staticClass: "settings-panel__section"
        }, [_c('div', {
          staticClass: "settings-panel--segment-name"
        }, [_c('pendo-input', {
          attrs: {
            "invalid": !_vm.name,
            "maxlength": "48",
            "disabled": _vm.readOnly,
            "placeholder": "Segment Name",
            "label": "Segment Name"
          },
          model: {
            value: _vm.name,
            callback: function ($$v) {
              _vm.name = $$v;
            },
            expression: "name"
          }
        })], 1)]), _vm._v(" "), _c('segment-stats-panel', {
          attrs: {
            "pipeline": _vm.pipeline,
            "nested-segment-count": _vm.nestedSegmentCount,
            "show-csv-upload-warning": _vm.showCsvUploadWarning,
            "is-valid": _vm.isPipelineValid
          }
        }), _vm._v(" "), _vm.showSuperPanel ? _c('segment-super-panel', {
          attrs: {
            "segment-flag-input-disabled": _vm.editingSegment === null ? false : _vm.createdBySomeoneElse,
            "pipeline-json": _vm.pipelineJson
          },
          on: {
            "invalid-segment-flag": function ($event) {
              _vm.invalidSegmentFlag = $event;
            }
          },
          model: {
            value: _vm.segmentFlagValue,
            callback: function ($$v) {
              _vm.segmentFlagValue = $$v;
            },
            expression: "segmentFlagValue"
          }
        }) : _vm._e(), _vm._v(" "), _vm.config.canShowAnonymous ? _c('visitor-type-options', {
          attrs: {
            "disable-anonymous-option": _vm.disableAnonymousOption,
            "value": _vm.selectedVisitorType
          },
          on: {
            "input": _vm.updateVisitorType
          }
        }) : _vm._e()], 1)], 1)]), _vm._v(" "), _c('div', {
          staticClass: "rules-panel"
        }, [_c('segment-editor', {
          attrs: {
            "is-visible": _vm.isVisible,
            "read-only": _vm.readOnly,
            "segment": _vm.segment,
            "config": _vm.config
          },
          on: {
            "update-nested-segment-count": function ($event) {
              return _vm.nestedSegmentCount = $event;
            },
            "update-rules": _vm.onUpdateRules,
            "showCsvUploadWarning": function ($event) {
              _vm.showCsvUploadWarning = $event;
            }
          }
        })], 1)] : _vm._e()];
      },
      proxy: true
    }, {
      key: "footer",
      fn: function () {
        return [_c('footer', {
          staticClass: "segment-builder--footer"
        }, [_c('pendo-button', {
          staticClass: "segment-builder--close",
          attrs: {
            "theme": "app",
            "type": "secondary",
            "label": _vm.closeButtonLabel
          },
          on: {
            "click": _vm.close
          }
        }), _vm._v(" "), _vm.showSaveButton ? _c('pendo-button', {
          staticClass: "segment-builder--save",
          attrs: {
            "disabled": _vm.invalidSegment,
            "loading": _vm.isSaving,
            "theme": "app",
            "type": "primary",
            "label": _vm.saveButtonLabel
          },
          on: {
            "click": _vm.save
          }
        }) : _vm._e()], 1)];
      },
      proxy: true
    }])
  });
};
var __vue_staticRenderFns__ = [];

/* style */
const __vue_inject_styles__ = function (inject) {
  if (!inject) return;
  inject("data-v-3885aac8_0", {
    source: ".segment-builder .pendo-multiselect__placeholder{color:#6a6c75}.segment-builder .pendo-drawer__body{padding:0;grid-template-columns:minmax(275px,350px) minmax(610px,1fr);display:grid;grid-column-gap:24px}.segment-builder .rules-panel,.segment-builder .settings-panel{padding:48px 0}.segment-builder .settings-panel{padding-left:32px;overflow-y:auto}.segment-builder .settings-panel .pendo-card__body{padding:16px 0}.segment-builder .settings-panel__section{padding:0 16px}.segment-builder .settings-panel__section .section-label{display:grid;grid-auto-flow:column;grid-gap:8px;justify-content:start;font-weight:600;min-height:24px;line-height:1rem}.segment-builder .settings-panel--segment-name{margin-bottom:1em}.segment-builder .settings-panel--divider{margin-top:1.5rem;margin-bottom:1.5rem;border-top:1px solid #dadce5}.segment-builder .rules-panel{overflow-y:auto;padding-right:32px}.segment-builder--footer{display:flex;align-items:center;justify-content:flex-end}.segment-builder--close svg{stroke:#6a6c75}@media (max-width:915px){.segment-builder .pendo-drawer__body{padding:0;grid-row-gap:24px;grid-template-columns:1fr;grid-template-rows:max-content;overflow-y:auto}.segment-builder .settings-panel{padding-top:48px;padding-bottom:0}.segment-builder .rules-panel{padding-top:0;padding-bottom:48px}.segment-builder .rules-panel,.segment-builder .settings-panel{padding-left:32px;padding-right:32px;overflow-y:initial}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__ = undefined;
/* module identifier */
const __vue_module_identifier__ = undefined;
/* functional template */
const __vue_is_functional_template__ = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__ = /*#__PURE__*/normalizeComponent({
  render: __vue_render__,
  staticRenderFns: __vue_staticRenderFns__
}, __vue_inject_styles__, __vue_script__, __vue_scope_id__, __vue_is_functional_template__, __vue_module_identifier__, false, createInjector, undefined, undefined);

export { __vue_component__ as SegmentBuilder, __vue_component__$7 as SegmentSummary, getInlineSegmentSummary, getMissingEntityText, parseSegmentSummary };
